import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  MessageSquare, 
  Send, 
  Paperclip, 
  Smile,
  Phone,
  Video,
  MoreVertical,
  Search,
  Filter,
  User,
  Clock,
  Check,
  CheckCheck,
  AlertCircle,
  FileText,
  Image,
  Mic,
  X,
  Download,
  Eye,
  Tag,
  Bookmark,
  Share2,
  Copy,
  Archive,
  Trash2,
  Edit,
  Save,
  Plus,
  Bell,
  Settings,
  Users,
  Calendar,
  MapPin,
  Star,
  MessageCircle,
  Zap,
  Bot,
  Headphones,
  Shield,
  TrendingUp,
  BarChart3,
  FileDown,
  RefreshCw,
  Volume2,
  VolumeX,
  Brain,
  List,
  Reply,
  Forward,
  Pin,
  Info,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Frown,
  Languages,
  Edit3,
  Play,
  Pause,
  ChevronRight,
  Link
} from 'lucide-react';
import { adminAPI, productAPI, whatsappAPI } from '../../services/api';
import api from '../../services/api';
import GoogleMap from '../Google/GoogleMap';
import { useCurrency } from '../../contexts/CurrencyContext';
import { toast } from 'react-toastify';
import WhatsAppAIFeatures from './WhatsAppAIFeatures';
import { useGoogleAnalytics } from '../../hooks/useGoogleAnalytics';

const WhatsAppChat = () => {
  const { trackEvent, trackButtonClick } = useGoogleAnalytics();

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [typing, setTyping] = useState(false);
  const [onlineStatus, setOnlineStatus] = useState({});
  
  // Advanced Features State
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showAgents, setShowAgents] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [messageSearchTerm, setMessageSearchTerm] = useState('');
  const [conversationNotes, setConversationNotes] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [voicePreview, setVoicePreview] = useState(null);
  const [showVoicePreview, setShowVoicePreview] = useState(false);
  const [showConversationInfo, setShowConversationInfo] = useState(false);
  const [showAIFeatures, setShowAIFeatures] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [playSoundOnMessage, setPlaySoundOnMessage] = useState(true);
  const messageAudioRef = useRef(null);
  const [showConversationMenu, setShowConversationMenu] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [isGeneratingReply, setIsGeneratingReply] = useState(false);
  const [headerMenuCoords, setHeaderMenuCoords] = useState({ x: 0, y: 0 });
  const [conversationMenuCoords, setConversationMenuCoords] = useState({ x: 0, y: 0 });
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [attachMenuCoords, setAttachMenuCoords] = useState({ x: 0, y: 0 });
  const [showCameraOptions, setShowCameraOptions] = useState(false);
  
  // Contact Management State
  const [showSaveContactModal, setShowSaveContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    phone_number: '',
    email: '',
    company: '',
    notes: ''
  });
  const [savingContact, setSavingContact] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [isCameraRecording, setIsCameraRecording] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [showMultiPreview, setShowMultiPreview] = useState(false);
  const [multiPreviewFiles, setMultiPreviewFiles] = useState([]); // [{file, previewUrl, type, caption}]
  const [multiPreviewIndex, setMultiPreviewIndex] = useState(0);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editorTarget, setEditorTarget] = useState(null); // { mode: 'single'|'multi', index?: number }
  const [videoTrim, setVideoTrim] = useState({ start: 0, end: 0, mute: false });
  const [isProcessingVideo, setIsProcessingVideo] = useState(false);
  const [imageCrop, setImageCrop] = useState({ x: 0, y: 0, widthPct: 100, heightPct: 100 });
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const ffmpegRef = useRef(null);
  // Image overlay editor state
  const [editorMode, setEditorMode] = useState('move'); // move|draw|erase|text|emoji
  const [brushColor, setBrushColor] = useState('#ff0000');
  const [brushSize, setBrushSize] = useState(6);
  const [eraseSize, setEraseSize] = useState(12);
  const [overlays, setOverlays] = useState([]); // [{type:'stroke', points:[{x,y}], color,size, erase:boolean} or {type:'text', text, x,y, color, fontSize, fontFamily} or {type:'emoji', text, x,y, fontSize}]
  const editorCanvasRef = useRef(null);
  const editorImgRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);
  
  // Bulk selection and delete functionality
  const [selectedConversations, setSelectedConversations] = useState(new Set());
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // 'single' or 'bulk'
  const [conversationToDelete, setConversationToDelete] = useState(null);

  // WhatsApp status state
  const [whatsappStatus, setWhatsappStatus] = useState(null);

  const [isPolling, setIsPolling] = useState(false);

  // Message loading state
  const [showAllMessages, setShowAllMessages] = useState(false);
  const [messageLimit, setMessageLimit] = useState(50);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [currentMessageLimit, setCurrentMessageLimit] = useState(50);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [totalMessageCount, setTotalMessageCount] = useState(0); // Track total available messages
  const [nextOffset, setNextOffset] = useState(50); // Track next offset for API calls
  
  // Message selection and options state
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [messageOptions, setMessageOptions] = useState({});
  const [showMessageOptions, setShowMessageOptions] = useState(false);
  const [messageOptionsCoords, setMessageOptionsCoords] = useState({ x: 0, y: 0 });
  const [selectedMessageForOptions, setSelectedMessageForOptions] = useState(null);
  
  // Additional message features state
  const [showMessageReactions, setShowMessageReactions] = useState({});
  const [messageReactions, setMessageReactions] = useState({});
  const [showMessageSearch, setShowMessageSearch] = useState(false);
  const [messageSearchResults, setMessageSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [showTranslation, setShowTranslation] = useState({});
  const [messageTranslations, setMessageTranslations] = useState({});
  const [editingMessage, setEditingMessage] = useState(null);
  const [editMessageText, setEditMessageText] = useState('');
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  
  // Additional message features state
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  


  // Map panel state
  const [showMapPanel, setShowMapPanel] = useState(false);
  const [mapAddress, setMapAddress] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [mapMarker, setMapMarker] = useState(null);
  const [mapSuggestions, setMapSuggestions] = useState([]);
  const placesServiceRef = useRef(null);

  // Products panel/modal state
  const [showProducts, setShowProducts] = useState(false);
  
  // WhatsApp Chat Window Timer State
  const [chatWindowTimer, setChatWindowTimer] = useState({
    remainingTime: null,
    isExpired: false,
    canSendMessage: true
  });
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsSearch, setProductsSearch] = useState('');
  // no pagination inside modal; always show up to 6
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  
  // Currency formatting
  const { formatPrice } = useCurrency();
  
  // Management data state for attachments
  const [managementData, setManagementData] = useState({
    location: {},
    contacts: [],
    payment: {},
    polls: [],
    events: []
  });
  
  // Selection modals state
  const [showContactSelector, setShowContactSelector] = useState(false);
  const [showPollSelector, setShowPollSelector] = useState(false);
  const [showEventSelector, setShowEventSelector] = useState(false);
  const [showPaymentSelector, setShowPaymentSelector] = useState(false);

  // File preview state
  const [filePreview, setFilePreview] = useState(null);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewCaption, setPreviewCaption] = useState('');

  // Ensure dropdown item clicks don't get swallowed by global listeners
  const withMenuEvent = (handler) => (event) => {
    if (event) {
      event.stopPropagation();
      if (event.preventDefault) event.preventDefault();
    }
    if (typeof handler === 'function') handler();
  };


  const [isManualRefresh, setIsManualRefresh] = useState(false);
  
  // Quick Replies & Templates
  const [quickReplies, setQuickReplies] = useState([
    { id: 1, text: "Hello! How can I help you today?", category: "greeting" },
    { id: 2, text: "Thank you for contacting us. We'll get back to you soon.", category: "acknowledgment" },
    { id: 3, text: "Your order has been confirmed and will be shipped soon.", category: "order" },
    { id: 4, text: "I understand your concern. Let me check this for you.", category: "support" },
    { id: 5, text: "Is there anything else I can help you with?", category: "closing" }
  ]);
  
  const [messageTemplates, setMessageTemplates] = useState([
    { id: 1, name: "Welcome Message", text: "Welcome to Niswa Info Tech! We're here to help you with all your digital needs.", category: "welcome" },
    { id: 2, name: "Order Update", text: "Your order #{orderId} has been updated. Current status: {status}", category: "order" },
    { id: 3, name: "Support Response", text: "Thank you for reaching out. Our support team will assist you shortly.", category: "support" },
    { id: 4, name: "Payment Reminder", text: "Friendly reminder: Your payment of {amount} is due on {date}.", category: "payment" }
  ]);

  // Agents data
  const [agents, setAgents] = useState([
    { id: 1, name: "John Doe", specialty: "General Support", status: "Online", avatar: "JD" },
    { id: 2, name: "Jane Smith", specialty: "Technical Support", status: "Online", avatar: "JS" },
    { id: 3, name: "Mike Johnson", specialty: "Sales", status: "Busy", avatar: "MJ" },
    { id: 4, name: "Sarah Wilson", specialty: "Customer Service", status: "Offline", avatar: "SW" }
  ]);

  // Refs
  const fileInputRef = useRef(null);
  // Message status indicator component
  const MessageStatus = ({ status, timestamp }) => {
    const getStatusIcon = () => {
      switch (status) {
        case 'pending':
          return <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>;
        case 'sent':
          return <span className="text-gray-400 dark:text-gray-500 text-xs">✓</span>;
        case 'delivered':
          return <span className="text-gray-400 dark:text-gray-500 text-xs">✓✓</span>;
        case 'read':
          return <span className="text-blue-500 dark:text-blue-400 font-semibold text-xs">✓✓</span>;
        case 'failed':
          return <span className="text-red-500 dark:text-red-400 text-xs">⚠</span>;
        default:
          return <span className="text-gray-400 dark:text-gray-500 text-xs">✓</span>; // Default to sent
      }
    };

    const getStatusText = () => {
      switch (status) {
        case 'pending':
          return 'Sending...';
        case 'sent':
          return 'Sent';
        case 'delivered':
          return 'Delivered';
        case 'read':
          return 'Read';
        case 'failed':
          return 'Failed';
        default:
          return '';
      }
    };

    return (
      <div className="flex items-center space-x-1 text-xs">
        {getStatusIcon()}
        {status === 'read' && (
          <span className="text-blue-500 dark:text-blue-400 font-medium">{getStatusText()}</span>
        )}
        {timestamp && (
          <span className="text-gray-500 dark:text-gray-400">{new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        )}
      </div>
    );
  };

  const docInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const addMoreInputRef = useRef(null);
  // Shared Google loader helpers
  const googleApiRef = useRef(null);
  const googleLoadInFlightRef = useRef(null);
  const loadGoogle = async () => {
    if (window.google) return window.google;
    if (googleApiRef.current) return googleApiRef.current;
    if (googleLoadInFlightRef.current) return await googleLoadInFlightRef.current;
    googleLoadInFlightRef.current = (async () => {
      const { Loader } = await import('@googlemaps/js-api-loader');
      const resp = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/maps/config`);
      const cfg = await resp.json();
      const apiKey = cfg?.data?.apiKey;
      const loader = new Loader({ apiKey, version: 'weekly', libraries: ['places','geocoding'] });
      const google = await loader.load();
      googleApiRef.current = google;
      return google;
    })();
    return await googleLoadInFlightRef.current;
  };
  const messagesEndRef = useRef(null);
  const voiceRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const statusUpdateIntervalRef = useRef(null);



  // Consolidated status checking function to prevent conflicts and status flickering
  // This replaces the previous multiple status checking functions that were causing conflicts
  const checkMessageStatusUpdates = async () => {
    if (!selectedConversation || !messages.length) return;
    
    // Prevent multiple simultaneous status checks
    if (checkMessageStatusUpdates.isRunning) {
      return;
    }
    
    checkMessageStatusUpdates.isRunning = true;
    
    try {
      const response = await whatsappAPI.getMessageStatuses(selectedConversation.phone_number);
      
      if (response.success && response.data) {
        // Create maps for both database ID and WhatsApp message ID
        const statusByDbId = {};
        const statusByWaId = {};
        
        response.data.forEach(msg => {
          if (msg.id != null) statusByDbId[msg.id] = msg.status;
          if (msg.message_id) statusByWaId[msg.message_id] = msg.status;
        });
        
        
        
        const now = new Date();
        
        // Check all outbound messages for status updates
        const outboundMessages = messages.filter(msg => 
          msg.direction === 'outbound' && 
          new Date(msg.created_at) > new Date(now.getTime() - 300000) // Last 5 minutes
        );
        
        outboundMessages.forEach(message => {
          // Try to find status by database ID first, then by WhatsApp message ID
          const backendStatus = statusByDbId[message.id] || statusByWaId[message.id] || statusByWaId[message.message_id];
          
          if (!backendStatus || backendStatus === message.status) {
            return; // Skip if no status or status is already correct
          }
          
          // Ensure proper status progression: sent → delivered → read
          if (message.status === 'sent' && backendStatus === 'read') {
            // Force delivered status first, then schedule read status
            updateMessageStatus(message.id, 'delivered');
          } else {
            // Normal status update
            updateMessageStatus(message.id, backendStatus);
          }
        });
      }
    } catch (error) {

    }
  };



  useEffect(() => {
    fetchConversations(false);
    checkWhatsAppStatus();
    fetchManagementData();
    
    // Set up intelligent polling for real-time updates
    const startPolling = () => {
      pollingIntervalRef.current = setInterval(() => {
        if (selectedConversation && !loading) {
            
            
          fetchMessages(selectedConversation.phone_number, false); // false = not initial load
        }
        // Only refresh conversations if not already polling
        if (!isPolling && !isManualRefresh) {
          fetchConversations();
        }
        }, 3000); // Poll every 3 seconds to prevent conflicts with status updates
      
      // Set up dedicated status update polling for faster tick updates
      statusUpdateIntervalRef.current = setInterval(() => {
        if (selectedConversation && !loading) {
          // Add a small delay to prevent conflicts with message polling
          setTimeout(() => {
            checkMessageStatusUpdates();
          }, 500);
        }
      }, 3000); // Check status every 3 seconds to prevent conflicts
    };

    // Function to handle webhook updates for message status
    const handleWebhookUpdate = (data) => {
      if (data.type === 'message_status_update' && selectedConversation) {
        const { messageId, status } = data;
        updateMessageStatus(messageId, status);
      }
    };

    // Function to simulate message status updates (for testing)
    const simulateMessageStatusUpdate = (messageId, status) => {
      setTimeout(() => {
        updateMessageStatus(messageId, status);
      }, 2000); // Simulate 2 second delay
    };



    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (statusUpdateIntervalRef.current) {
        clearInterval(statusUpdateIntervalRef.current);
        statusUpdateIntervalRef.current = null;
      }
    };

    // Start polling when component mounts
    startPolling();
    
    // Start status update checking (more frequent for better UX)
    statusUpdateIntervalRef.current = setInterval(() => {
      
      checkMessageStatusUpdates();
    }, 50); // Check status every 50ms for faster updates, especially for delivered status

    // Stop polling when tab becomes hidden, resume when visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedConversation?.phone_number, loading, isManualRefresh]); // Only depend on phone number, not entire object

  useEffect(() => {
    if (!isUserScrolling) {
      scrollToBottom();
    }
  }, [messages, isUserScrolling]);

  // Force scroll to bottom on initial conversation load
  useEffect(() => {
    if (selectedConversation && messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        scrollToBottom(true); // Force scroll to bottom
      }, 100);
    }
  }, [selectedConversation?.phone_number]);

  // Handle scroll events to prevent auto-scroll interference
  useEffect(() => {
    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      let scrollTimeout;
      const handleScroll = () => {
        setIsUserScrolling(true);
        // Clear existing timeout and set new one
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => setIsUserScrolling(false), 1000);
      };
      
      messagesContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        messagesContainer.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollTimeout);
      };
    }
  }, []);

  // Cleanup function for preview URLs
  useEffect(() => {
    return () => {
      // Cleanup preview URLs on unmount
      if (filePreview?.previewUrl) {
        URL.revokeObjectURL(filePreview.previewUrl);
      }
    };
  }, [filePreview]);

  // Voice Recording Timer
  useEffect(() => {
    if (isRecording) {
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        setRecordingTime(0);
      }
    }

    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, [isRecording]);

  // Cleanup voice preview when component unmounts or conversation changes
  useEffect(() => {
    return () => {
      if (voicePreview?.url) {
        URL.revokeObjectURL(voicePreview.url);
      }
    };
  }, [voicePreview]);

  // Clear voice preview when conversation changes
  useEffect(() => {
    if (voicePreview?.url) {
      URL.revokeObjectURL(voicePreview.url);
    }
    setShowVoicePreview(false);
    setVoicePreview(null);
  }, [selectedConversation?.phone_number]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-menu')) {
        setShowHeaderMenu(false);
        setShowConversationMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const scrollToBottom = (force = false) => {
    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      
      // Force scroll to bottom on initial load or if user is near bottom
      if (force || isNearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Check WhatsApp service status
  const checkWhatsAppStatus = async () => {
    try {
      const response = await api.get('/whatsapp/status');
      if (response.data.success) {
        setWhatsappStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error checking WhatsApp status:', error);
      setWhatsappStatus({ enabled: false, configured: false });
    }
  };



  // Test message to specific number
  const testMessageToNumber = async () => {
    try {
      const response = await whatsappAPI.sendMessage({
        phoneNumber: '+918639981771',
        message: '🧪 Test message from WhatsApp Chat Admin Panel - ' + new Date().toLocaleString()
      });
      
      if (response.data.success) {
        toast.success('Test message sent successfully to +918639981771!');
      } else {
        toast.error('Failed to send test message: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error sending test message:', error);
      toast.error('Failed to send test message: ' + error.message);
    }
  };

  // Test file upload to specific number
  const testFileUpload = async () => {
    try {
      // Create a test text file
      const testContent = '🧪 Test file from WhatsApp Chat Admin Panel\nTimestamp: ' + new Date().toLocaleString();
      const blob = new Blob([testContent], { type: 'text/plain' });
      const file = new File([blob], 'test-file.txt', { type: 'text/plain' });
      
      // Upload file
      const formData = new FormData();
      formData.append('document', file);
      const uploadResponse = await api.post('/upload/document', formData, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      
      const fileUrl = uploadResponse?.data?.data?.url || uploadResponse?.data?.url;
      if (!fileUrl) throw new Error('Upload failed: no URL returned');

      // Send via WhatsApp
      const whatsappResponse = await whatsappAPI.sendDocument({
        phoneNumber: '+918639981771',
        documentUrl: fileUrl,
        filename: 'test-file.txt',
        caption: '🧪 Test file from WhatsApp Chat Admin Panel'
      });

      if (whatsappResponse?.data?.success) {
        toast.success('Test file sent successfully to +918639981771!');
      } else {
        toast.error('Failed to send test file: ' + whatsappResponse?.data?.error);
      }
    } catch (error) {
      console.error('Error sending test file:', error);
      toast.error('Failed to send test file: ' + error.message);
    }
  };

  // Test: Add a simple message to current conversation
  const testAddMessage = () => {
    if (!selectedConversation) {
      toast.error('No conversation selected');
      return;
    }
    
    const testMessage = {
      id: Date.now(),
      phone_number: selectedConversation.phone_number,
      message_type: 'text',
      message_content: '🧪 Test message added manually - ' + new Date().toLocaleString(),
      direction: 'outbound',
      status: 'sent',
      created_at: new Date().toISOString()
    };
    
    
    
    setMessages(prev => {
      const newMessages = [...prev, testMessage];

      return newMessages;
    });
    
    toast.success('Test message added manually!');
  };

  // Test: Add an attachment message manually
  const testAddAttachmentMessage = () => {
    if (!selectedConversation) {
      toast.error('No conversation selected');
      return;
    }
    
    const testAttachmentMessage = {
      id: Date.now(),
      phone_number: selectedConversation.phone_number,
      message_type: 'document',
      message_content: '📎 Test Document.pdf',
      media_url: 'https://example.com/test.pdf',
      media_type: 'document',
      direction: 'outbound',
      status: 'sent',
      created_at: new Date().toISOString()
    };
    
    
    
    setMessages(prev => {
      const newMessages = [...prev, testAttachmentMessage];

      return newMessages;
    });
    
    toast.success('Test attachment message added manually!');
  };

  // Load all messages function
  const loadAllMessages = async () => {
    if (!selectedConversation) return;
    
    setLoadingMoreMessages(true);
    try {
      const response = await api.get(`/whatsapp/messages/${selectedConversation.phone_number}?limit=all`);
      
      if (response.data.success) {
        const allMessages = response.data.data || response.data.messages || [];
        
        // Normalize message data using the same logic as fetchMessages
        const normalizedMessages = allMessages.map((m) => {
          const createdAt = m?.created_at || m?.timestamp || m?.createdAt || m?.created || null;
          const direction = m?.direction || (m?.type === 'sent' ? 'outbound' : (m?.type === 'received' ? 'inbound' : undefined));
          const messageType = m?.message_type || m?.mediaType || 'text';
          const mediaUrl = m?.media_url || m?.mediaUrl || null;
          const mediaType = m?.media_type || m?.mediaType || null;
          const content = m?.message_content || m?.text || m?.message || '';

          return {
            ...m,
            created_at: createdAt,
            direction: direction || 'outbound',
            message_type: messageType,
            media_url: mediaUrl,
            media_type: mediaType,
            message_content: content,
          };
        });
        
        setMessages(normalizedMessages);
        setShowAllMessages(true);
        setMessageLimit(allMessages.length);
        setTotalMessageCount(allMessages.length);
        setNextOffset(allMessages.length); // No more messages to load
        toast.success(`Loaded all ${allMessages.length} messages!`);
      }
    } catch (error) {
      console.error('Error loading all messages:', error);
      toast.error('Failed to load all messages: ' + error.message);
    } finally {
      setLoadingMoreMessages(false);
    }
  };

  // Toggle message display mode
  const toggleMessageDisplay = () => {
    if (showAllMessages) {
      // Switch back to performance mode (last 50)
      setShowAllMessages(false);
      setMessageLimit(50);
      setCurrentMessageLimit(50);
      setNextOffset(50);
      // Show the last 50 messages (latest messages)
      setMessages(prev => prev.slice(-50));
      toast.info('Switched to performance mode (showing last 50 messages)');
    } else {
      // Load all messages
      loadAllMessages();
    }
  };

  // Load more messages incrementally
  const loadMoreMessages = async () => {
    if (!selectedConversation || loadingMoreMessages) return;
    
    setLoadingMoreMessages(true);
    try {
      // Store current scroll position before loading more messages
      const messagesContainer = document.querySelector('.messages-container');
      const currentScrollTop = messagesContainer?.scrollTop || 0;
      const currentScrollHeight = messagesContainer?.scrollHeight || 0;
      
      // Use the next offset to load earlier messages
      const offset = nextOffset;

      const response = await api.get(`/whatsapp/messages/${selectedConversation.phone_number}?offset=${offset}&limit=50`);
      
      if (response.data.success) {
        const newMessages = response.data.data || response.data.messages || [];
        
        if (newMessages.length === 0) {
          setHasMoreMessages(false);
          toast.info('No more messages to load');
          return;
        }
        
        // If we got fewer messages than requested, we've reached the end
        if (newMessages.length < 50) {
          setHasMoreMessages(false);
        }
        
        // Normalize message data using the same logic as fetchMessages
        const normalizedMessages = newMessages.map((m) => {
          const createdAt = m?.created_at || m?.timestamp || m?.createdAt || m?.created || null;
          const direction = m?.direction || (m?.type === 'sent' ? 'outbound' : (m?.type === 'received' ? 'inbound' : undefined));
          const messageType = m?.message_type || m?.mediaType || 'text';
          const mediaUrl = m?.media_url || m?.mediaUrl || null;
          const mediaType = m?.media_type || m?.mediaType || null;
          const content = m?.message_content || m?.text || m?.message || '';

          return {
            ...m,
            created_at: createdAt,
            direction: direction || 'outbound',
            message_type: messageType,
            media_url: mediaUrl,
            media_type: mediaType,
            message_content: content,
          };
        });
        
        // Prepend new messages to existing messages (messages come in chronological order - oldest first)
        // Remove duplicates based on message ID to prevent React key conflicts
        setMessages(prev => {
          const existingIds = new Set(prev.map(msg => msg.id));
          const uniqueNewMessages = normalizedMessages.filter(msg => !existingIds.has(msg.id));
          const newTotalLoaded = prev.length + uniqueNewMessages.length;
          
          // Keep currentMessageLimit at 50 for performance mode, but track total loaded
          if (!showAllMessages) {
            setCurrentMessageLimit(50);
          } else {
            setCurrentMessageLimit(newTotalLoaded);
          }
          
          // Update next offset for the next API call
          setNextOffset(prev => {
            const newOffset = prev + uniqueNewMessages.length;
    
            return newOffset;
          });
          
          // Check if there are more messages available based on total count
          const hasMore = newTotalLoaded < totalMessageCount;
          setHasMoreMessages(hasMore);
          
  
          
          return [...uniqueNewMessages, ...prev];
        });
        
        // Restore scroll position after messages are loaded
        setTimeout(() => {
          if (messagesContainer) {
            const newScrollHeight = messagesContainer.scrollHeight;
            const scrollOffset = newScrollHeight - currentScrollHeight;
            messagesContainer.scrollTop = currentScrollTop + scrollOffset;
          }
        }, 100);
        
        toast.success(`Loaded ${newMessages.length} more messages`);
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
      toast.error('Failed to load more messages: ' + error.message);
    } finally {
      setLoadingMoreMessages(false);
    }
  };

  // Message selection and options functions
  const toggleMessageSelection = (messageId, event = null) => {
    setSelectedMessages(prev => {
      const newSet = new Set(prev);
      
      // Handle Shift+Click for range selection
      if (event && event.shiftKey && prev.size > 0) {
        const messageIds = messages.map(msg => msg.id);
        const currentIndex = messageIds.indexOf(messageId);
        const lastSelectedId = Array.from(prev).pop();
        const lastSelectedIndex = messageIds.indexOf(lastSelectedId);
        
        if (currentIndex !== -1 && lastSelectedIndex !== -1) {
          const start = Math.min(currentIndex, lastSelectedIndex);
          const end = Math.max(currentIndex, lastSelectedIndex);
          
          // Add all messages in the range
          for (let i = start; i <= end; i++) {
            newSet.add(messageIds[i]);
          }
          return newSet;
        }
      }
      
      // Normal toggle behavior
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const selectAllMessages = () => {
    setSelectedMessages(new Set(messages.map(msg => msg.id)));
  };

  const clearMessageSelection = () => {
    setSelectedMessages(new Set());
  };

  const deleteSelectedMessages = async (deleteForEveryone = false) => {
    if (selectedMessages.size === 0) return;

    const action = deleteForEveryone ? 'delete for everyone' : 'delete for me';
    const confirmed = window.confirm(
      `Are you sure you want to ${action} ${selectedMessages.size} selected message(s)?`
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      const messageIds = Array.from(selectedMessages);
      
      if (deleteForEveryone) {
        // Delete for everyone - call backend API
        await Promise.all(messageIds.map(id => 
          api.delete(`/whatsapp/messages/${selectedConversation.phone_number}/${id}`)
        ));
      }

      // Remove from local state
      setMessages(prev => prev.filter(msg => !selectedMessages.has(msg.id)));
      clearMessageSelection();
      
      toast.success(`Messages ${action} successfully`);
    } catch (error) {
      console.error('Error deleting messages:', error);
      toast.error(`Failed to ${action} messages`);
    } finally {
      setLoading(false);
    }
  };

  const showMessageOptionsMenu = (message, event) => {
    event.preventDefault();
    event.stopPropagation();
    

    setSelectedMessageForOptions(message);
    
    // Get the button element position instead of mouse position
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    
    // Calculate position relative to the button
    let x = rect.left + rect.width / 2; // Center horizontally on the button
    let y = rect.bottom + 8; // 8px below the button for better spacing
    
    // Ensure menu stays within viewport
    const menuWidth = 200; // Approximate menu width
    const menuHeight = 400; // Approximate menu height
    
    // Adjust horizontal position if menu would go off-screen
    if (x + menuWidth / 2 > window.innerWidth) {
      x = window.innerWidth - menuWidth / 2 - 10;
    } else if (x - menuWidth / 2 < 0) {
      x = menuWidth / 2 + 10;
    }
    
    // Adjust vertical position if menu would go off-screen
    if (y + menuHeight > window.innerHeight) {
      y = rect.top - menuHeight - 5; // Show above the button
    }
    
    
    setMessageOptionsCoords({ x, y });
    setShowMessageOptions(true);
  };

  const hideMessageOptionsMenu = () => {

    setShowMessageOptions(false);
    setSelectedMessageForOptions(null);
  };

  const copyMessageText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Message copied to clipboard');
    hideMessageOptionsMenu();
  };

  const replyToMessage = (message) => {

    // Set the message as a reply in the input field
    setReplyingToMessage(message);
    setNewMessage(''); // Don't add the quoted text to input
    hideMessageOptionsMenu();
    // Focus on the input field
    setTimeout(() => {
      const textarea = document.querySelector('textarea[placeholder="Type a message..."]');
      if (textarea) {
        textarea.focus();
      }
    }, 100);
  };

  const cancelReply = () => {
    setReplyingToMessage(null);
    setNewMessage('');
  };

  // Audio message playback tracking
  const handleAudioPlay = (messageId) => {
      setPlayingAudio(messageId);
  };

  const handleAudioPause = () => {
            setPlayingAudio(null);
  };

  const handleAudioEnded = () => {
    setPlayingAudio(null);
  };

  // Location message functions
  const showLocationOnMap = (location) => {
    // Validate location data first
    if (!location) {
      toast.error('No location data available');
      return;
    }
    
    const lat = location.latitude || location.lat;
    const lng = location.longitude || location.lng;
    
    if (typeof lat === 'undefined' || typeof lng === 'undefined') {
      toast.error('Invalid location coordinates');
      return;
    }
    
    // Convert to numbers and validate
    const numLat = Number(lat);
    const numLng = Number(lng);
    
    if (isNaN(numLat) || isNaN(numLng)) {
      toast.error('Invalid location coordinates');
      return;
    }
    
    // Check if coordinates are reasonable (not 0,0 which is usually invalid)
    if (numLat === 0 && numLng === 0) {
      toast.error('Invalid location coordinates');
      return;
    }
    
    // Normalize location data
    const normalizedLocation = {
      latitude: numLat,
      longitude: numLng,
      address: location.address || ''
    };
    
    // Clear any existing modal state first
    setShowLocationModal(false);
    setSelectedLocation(null);
    
    // Set state with a small delay to ensure proper cleanup
    setTimeout(() => {
      setSelectedLocation(normalizedLocation);
    setShowLocationModal(true);
    }, 100);
  };

  const sendLocationMessage = async () => {
    if (!selectedConversation) return;

    try {
      // Get current location
      navigator.geolocation.getCurrentPosition(async (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          address: 'Current Location' // In production, reverse geocode this
        };

        const response = await whatsappAPI.sendLocation({
          phoneNumber: selectedConversation.phone_number,
          location: location
        });

        if (response.data.success) {
          const messageData = {
            id: response.data.data.messageId,
            phone_number: selectedConversation.phone_number,
            message_type: 'location',
            message_content: 'Location shared',
            direction: 'outbound',
            status: 'sent',
            created_at: new Date().toISOString(),
            location: location
          };

          setMessages(prev => [...prev, messageData]);
          toast.success('Location sent successfully!');
        }
      }, (error) => {
        toast.error('Failed to get location: ' + error.message);
      });
    } catch (error) {
      console.error('Error sending location:', error);
      toast.error('Failed to send location');
    }
  };

  const sendContactMessage = async (contact) => {
    if (!selectedConversation) return;

    try {
      const response = await whatsappAPI.sendContact({
        phoneNumber: selectedConversation.phone_number,
        contact: contact
      });

      if (response.data.success) {
        const messageData = {
          id: response.data.data.messageId,
          phone_number: selectedConversation.phone_number,
          message_type: 'contact',
          message_content: `Contact: ${contact.name}`,
          direction: 'outbound',
          status: 'sent',
          created_at: new Date().toISOString(),
          contact: contact
        };

        setMessages(prev => [...prev, messageData]);
        toast.success('Contact sent successfully!');
      }
    } catch (error) {
      console.error('Error sending contact:', error);
      toast.error('Failed to send contact');
    }
  };

  const forwardMessage = (message) => {
    // Store message to be forwarded in localStorage for cross-conversation forwarding
    localStorage.setItem('messageToForward', JSON.stringify({
      content: message.message_content,
      type: message.message_type,
      mediaUrl: message.media_url,
      timestamp: new Date().toISOString()
    }));
    toast.success('Message ready to forward. Select another conversation to forward it.');
    hideMessageOptionsMenu();
  };

  // Contact Management Functions
  const handleContactFormChange = (field, value) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetContactForm = () => {
    setContactForm({
      name: selectedConversation?.user_name || '',
      phone_number: selectedConversation?.phone_number || '',
      email: '',
      company: '',
      notes: ''
    });
  };

  // Initialize contact form when modal opens
  useEffect(() => {
    if (showSaveContactModal && selectedConversation) {
      setContactForm({
        name: selectedConversation.user_name || '',
        phone_number: selectedConversation.phone_number || '',
        email: '',
        company: '',
        notes: ''
      });
    }
  }, [showSaveContactModal, selectedConversation]);

  const saveContact = async () => {
    if (!contactForm.name.trim() || !contactForm.phone_number.trim()) {
      toast.error('Name and phone number are required');
      return;
    }

    // Validate phone number format
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    if (!phoneRegex.test(contactForm.phone_number.replace(/\s/g, ''))) {
      toast.error('Please enter a valid phone number with country code');
      return;
    }

    setSavingContact(true);
    try {
      // First, check if contact already exists
      const checkResponse = await api.get(`/whatsapp/contacts/check/${encodeURIComponent(contactForm.phone_number.trim())}`);
      
      if (checkResponse.data.exists) {
        // Contact exists, update it instead of creating new one
        const existingContact = checkResponse.data.contact;
        const updateResponse = await api.put(`/whatsapp/contacts/${existingContact.id}`, {
          name: contactForm.name.trim(),
          phone_number: contactForm.phone_number.trim(),
          email: contactForm.email?.trim() || null,
          company: contactForm.company?.trim() || null,
          notes: contactForm.notes?.trim() || null,
          contact_type: existingContact.contact_type || 'New',
          tags: existingContact.tags ? JSON.parse(existingContact.tags) : [],
          is_active: true
        });
        
        if (updateResponse.data.success) {
          toast.success('Contact updated successfully');
          setShowSaveContactModal(false);
          resetContactForm();
          
          // Refresh conversations to get updated data from backend
          await fetchConversations(true);
          
          // Update selected conversation if it's the same phone number
          if (selectedConversation) {
            setSelectedConversation(prev => ({ ...prev, user_name: contactForm.name }));
          }
        } else {
          toast.error('Failed to update contact');
        }
        return;
      }

      // Contact doesn't exist, create new one
      const response = await api.post('/whatsapp/contacts', {
        name: contactForm.name.trim(),
        phone_number: contactForm.phone_number.trim(),
        email: contactForm.email?.trim() || null,
        company: contactForm.company?.trim() || null,
        notes: contactForm.notes?.trim() || null,
        contact_type: 'New',
        tags: [], // Add empty tags array as expected by backend
        is_active: true
      });

      if (response.data.success) {
        toast.success('Contact saved successfully');
        setShowSaveContactModal(false);
        resetContactForm();
        
        // Refresh conversations to get updated data from backend
        await fetchConversations(true);
        
        // Update selected conversation if it's the same phone number
        if (selectedConversation) {
          setSelectedConversation(prev => ({ ...prev, user_name: contactForm.name }));
        }
      } else {
        toast.error(response.data.message || 'Failed to save contact');
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        console.log('400 Error details:', errorData);
        toast.error(errorData.error || errorData.message || 'Contact validation failed');
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error('Failed to save contact');
      }
    } finally {
      setSavingContact(false);
    }
  };

  const pinMessage = (message) => {
    // Store pinned message in localStorage for now
    const pinnedMessages = JSON.parse(localStorage.getItem('pinnedMessages') || '{}');
    const conversationKey = selectedConversation?.phone_number || 'default';
    
    if (!pinnedMessages[conversationKey]) {
      pinnedMessages[conversationKey] = [];
    }
    
    // Check if message is already pinned
    const isAlreadyPinned = pinnedMessages[conversationKey].some(msg => msg.id === message.id);
    
    if (isAlreadyPinned) {
      // Unpin the message
      pinnedMessages[conversationKey] = pinnedMessages[conversationKey].filter(msg => msg.id !== message.id);
      toast.success('Message unpinned');
    } else {
      // Pin the message
      pinnedMessages[conversationKey].push({
        id: message.id,
        content: message.message_content,
        timestamp: message.created_at,
        type: message.message_type
      });
      toast.success('Message pinned');
    }
    
    localStorage.setItem('pinnedMessages', JSON.stringify(pinnedMessages));
    hideMessageOptionsMenu();
  };

  const showMessageInfo = (message) => {
    // Show message info in a toast for now
    const info = `
Message ID: ${message.id}
Type: ${message.message_type}
Direction: ${message.direction}
Status: ${message.status}
Sent: ${new Date(message.created_at).toLocaleString()}
Content: ${message.message_content.substring(0, 100)}${message.message_content.length > 100 ? '...' : ''}
    `.trim();
    
    toast.info(info, {
      autoClose: 5000,
      position: "top-center"
    });
    hideMessageOptionsMenu();
  };

  // Update message status (for webhook updates) with debouncing
  const updateMessageStatus = (messageId, newStatus) => {
    // Map WhatsApp status to our status format
    const statusMap = {
      'sent': 'sent',
      'delivered': 'delivered', 
      'read': 'read',
      'failed': 'failed',
      'pending': 'pending'
    };
    
    const mappedStatus = statusMap[newStatus] || newStatus;
    

    
    // Ensure proper status progression: sent → delivered → read
    setMessages(prev => {
      const messageToUpdate = prev.find(msg => msg.id === messageId);
      if (!messageToUpdate) {

        return prev;
      }
      
      // Prevent unnecessary status updates (only update if status is actually different)
      if (messageToUpdate.status === mappedStatus) {
        return prev;
      }
      

      
      // Prevent skipping delivered status
      if (messageToUpdate.status === 'sent' && mappedStatus === 'read') {

        
        // First update to delivered status
        const updatedWithDelivered = prev.map(msg => 
          msg.id === messageId ? { ...msg, status: 'delivered' } : msg
        );
        
        // Then schedule read status update after minimum delivered time
        setTimeout(() => {
          setMessages(current => current.map(msg => 
            msg.id === messageId ? { ...msg, status: 'read' } : msg
          ));
        }, 2000); // Wait 2 seconds before showing read status
        
        return updatedWithDelivered;
      }
      
      // Normal status update
      const updated = prev.map(msg => 
        msg.id === messageId ? { ...msg, status: mappedStatus } : msg
      );
      return updated;
    });
    
    // Also update the conversation's last message status if this is the last message
    if (selectedConversation) {
      setConversations(prev => prev.map(conv => {
        if (conv.phone_number === selectedConversation.phone_number) {
          // Check if this message is the last message in the conversation
          const lastMessage = messages[messages.length - 1];
          if (lastMessage && lastMessage.id === messageId) {
            return { ...conv, last_message_status: mappedStatus };
          }
        }
        return conv;
      }));
    }
    

  };

  // Message reactions functions
  const toggleMessageReaction = (messageId, reaction) => {
    setMessageReactions(prev => {
      const current = prev[messageId] || [];
      const newReactions = current.includes(reaction) 
        ? current.filter(r => r !== reaction)
        : [...current, reaction];
      
      const isRemoving = current.includes(reaction);
      
      toast.success(`Message ${isRemoving ? 'unreacted' : 'reacted'} with ${reaction}`);
      
      return {
        ...prev,
        [messageId]: newReactions
      };
    });
  };

  const showReactionsForMessage = (messageId) => {
    setShowMessageReactions(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Message search functions
  const searchMessagesInConversation = (searchTerm) => {
    if (!searchTerm.trim()) {
      setMessageSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }

    const results = messages.filter(msg => 
      msg.message_content.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setMessageSearchResults(results);
    setCurrentSearchIndex(0);
    
    if (results.length > 0) {
      toast.success(`Found ${results.length} message(s)`);
    } else {
      toast.info('No messages found');
    }
  };

  const navigateSearchResults = (direction) => {
    if (messageSearchResults.length === 0) return;
    
    setCurrentSearchIndex(prev => {
      if (direction === 'next') {
        return prev < messageSearchResults.length - 1 ? prev + 1 : 0;
      } else {
        return prev > 0 ? prev - 1 : messageSearchResults.length - 1;
      }
    });
  };

  // Message translation functions
  const translateMessage = async (messageId, text) => {
    try {
      // Use the AI translation API instead of simulation
      const response = await api.post('/ai/translate', {
        text: text,
        target_language: 'en', // Default to English, can be made configurable
        source_language: 'auto'
      });

      if (response.data.success) {
        const translatedText = response.data.data.translated_text;
        setMessageTranslations(prev => ({
          ...prev,
          [messageId]: translatedText
        }));
        setShowTranslation(prev => ({
          ...prev,
          [messageId]: true
        }));
        toast.success('Message translated successfully');
      } else {
        toast.error('Translation failed: ' + response.data.message);
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Translation failed: ' + error.message);
    }
  };

  // Message editing functions
  const startEditingMessage = (message) => {
    setEditingMessage(message);
    setEditMessageText(message.message_content);
  };

  const saveEditedMessage = async (messageId) => {
    if (!editMessageText.trim()) return;

    setLoading(true);
    try {
      // Call API to update message in database and WhatsApp
      const response = await api.put(`/whatsapp/messages/${selectedConversation.phone_number}/${messageId}`, {
        message_content: editMessageText
      });
      
      if (response.data.success) {
        // Update local state only after successful API call
        setMessages(prev => prev.map(msg => 
          msg.id === messageId ? { ...msg, message_content: editMessageText, edited: true } : msg
        ));
        
        setEditingMessage(null);
        setEditMessageText('');
        toast.success('Message edited successfully');
      } else {
        toast.error('Failed to edit message: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error('Failed to edit message: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelEditingMessage = () => {
    setEditingMessage(null);
    setEditMessageText('');
  };

  // Message starring/favoriting
  const toggleMessageStar = (messageId) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, starred: !msg.starred } : msg
    ));
    
    const message = messages.find(msg => msg.id === messageId);
    toast.success(`Message ${message?.starred ? 'unstarred' : 'starred'}`);
  };

  // Export messages
  const exportMessages = () => {
    const exportData = {
      conversation: selectedConversation,
      messages: messages,
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whatsapp-chat-${selectedConversation?.phone_number}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Messages exported successfully');
  };

  const deleteMessage = async (message, deleteForEveryone = false) => {
    const action = deleteForEveryone ? 'delete for everyone' : 'delete for me';
    const confirmed = window.confirm(
      `Are you sure you want to ${action} this message?`
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      if (deleteForEveryone) {
        // Call API to delete message from WhatsApp and database
        const response = await api.delete(`/whatsapp/messages/${selectedConversation.phone_number}/${message.id}`);
        
        if (response.data.success) {
          // Remove from local state only after successful API call
          setMessages(prev => prev.filter(msg => msg.id !== message.id));
          toast.success('Message deleted for everyone');
        } else {
          toast.error('Failed to delete message: ' + response.data.message);
        }
      } else {
        // Only remove from local state for "delete for me"
        setMessages(prev => prev.filter(msg => msg.id !== message.id));
        toast.success('Message deleted for you');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error(`Failed to ${action}: ` + error.message);
    } finally {
      setLoading(false);
    }
    hideMessageOptionsMenu();
  };

  // Clear chat function
  const clearChat = async () => {
    if (!selectedConversation) {
      toast.warning('No conversation selected');
      return;
    }
    
    const confirmed = window.confirm(
      `Are you sure you want to clear the chat with ${selectedConversation.phone_number}?\n\nThis will remove all messages from this conversation. This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    setLoading(true);
    try {
      const response = await api.delete(`/whatsapp/conversations/${selectedConversation.phone_number}/messages`);
      
      if (response.data.success) {
        setMessages([]);
        setShowAllMessages(false);
        setCurrentMessageLimit(50);
        setHasMoreMessages(false);
        setNextOffset(50);
        setTotalMessageCount(0);
        toast.success('Chat cleared successfully!');
      } else {
        toast.error('Failed to clear chat: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error clearing chat:', error);
      toast.error('Failed to clear chat: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async (isManual = false) => {
    // Prevent multiple simultaneous calls
    if (isPolling && !isManual) return;
    
    if (isManual) {
      setIsManualRefresh(true);
    } else {
      setIsPolling(true);
    }
    
    try {
      // Fetch all conversations using admin endpoint
      const response = await api.get('/whatsapp/conversations');
      const list = response?.data?.data || response?.data?.conversations;
      if (response.data.success && Array.isArray(list)) {
        setConversations(list);
      } else {
        console.error('Invalid conversations data:', response.data);
        setConversations([]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    } finally {
      if (isManual) {
      setIsManualRefresh(false);
      } else {
        setIsPolling(false);
      }
    }
  };

  const fetchMessages = async (phoneNumber, isInitialLoad = true) => {
    if (!phoneNumber) return;
    
    // Prevent multiple simultaneous calls for the same conversation
    if (loading) return;
    setLoading(true);
    
    try {
      const response = await api.get(`/whatsapp/messages/${phoneNumber}`);
      const list = response?.data?.data || response?.data?.messages;
      
              // Debug: Log the raw response to see what's being returned
        if (isInitialLoad) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('🔍 Raw backend response:', response.data);
            console.log('🔍 Messages list:', list);
          }
          if (list && list.length > 0) {
            if (process.env.NODE_ENV !== 'production') {
              console.log('🔍 First message structure:', list[0]);
            }
            
            // Check for image messages specifically
            const imageMessages = list.filter(msg => msg.message_type === 'image');
            if (imageMessages.length > 0) {
              if (process.env.NODE_ENV !== 'production') {
                console.log('🔍 Image messages found:', imageMessages);
              }
            }
          }
        }
      
      if (response.data.success && Array.isArray(list)) {
        // Normalize to a consistent shape expected by the UI
        const normalized = list.map((m) => {
          const createdAt = m?.created_at || m?.timestamp || m?.createdAt || m?.created || null;
          const direction = m?.direction || (m?.type === 'sent' ? 'outbound' : (m?.type === 'received' ? 'inbound' : undefined));
          const messageType = m?.message_type || m?.mediaType || 'text';
          const mediaUrl = m?.media_url || m?.mediaUrl || null;
          const mediaType = m?.media_type || m?.mediaType || null;
          const content = m?.message_content || m?.text || m?.message || '';

          const normalizedMessage = {
            ...m,
            created_at: createdAt,
            direction: direction || 'outbound',
            message_type: messageType,
            media_url: mediaUrl,
            media_type: mediaType,
            message_content: content,
            // Preserve reply information
            reply_to: m.reply_to || null,
            reply_to_message_id: m.reply_to_message_id || null
          };

          // Debug: Log normalization for image messages
          if (isInitialLoad && m.message_type === 'image' && process.env.NODE_ENV !== 'production') {
            console.log('🔍 Normalizing image message:', {
              original: { message_type: m.message_type, media_url: m.media_url, message_content: m.message_content },
              normalized: { message_type: normalizedMessage.message_type, media_url: normalizedMessage.media_url, message_content: normalizedMessage.message_content }
            });
          }

          return normalizedMessage;
        });
        

        
        if (isInitialLoad) {
          // Only reset states on initial load, not on polling updates
          // For initial load, messages come in DESC order (newest first), so we reverse them for display
          setMessages([...normalized].reverse());
          setCurrentMessageLimit(50);
          setNextOffset(50); // Next offset for loading more messages
          
          // Get total message count by making a request with limit=all
          try {
            const totalResponse = await api.get(`/whatsapp/messages/${phoneNumber}?limit=all`);
            if (totalResponse.data.success) {
              const totalMessages = totalResponse.data.data || totalResponse.data.messages || [];
              setTotalMessageCount(totalMessages.length);

            }
          } catch (error) {

            setTotalMessageCount(list.length);
          }
          
          // Check if there are more messages available
          // If we got exactly 50 messages, there might be more
          // If we got fewer than 50, we've reached the end
          const hasMore = list.length >= 50;
          setHasMoreMessages(hasMore);
          setShowAllMessages(false);
          

        } else {
                      // On polling updates, only add truly new messages
            setMessages(prev => {
              const existingIds = new Set(prev.map(msg => msg.id));
              const existingWaIds = new Set(prev.map(msg => msg.message_id).filter(Boolean));

              

              
              const newMessages = normalized.filter(msg => {
                // Check if message ID doesn't exist
                if (existingIds.has(msg.id)) {
                  return false;
                }
                
                // Check if WhatsApp message ID already exists (for messages sent from frontend)
                if (msg.message_id && existingWaIds.has(msg.message_id)) {
                  return false;
                }
                
                // Check if we already have a message with the same content and direction
                // within the last 30 seconds (to handle timing differences)
                // But also consider reply context to avoid treating reply messages as duplicates
                const hasRecentDuplicate = prev.some(existingMsg => {
                  const sameContent = existingMsg.message_content === msg.message_content;
                  const sameDirection = existingMsg.direction === msg.direction;
                  const sameReplyContext = (
                    (existingMsg.reply_to && msg.reply_to) &&
                    existingMsg.reply_to_message_id === msg.reply_to_message_id
                  ) || (!existingMsg.reply_to && !msg.reply_to);
                  const recentTime = Math.abs(new Date(existingMsg.created_at) - new Date(msg.created_at)) < 30000; // 30 seconds
                  
                  // Special handling for reply messages - don't treat them as duplicates if they have different reply context
                  if (msg.reply_to && existingMsg.reply_to) {
                    // If both are reply messages, check if they're replying to the same message
                    if (msg.reply_to_message_id === existingMsg.reply_to_message_id) {
                      return true; // This is a duplicate
                    }
                  }
                  
                  return sameContent && sameDirection && sameReplyContext && recentTime;
                });
                
                if (hasRecentDuplicate) {
                  return false;
                }
                
                return true;
              });
            
            if (newMessages.length > 0) {
              

              
              // Add new messages to the end and scroll to bottom
              setTimeout(() => scrollToBottom(true), 100);
              
              return [...prev, ...newMessages];
            }
            
            // Note: Status updates are handled by checkMessageStatusUpdates function
            // to avoid race conditions. This function only handles new messages.
            return prev;
          });
        }
      } else {
        console.error('Invalid messages data:', response.data);
        if (isInitialLoad) {
        setMessages([]);
          setHasMoreMessages(false);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (isInitialLoad) {
      setMessages([]);
        setHasMoreMessages(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Check if we're in test mode (messages with test_msg_ prefix)
  const isTestMode = useCallback(() => {
    return messages.some(msg => msg.message_id && msg.message_id.startsWith('test_msg_'));
  }, [messages]);

  // Optimized send message function with useCallback
  const sendMessage = useCallback(async (messageText = null) => {
    const textToSend = String(messageText || newMessage || '');
    if (!textToSend.trim() || !selectedConversation) return;

    // Check if chat window is expired
    if (chatWindowTimer?.isExpired) {
      toast.error('Chat window expired! You can only send template messages now.');
      return;
    }

    // Check if chat window is close to expiring (less than 1 hour)
    if (chatWindowTimer && chatWindowTimer.remainingTime < 3600000) {
      toast.warning(`⚠️ Chat window expires in ${formatRemainingTime(chatWindowTimer.remainingTime)}. Send your message quickly!`);
    }

    // Check if we already have a message with the same content being sent recently
    const recentMessage = messages.find(msg => 
      msg.message_content === textToSend && 
      msg.direction === 'outbound' && 
      (msg.status === 'pending' || msg.status === 'sent') &&
      new Date(msg.created_at) > new Date(Date.now() - 30000) // Within last 30 seconds
    );

    if (recentMessage) {
      
      toast.info('Message already being sent');
      return;
    }

    // Prevent multiple rapid sends
    if (loading) {
      
      return;
    }

    // Clear input immediately
    if (!messageText) setNewMessage('');

    setLoading(true);
    try {
      // Prepare the request payload
      const requestPayload = {
        phoneNumber: selectedConversation.phone_number,
        message: textToSend
      };

      // Add reply context if this is a reply to another message
      if (replyingToMessage) {
        requestPayload.replyTo = {
          id: replyingToMessage.id,
          message_id: replyingToMessage.message_id, // Use the actual WhatsApp message ID
          message_content: replyingToMessage.message_content,
          direction: replyingToMessage.direction,
          message_type: replyingToMessage.message_type
        };

      }

      const response = await whatsappAPI.sendMessage(requestPayload);

      // Track WhatsApp message sent
      trackEvent('whatsapp_message_sent', {
        message_type: 'text',
        has_reply: !!replyingToMessage,
        conversation_phone: selectedConversation.phone_number,
        message_length: textToSend.length
      });

      if (response.data.success) {
        // Create the real message data with a unique ID to prevent conflicts
        const messageData = {
          id: response.data.data?.dbId || response.data.dbId || `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Use backend database ID if available
          message_id: response.data.data?.messageId || response.data.messageId, // Store WhatsApp message ID for status tracking
          phone_number: selectedConversation.phone_number,
          message_type: 'text',
          message_content: textToSend,
          direction: 'outbound',
          status: 'sent', // Start with sent status
          created_at: new Date().toISOString(),
          isNewlySent: true, // Flag to identify newly sent messages
          // Add reply information if this is a reply
          ...(replyingToMessage && {
            reply_to: replyingToMessage,
            reply_to_message_id: replyingToMessage.message_id || replyingToMessage.id
          })
        };
        


        // Clear any editing state before adding the new message
        if (editingMessage) {
          setEditingMessage(null);
          setEditMessageText('');
        }
        


        // Add the real message to the list
        setMessages(prev => {
          const newMessages = [...prev, messageData];
          return newMessages;
        });
        

        

        
        // Scroll to bottom to show the new message
        setTimeout(() => {
          scrollToBottom(true);
        }, 100);
        
        // Message is already added with 'sent' status
        // Status updates are now handled by the consolidated checkMessageStatusUpdates function
        
        // Update conversation last message
        updateConversationLastMessage(selectedConversation.phone_number, textToSend);
        
        // Refresh chat window timer since we sent a message
        setTimeout(() => {
          updateChatWindowTimer();
        }, 100);
        
        // Close quick replies and templates
        setShowQuickReplies(false);
        setShowTemplates(false);
        
          // Clear reply state after successful send
  if (replyingToMessage) {
    
    setReplyingToMessage(null);
  }
  

  
  // Clear editing state when sending new messages
  if (editingMessage) {
    setEditingMessage(null);
    setEditMessageText('');
  }
        
        // Schedule an update to replace the temporary ID with the real database ID
        if (response.data.data.dbId) {
          setTimeout(() => {
            setMessages(prev => prev.map(msg => 
              msg.id === messageData.id 
                ? { ...msg, id: response.data.data.dbId, isNewlySent: false }
                : msg
            ));
          }, 1000); // Update after 1 second
        }
        
        // Status progression is now handled automatically by the consolidated checkMessageStatusUpdates function
        
        toast.success('Message sent successfully!');
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [newMessage, selectedConversation?.phone_number, messages]);

  // Delete conversation functions
  const handleDeleteConversation = async (conversationId) => {
    try {
      setLoading(true);
      const response = await adminAPI.deleteWhatsAppConversation(conversationId);
      
      if (response.success) {
        // Remove from conversations list
        setConversations(prev => prev.filter(conv => conv.id !== conversationId));
        
        // If this was the selected conversation, clear it
        if (selectedConversation?.id === conversationId) {
          setSelectedConversation(null);
          setMessages([]);
        }
        
        toast.success('Conversation deleted successfully');
      } else {
        toast.error(response.message || 'Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation: ' + error.message);
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setConversationToDelete(null);
    }
  };

  const handleBulkDeleteConversations = async () => {
    try {
      setLoading(true);
      const conversationIds = Array.from(selectedConversations);
      const response = await adminAPI.bulkDeleteWhatsAppConversations(conversationIds);
      
      if (response.success) {
        // Remove deleted conversations from list
        setConversations(prev => prev.filter(conv => !selectedConversations.has(conv.id)));
        
        // Clear selection if selected conversation was deleted
        if (selectedConversation && selectedConversations.has(selectedConversation.id)) {
          setSelectedConversation(null);
          setMessages([]);
        }
        
        // Clear bulk selection
        setSelectedConversations(new Set());
        setIsBulkMode(false);
        
        toast.success(response.message || 'Conversations deleted successfully');
      } else {
        toast.error(response.message || 'Failed to delete conversations');
      }
    } catch (error) {
      console.error('Error bulk deleting conversations:', error);
      toast.error('Failed to delete conversations: ' + error.message);
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const toggleBulkMode = () => {
    setIsBulkMode(!isBulkMode);
    if (isBulkMode) {
      setSelectedConversations(new Set());
    }
  };

  const toggleConversationSelection = (conversationId) => {
    setSelectedConversations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(conversationId)) {
        newSet.delete(conversationId);
      } else {
        newSet.add(conversationId);
      }
      return newSet;
    });
  };

  const selectAllConversations = () => {
    const allIds = filteredConversations.slice(0, 50).map(conv => conv.id);
    setSelectedConversations(new Set(allIds));
  };

  const clearSelection = () => {
    setSelectedConversations(new Set());
  };

  const showDeleteDialog = (type, conversation = null) => {
    setDeleteType(type);
    setConversationToDelete(conversation);
    setShowDeleteConfirm(true);
  };

  // Handle file selection - show preview instead of auto-sending
  const handleFileSelect = (file) => {
    if (!file) return;
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    
    setPreviewFile(file);
    setFilePreview({
      file: file,
      previewUrl: previewUrl,
      type: file.type.startsWith('image/') ? 'image' : 
            file.type.startsWith('video/') ? 'video' : 
            file.type.startsWith('audio/') ? 'audio' : 'document'
    });
    setShowFilePreview(true);
    setPreviewCaption('');
  };

  // Multi-file helper: if multiple, send sequentially without preview
  const handleFilesSelect = async (files) => {
    const fileArray = Array.from(files || []);
    if (fileArray.length === 0) return;
    // Build multi preview items
    const items = fileArray.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      type: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'document',
      caption: ''
    }));
    setMultiPreviewFiles(items);
    setMultiPreviewIndex(0);
    setShowMultiPreview(true);
  };

  // Camera helpers
  const openCamera = async () => {
    try {
      setCameraError('');
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      cameraStreamRef.current = stream;
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera open error:', err);
      setCameraError(err?.message || 'Unable to access camera');
    }
  };

  const closeCamera = () => {
    try {
      setShowCameraModal(false);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(t => t.stop());
        cameraStreamRef.current = null;
      }
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = null;
      }
      setIsCameraRecording(false);
      recordedChunksRef.current = [];
    } catch (e) {
      console.warn('Error closing camera:', e);
    }
  };

  useEffect(() => {
    if (showCameraModal) {
      openCamera();
    } else {
      // ensure cleanup
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(t => t.stop());
        cameraStreamRef.current = null;
      }
    }
    // cleanup on unmount
    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(t => t.stop());
        cameraStreamRef.current = null;
      }
    };
  }, [showCameraModal]);

  const capturePhoto = async () => {
    try {
      const videoEl = cameraVideoRef.current;
      if (!videoEl) return;
      const canvas = document.createElement('canvas');
      canvas.width = videoEl.videoWidth || 1280;
      canvas.height = videoEl.videoHeight || 720;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      if (!blob) return;
      const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
      handleFileSelect(file);
      setShowCameraModal(false);
      closeCamera();
    } catch (e) {
      console.error('Capture photo error:', e);
      setCameraError('Failed to capture photo');
    }
  };

  const toggleRecording = () => {
    if (isCameraRecording) {
      // stop
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
      setIsCameraRecording(false);
    } else {
      const stream = cameraStreamRef.current;
      if (!stream) return;
      recordedChunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus' });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const file = new File([blob], `camera_${Date.now()}.webm`, { type: 'video/webm' });
        handleFileSelect(file);
        setShowCameraModal(false);
        closeCamera();
      };
      recorder.start();
      setIsCameraRecording(true);
    }
  };

  const sendFile = async (file, caption = '') => {
    if (!selectedConversation) return;

    setLoading(true);
    try {

      
      // 1) Upload file to server
      let uploadResponse;
      const fileName = file.name.toLowerCase();
      if (file.type.startsWith('image/')) {
        const formData = new FormData();
        formData.append('image', file);
        uploadResponse = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else if (file.type.startsWith('audio/')) {
        const formData = new FormData();
        formData.append('audio', file);
        uploadResponse = await api.post('/upload/audio', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else if (file.type.startsWith('video/')) {
        const formData = new FormData();
        formData.append('video', file);
        uploadResponse = await api.post('/upload/video', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        const formData = new FormData();
        formData.append('document', file);
        uploadResponse = await api.post('/upload/document', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      const fileUrl = uploadResponse?.data?.data?.url || uploadResponse?.data?.url || uploadResponse?.data?.fileUrl;
      if (!fileUrl) throw new Error('Upload failed: no URL returned');
      


      // 2) Send via WhatsApp using API service methods
      const mediaType = file.type.startsWith('image/')
        ? 'image'
        : file.type.startsWith('video/')
        ? 'video'
        : file.type.startsWith('audio/')
        ? 'audio'
        : 'document';

      let whatsappResponse;
      try {
        // Prepare the request payload with reply context if this is a reply
        const requestPayload = {
        phoneNumber: selectedConversation.phone_number,
          caption: caption
        };

        // Add reply context if this is a reply to another message
        if (replyingToMessage) {
          requestPayload.replyTo = {
            id: replyingToMessage.id,
            message_id: replyingToMessage.message_id,
            message_content: replyingToMessage.message_content,
            direction: replyingToMessage.direction,
            message_type: replyingToMessage.message_type
          };
  
        }

      if (mediaType === 'document') {
          whatsappResponse = await whatsappAPI.sendDocument({
            ...requestPayload,
            documentUrl: fileUrl,
            filename: file.name
          });
      } else if (mediaType === 'audio') {
          whatsappResponse = await whatsappAPI.sendAudio({
            ...requestPayload,
            mediaUrl: fileUrl
          });
      } else if (mediaType === 'video') {
          whatsappResponse = await whatsappAPI.sendVideo({
            ...requestPayload,
            mediaUrl: fileUrl
          });
      } else {
          whatsappResponse = await whatsappAPI.sendMedia({
            ...requestPayload,
            mediaUrl: fileUrl,
            mediaType: 'image'
          });
        }



      if (!whatsappResponse?.data?.success) {
        throw new Error('WhatsApp API failed to send file: ' + (whatsappResponse?.data?.message || 'Unknown error'));
      }

        // Track WhatsApp file sent
        trackEvent('whatsapp_file_sent', {
          file_type: mediaType,
          file_size: file.size,
          has_caption: !!caption,
          conversation_phone: selectedConversation.phone_number
        });

      // 3) Update UI
        const messageId = whatsappResponse?.data?.data?.dbId || whatsappResponse?.data?.dbId || whatsappResponse?.data?.data?.messageId || whatsappResponse?.data?.messageId || whatsappResponse?.data?.data?.data?.messages?.[0]?.id || Date.now();

      const messageData = {
          id: messageId,
          message_id: whatsappResponse?.data?.data?.messageId || whatsappResponse?.data?.messageId, // Store WhatsApp message ID for status tracking
        phone_number: selectedConversation.phone_number,
        message_type: mediaType,
          message_content: caption || null, // Use caption as message content
        media_url: fileUrl,
        media_type: mediaType,
        direction: 'outbound',
        status: 'sent',
          created_at: new Date().toISOString(),
          // Add reply information if this is a reply
          ...(replyingToMessage && {
            reply_to: replyingToMessage,
            reply_to_message_id: replyingToMessage.message_id || replyingToMessage.id
          })
        };
        

        
        // Add the attachment message to UI
        setMessages(prev => {
          const newMessages = [...prev, messageData];
          return newMessages;
        });
        
      updateConversationLastMessage(selectedConversation.phone_number, `📎 ${file.name}`);
        toast.success('File sent successfully!');
        
      } catch (whatsappError) {
        console.error('📎 WhatsApp sending error:', whatsappError);
        throw new Error(`WhatsApp sending failed: ${whatsappError.message}`);
      }
      
    } catch (error) {
      console.error('❌ Error sending file:', error);
      toast.error('Failed to send file: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
      setShowFileUpload(false);
    }
  };

  // Attachment helpers
  const openAttachMenu = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 240;
    const estimatedHeight = 420; // allow for all options
    let x = rect.left;
    let y = rect.top - estimatedHeight - 12; // bias upwards a bit more
    // If above viewport, place below trigger
    if (y < 8) y = rect.bottom + 8;
    // If still overflowing bottom, clamp within viewport with some margin
    const maxY = window.innerHeight - estimatedHeight - 12;
    if (y > maxY) y = Math.max(8, maxY);
    // Clamp horizontally
    if (x + width > window.innerWidth - 8) x = rect.right - width;
    if (x < 8) x = 8;
    setAttachMenuCoords({ x, y });
    setShowAttachMenu(true);
  };

  const sendTextStub = async (content, typeLabel = 'info') => {
    if (!selectedConversation) return;
    
    try {
      // Send via WhatsApp API
      const response = await whatsappAPI.sendMessage({
        phoneNumber: selectedConversation.phone_number,
        message: content
      });

      if (response.data?.success) {
    const messageData = {
          id: response.data?.data?.dbId || response.data?.data?.messageId || Date.now(),
          message_id: response.data?.data?.messageId || response.data?.messageId,
      phone_number: selectedConversation.phone_number,
      message_type: 'text',
      message_content: content,
      direction: 'outbound',
      status: 'sent',
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, messageData]);
    updateConversationLastMessage(selectedConversation.phone_number, content);
        toast.success(`${typeLabel} sent successfully`);
      } else {
        throw new Error(response.data?.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(`Failed to send ${typeLabel}: ${error.message}`);
    }
  };

  const handleSendLocation = async () => {
    try {
      // Open location modal, keep company address fetched separately
      try {
      const mgmt = await api.get('/whatsapp/management');
        const ca = mgmt.data?.data?.location?.address || '';
        setCompanyAddress(ca);
      } catch {}
      setMapAddress('');
      setMapMarker(null);
      setShowMapPanel(true);
    } finally {
      setShowAttachMenu(false);
    }
  };

  const handleSendCompanyAddress = async () => {
    try {
      const mgmt = await api.get('/whatsapp/management');
      const loc = mgmt.data?.data?.location || {};
      const address = loc.address || '';
      let latitude = loc.latitude ? Number(loc.latitude) : null;
      let longitude = loc.longitude ? Number(loc.longitude) : null;

      if ((!latitude || !longitude) && address) {
        try {
          const google = await loadGoogle();
          const { Geocoder } = await google.maps.importLibrary('geocoding');
          const geocoder = new Geocoder();
          const result = await geocoder.geocode({ address });
          if (result.results.length) {
            const p = result.results[0].geometry.location;
            latitude = p.lat();
            longitude = p.lng();
          }
        } catch {}
      }

      // Fallback if still missing
      if (!latitude || !longitude) {
        latitude = 19.0760; longitude = 72.8777;
      }

      // Send immediately via WhatsApp API
      if (process.env.NODE_ENV !== 'production') {
        console.log('Sending company address location:', { latitude, longitude, name: 'Company Address', address });
      }
      const response = await whatsappAPI.sendLocation({
        phoneNumber: selectedConversation.phone_number,
        location: { latitude, longitude, name: 'Company Address', address },
        context: {}
      });

      if (response.data?.success) {
        // The backend will now format the message content properly
        const messageData = {
          id: response.data?.messageId || Date.now(),
          message_id: response.data?.messageId,
          phone_number: selectedConversation.phone_number,
          message_type: 'location',
          message_content: `🏢 Company Address\n📍 ${address || '—'}\n🌍 ${latitude}, ${longitude}`,
          direction: 'outbound',
          status: 'sent',
          created_at: new Date().toISOString(),
          location: { latitude, longitude, address }
        };
        setMessages(prev => [...prev, messageData]);
        updateConversationLastMessage(selectedConversation.phone_number, `🏢 Company Address`);
        toast.success('Company address sent');
      } else {
        throw new Error(response.data?.error || 'Failed to send company address');
      }
    } catch (error) {
      console.error('Error sending company address:', error);
      toast.error('Failed to send company address');
    } finally {
    setShowAttachMenu(false);
    }
  };

  const handleSendContact = async () => {
    const name = prompt('Contact Name');
    if (name === null) return; // cancelled
    const phone = prompt('Contact Phone (with country code)');
    if (phone === null) return;
    
    // Track WhatsApp contact share
    trackEvent('whatsapp_contact_shared', {
      conversation_phone: selectedConversation.phone_number
    });
    
    await sendTextStub(`👤 Contact:\nName: ${name}\nPhone: ${phone}`, 'Contact');
    setShowAttachMenu(false);
  };

  const handleSendPoll = async () => {
    try {
      const mgmt = await api.get('/whatsapp/management');
      const first = mgmt.data?.data?.polls?.[0];
      const question = first?.question || prompt('Poll question');
      if (question === null) return;
      
      // Track WhatsApp poll sent
      trackEvent('whatsapp_poll_sent', {
        conversation_phone: selectedConversation.phone_number,
        poll_question: question
      });
      
      await sendTextStub(`📊 Poll: ${question}\nReply with: 1) Yes  2) No`, 'Poll');
    } catch (_) {
    const question = prompt('Poll question');
    if (question === null) return;
    await sendTextStub(`📊 Poll: ${question}\nReply with: 1) Yes  2) No`, 'Poll');
    }
    setShowAttachMenu(false);
  };

  const handleSendEvent = async () => {
    try {
      const mgmt = await api.get('/whatsapp/management');
      const first = mgmt.data?.data?.events?.[0];
      const title = first?.title || prompt('Event title');
      if (title === null) return;
      await sendTextStub(`📅 Event: ${title}\nDetails will follow.`, 'Event');
    } catch (_) {
    const title = prompt('Event title');
    if (title === null) return;
    await sendTextStub(`📅 Event: ${title}\nDetails will follow.`, 'Event');
    }
    setShowAttachMenu(false);
  };

  const handleSendPaymentLink = async () => {
    try {
      const mgmt = await api.get('/whatsapp/management');
      const url = mgmt.data?.data?.payment?.link || prompt('Payment Link URL');
      if (url === null) return;
      
      // Track WhatsApp payment link sent
      trackEvent('whatsapp_payment_link_sent', {
        conversation_phone: selectedConversation.phone_number,
        payment_url: url
      });
      
      await sendTextStub(`💳 Payment Link: ${url}`, 'Payment Link');
    } catch (_) {
    const url = prompt('Payment Link URL');
    if (url === null) return;
    await sendTextStub(`💳 Payment Link: ${url}`, 'Payment Link');
    }
    setShowAttachMenu(false);
  };

  const handleSendPaymentQR = async () => {
    try {
      const mgmt = await api.get('/whatsapp/management');
      const qr = mgmt.data?.data?.payment?.qr;
      

      
      if (qr) {
        let qrUrl = qr;
        
        // Handle different QR formats
        if (typeof qr === 'string') {
          if (qr.startsWith('data:image/')) {
            // Already a data URL
            qrUrl = qr;
          } else if (qr.startsWith('http')) {
            // HTTP URL
            qrUrl = qr;
          } else if (qr.length > 100 && !qr.includes(' ')) {
            // Likely a base64 string without prefix - add data:image/png prefix
            qrUrl = `data:image/png;base64,${qr}`;
          }
        }
        
        // Try to send as image if we have a valid image URL
        if (qrUrl && (qrUrl.startsWith('data:image/') || qrUrl.startsWith('http'))) {

          
          const response = await whatsappAPI.sendMedia({
            phoneNumber: selectedConversation.phone_number,
            mediaUrl: qrUrl,
            caption: '💳 Payment QR Code - Please scan to make payment',
            mediaType: 'image'
          });

          if (response.data?.success) {
            const messageData = {
              id: response.data?.data?.dbId || response.data?.data?.messageId || Date.now(),
              message_id: response.data?.data?.messageId || response.data?.messageId,
              phone_number: selectedConversation.phone_number,
              message_type: 'image',
              message_content: '💳 Payment QR Code - Please scan to make payment',
              media_url: qrUrl,
              media_type: 'image',
              direction: 'outbound',
              status: 'sent',
              created_at: new Date().toISOString()
            };
            setMessages(prev => [...prev, messageData]);
            updateConversationLastMessage(selectedConversation.phone_number, '💳 Payment QR Code');
            toast.success('Payment QR sent successfully');
            return; // Success, exit early
      } else {
            throw new Error(response.data?.message || 'Failed to send QR image');
          }
        }
      }
      
      // If we reach here, either no QR or failed to send as image
      await sendTextStub('💳 Payment QR: Please scan the QR shared by our team.', 'Payment QR');
      
    } catch (error) {
      console.error('Error sending payment QR:', error);
      toast.error('Failed to send payment QR: ' + error.message);
      // Fallback to text
      await sendTextStub('💳 Payment QR: Please scan the QR shared by our team.', 'Payment QR');
    } finally {
    setShowAttachMenu(false);
    }
  };

  const handleSendPaymentDetails = async () => {
    try {
      const mgmt = await api.get('/whatsapp/management');
      const details = mgmt.data?.data?.payment?.details;
      await sendTextStub(`🏦 Payment Details:\n${details || 'Account: Niswa Info Tech\nUPI: niswa@upi'}`, 'Payment Details');
    } catch (_) {
    await sendTextStub('🏦 Payment Details:\nAccount: Niswa Info Tech\nUPI: niswa@upi', 'Payment Details');
    }
    setShowAttachMenu(false);
  };

  // Fetch management data for attachments
  const fetchManagementData = async () => {
    try {
      const response = await api.get('/whatsapp/management');
      if (response.data?.success) {
        setManagementData(response.data.data);

      }
    } catch (error) {
      console.error('Error fetching management data:', error);
    }
  };

  // Load products and send product helpers
  const loadProducts = async (searchTerm = '', { limit = 6, append = false, page = 1 } = {}) => {
    setProductsLoading(true);
    try {
      const resp = await productAPI.getAll({ status: 'active', search: searchTerm || undefined, limit, page });
      if (resp?.data?.success) {
        const list = resp.data.data.products || [];
        setProducts(prev => append ? [...prev, ...list] : list);
      }
    } catch (e) {
      console.error('Failed to load products', e);
    } finally {
      setProductsLoading(false);
    }
  };

  const sendProduct = async (product) => {
    if (!selectedConversation) return;
    const productUrl = product.url || product.slug ? `${window.location.origin}/product/${product.slug || product.id}` : `${window.location.origin}/products`;
    
    // Handle different license types and determine display price
    let displayPrice = '';
    let priceText = '';
    
    if (product.regularLicensePrice || product.regular_license_price) {
      const regularPrice = product.regularLicensePrice || product.regular_license_price;
      displayPrice = formatPrice(regularPrice);
      priceText = `Starting from *${displayPrice}*`;
    } else if (product.extendedLicensePrice || product.extended_license_price) {
      const extendedPrice = product.extendedLicensePrice || product.extended_license_price;
      displayPrice = formatPrice(extendedPrice);
      priceText = `Starting from *${displayPrice}*`;
    } else if (product.formattedPrice || product.price) {
      // Fallback to general price fields
      const fallbackPrice = product.formattedPrice || product.price;
      displayPrice = formatPrice(fallbackPrice);
      priceText = `Starting from *${displayPrice}*`;
    }
    
    const image = (product.image_url || product.image || (Array.isArray(product.images) && (product.images[0]?.url || product.images[0]))) || '';
    const shortDesc = (product.short_description || product.shortDescription || product.description || '').toString().replace(/\s+/g, ' ').slice(0, 160);
    
    // Create message with precise spacing rules:
    // - No blank line between title and description
    // - One blank line before price block (if present)
    // - One blank line before View Product block
    // - No blank line between View Product label and URL (placed on next line)
    const messageLines = [];
    // Title
    messageLines.push(`🛍️ *${product.name}*`);
    // Description directly below title (no blank line)
    if (shortDesc) {
      messageLines.push(`📝 ${shortDesc}`);
    }
    // Blank line before price (only if price exists)
    if (displayPrice) {
      messageLines.push('');
      messageLines.push(`💵 Starting from *${displayPrice}*`);
    }
    // Blank line before View Product
    messageLines.push('');
    // Label and link with no blank line between them
    messageLines.push(`🔗 *View Product:*`);
    messageLines.push(`${productUrl}`);

    const messageText = messageLines.join('\n');

    try {
      // If we have an image, send as media with button
      if (image) {
        const requestPayload = {
          phoneNumber: selectedConversation.phone_number,
          mediaUrl: image,
          caption: messageText,
          mediaType: 'image'
        };
        
        const whatsappResponse = await whatsappAPI.sendMedia({ ...requestPayload, mediaType: 'image' });
        if (!whatsappResponse?.data?.success) throw new Error(whatsappResponse?.data?.message || 'Failed');

        const messageId = whatsappResponse?.data?.data?.dbId || whatsappResponse?.data?.dbId || whatsappResponse?.data?.data?.messageId || whatsappResponse?.data?.messageId || Date.now();
        const messageData = {
          id: messageId,
          message_id: whatsappResponse?.data?.data?.messageId || whatsappResponse?.data?.messageId,
          phone_number: selectedConversation.phone_number,
          message_type: 'image',
          message_content: messageText || null,
          media_url: image,
          media_type: 'image',
          direction: 'outbound',
          status: 'sent',
          created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, messageData]);
        updateConversationLastMessage(selectedConversation.phone_number, `🛍️ ${product.name} - ${displayPrice}`);
      } else {
        // Send as text message with clickable link if no image
        const textPayload = {
          phoneNumber: selectedConversation.phone_number,
          message: messageText
        };
        
        const whatsappResponse = await whatsappAPI.sendMessage(textPayload);
        if (!whatsappResponse?.data?.success) throw new Error(whatsappResponse?.data?.message || 'Failed');

        const messageId = whatsappResponse?.data?.data?.dbId || whatsappResponse?.data?.dbId || whatsappResponse?.data?.data?.messageId || whatsappResponse?.data?.messageId || Date.now();
        const messageData = {
          id: messageId,
          message_id: whatsappResponse?.data?.data?.messageId || whatsappResponse?.data?.messageId,
          phone_number: selectedConversation.phone_number,
          message_type: 'text',
          message_content: messageText || null,
          direction: 'outbound',
          status: 'sent',
          created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, messageData]);
        updateConversationLastMessage(selectedConversation.phone_number, `🛍️ ${product.name} - ${displayPrice}`);
      }
    } catch (err) {
      console.error('Failed to send product:', err);
      toast.error('Failed to send product');
    }
  };

  const toggleSelectProduct = (id) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const sendSelectedProducts = async () => {
    if (!selectedConversation || selectedProducts.size === 0) return;
    const all = products.filter(p => selectedProducts.has(p.id));
    for (const p of all) {
      // reuse enhanced sendProduct formatting
      // eslint-disable-next-line no-await-in-loop
      await sendProduct(p);
    }
    setSelectedProducts(new Set());
    setShowProductsModal(false);
  };

  // Optimized conversation update function with useCallback
  const updateConversationLastMessage = useCallback((phoneNumber, message) => {
    setConversations(prev => 
      prev.map(conv => 
        conv.phone_number === phoneNumber 
          ? { ...conv, last_message: message, last_message_at: new Date().toISOString() }
          : conv
      )
    );
  }, []);

  // Optimized conversation selection handler with useCallback
  const handleConversationSelect = useCallback((conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.phone_number);
    setShowConversationInfo(false);
    setShowQuickReplies(false);
    setShowTemplates(false);
    setShowNotes(false);
    setShowAgents(false);
    
    // Clear editing state when switching conversations
    setEditingMessage(null);
    setEditMessageText('');
    
    // Clear location modal state when switching conversations
    setShowLocationModal(false);
    setSelectedLocation(null);
    
    // Load conversation notes from local storage
    const notesKey = `conversation_notes_${conversation.phone_number}`;
    const savedNotes = localStorage.getItem(notesKey);
    if (savedNotes) {
      setConversationNotes(savedNotes);
    } else {
      setConversationNotes('');
    }

    // Check if there's a message to forward
    const messageToForward = localStorage.getItem('messageToForward');
    if (messageToForward) {
      try {
        const forwardData = JSON.parse(messageToForward);
        const confirmed = window.confirm(
          `Forward message to ${conversation.phone_number}?\n\n"${forwardData.content.substring(0, 50)}${forwardData.content.length > 50 ? '...' : ''}"`
        );
        
        if (confirmed) {
          // Send the forwarded message
          if (forwardData.type === 'text') {
            sendMessage(forwardData.content);
          } else {
            // Handle media forwarding
            toast.info('Media forwarding will be implemented soon');
          }
          
          // Clear the forwarded message
          localStorage.removeItem('messageToForward');
        }
      } catch (error) {
        console.error('Error handling forwarded message:', error);
        localStorage.removeItem('messageToForward');
      }
    }
  }, [sendMessage]);



  // Optimized key press handler with useCallback to prevent re-renders
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  // Track previous conversation to detect actual changes
  const prevConversationRef = useRef(null);
  
  // Cleanup location modal state when conversation changes
  useEffect(() => {
    const currentConversation = selectedConversation?.phone_number;
    const prevConversation = prevConversationRef.current;
    
    // Only clear location modal state when actually switching to a different conversation
    if (prevConversation && currentConversation && prevConversation !== currentConversation) {
      setShowLocationModal(false);
      setSelectedLocation(null);
    }
    
    // Update the ref with current conversation
    prevConversationRef.current = currentConversation;
  }, [selectedConversation?.phone_number]);

  // Cleanup on component unmount and page reload
  useEffect(() => {
    const handleBeforeUnload = () => {
      setShowLocationModal(false);
      setSelectedLocation(null);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setShowLocationModal(false);
        setSelectedLocation(null);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setShowLocationModal(false);
      setSelectedLocation(null);
    };
  }, []);

  // Global keyboard shortcuts for message selection
  useEffect(() => {
    const handleGlobalKeyPress = (e) => {
      // Only handle shortcuts when not typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key) {
        case 'Escape':
          // Clear message selection
          if (selectedMessages.size > 0) {
            clearMessageSelection();
            e.preventDefault();
          }
          // Also clear editing state
          if (editingMessage) {
            setEditingMessage(null);
            setEditMessageText('');
            e.preventDefault();
          }
          // Also clear location modal state
          if (showLocationModal) {
            setShowLocationModal(false);
            setSelectedLocation(null);
            e.preventDefault();
          }
          break;
        case 'a':
        case 'A':
          // Select all messages (Ctrl+A)
          if (e.ctrlKey || e.metaKey) {
            selectAllMessages();
            e.preventDefault();
          }
          break;
        case 'Delete':
        case 'Backspace':
          // Delete selected messages
          if (selectedMessages.size > 0) {
            deleteSelectedMessages(false); // Delete for me
            e.preventDefault();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleGlobalKeyPress);
    return () => document.removeEventListener('keydown', handleGlobalKeyPress);
  }, [selectedMessages.size, editingMessage]);

  // Optimized message input change handler
  const handleMessageChange = useCallback((e) => {
    setNewMessage(e.target.value);
  }, []);



  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  // WhatsApp Chat Window Timer Functions
  const calculateChatWindowTimer = useCallback((lastMessageTime) => {
    if (!lastMessageTime) return null;
    
    const now = new Date();
    const lastMessage = new Date(lastMessageTime);
    const chatWindowDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    const timeElapsed = now.getTime() - lastMessage.getTime();
    const remainingTime = chatWindowDuration - timeElapsed;
    
    return {
      remainingTime: Math.max(0, remainingTime),
      isExpired: remainingTime <= 0,
      canSendMessage: remainingTime > 0
    };
  }, []);

  const formatRemainingTime = (milliseconds) => {
    if (milliseconds <= 0) return 'Expired';
    
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else if (minutes > 0) {
      return `${minutes}m remaining`;
    } else {
      return 'Less than 1m remaining';
    }
  };

  const updateChatWindowTimer = useCallback(() => {
    // Use last_inbound_at for chat window expiration (WhatsApp rule: 24h after last user message)
    // Fallback to last_message_at if last_inbound_at is not available
    const timestampToUse = selectedConversation?.last_inbound_at || selectedConversation?.last_message_at;
    if (!timestampToUse) return;
    
    const timer = calculateChatWindowTimer(timestampToUse);
    setChatWindowTimer(timer);
  }, [selectedConversation?.last_inbound_at, selectedConversation?.last_message_at, calculateChatWindowTimer]);

  // Check if current conversation can send messages
  const canSendMessage = useCallback(() => {
    return chatWindowTimer && !chatWindowTimer.isExpired;
  }, [chatWindowTimer]);

  // Get chat window status for any conversation
  const getConversationChatWindowStatus = useCallback((conversation) => {
    // Use last_inbound_at for chat window expiration (WhatsApp rule: 24h after last user message)
    // Fallback to last_message_at if last_inbound_at is not available
    const timestampToUse = conversation?.last_inbound_at || conversation?.last_message_at;
    if (!timestampToUse) return null;
    return calculateChatWindowTimer(timestampToUse);
  }, [calculateChatWindowTimer]);

  const getMessageStatus = (status) => {
    switch (status) {
      case 'sent':
        return <Check className="w-3 h-3 text-gray-400" />;
      case 'delivered':
        return <CheckCheck className="w-3 h-3 text-gray-400" />;
      case 'read':
        return <CheckCheck className="w-3 h-3 text-blue-500" />;
      case 'failed':
        return <AlertCircle className="w-3 h-3 text-red-500" />;
      default:
        return <Clock className="w-3 h-3 text-gray-400" />;
    }
  };

  // Advanced Features Functions
  const handleQuickReply = (reply) => {
    const messageText = String(reply?.text || '');
    if (messageText.trim()) {
      sendMessage(messageText);
    }
  };

  const handleTemplateUse = (template) => {
    setNewMessage(template.text);
    setShowTemplates(false);
  };

  const saveConversationNotes = async () => {
    if (!selectedConversation) return;
    
    try {
      // For now, save notes locally since backend endpoint might not exist
      const notesKey = `conversation_notes_${selectedConversation.phone_number}`;
      localStorage.setItem(notesKey, conversationNotes);
      
      // Try to save to backend if endpoint exists
      try {
        await api.post('/whatsapp/conversation-notes', {
          conversationId: selectedConversation.id,
          notes: conversationNotes
        });
      } catch (backendError) {

      }
      
      toast.success('Notes saved successfully!');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const assignAgent = async () => {
    if (!selectedConversation || !selectedAgent) return;
    
    try {
      await api.post('/whatsapp/assign-conversation', {
        conversationId: selectedConversation.id,
        agentId: selectedAgent
      });
      toast.success('Agent assigned successfully!');
      setShowAgents(false);
    } catch (error) {
      console.error('Error assigning agent:', error);
      toast.error('Failed to assign agent');
    }
  };

  const updateConversationStatus = async (status) => {
    if (!selectedConversation) return;
    
    try {
      // Update local state immediately for better UX
      setConversations(prev => 
        prev.map(conv => 
          conv.id === selectedConversation.id 
            ? { ...conv, conversation_status: status }
            : conv
        )
      );
      
      // Update selected conversation status
      setSelectedConversation(prev => 
        prev ? { ...prev, conversation_status: status } : prev
      );
      
      // Try to update on backend
      try {
        await api.post('/whatsapp/update-conversation-status', {
          conversationId: selectedConversation.id,
          status: status
        });
        
        toast.success(`Conversation marked as ${status}`);
      } catch (backendError) {

        toast.success(`Conversation marked as ${status} (local only)`);
      }
    } catch (error) {
      console.error('Error updating conversation status:', error);
      toast.error('Failed to update status');
    }
  };

  const analyzeConversation = async () => {
    if (!selectedConversation || messages.length === 0) {
      toast.warning('No conversation or messages available for analysis');
      return;
    }
    
    // Toggle analysis panel
    if (aiSuggestions.length > 0) {
      setAiSuggestions([]);
      toast.info('Analysis panel closed');
      return;
    }
    
    setIsAnalyzing(true);
    
    // Track WhatsApp conversation analysis
    trackEvent('whatsapp_conversation_analyzed', {
      conversation_phone: selectedConversation.phone_number,
      message_count: messages.length
    });
    
    try {
      const response = await api.post('/ai/analyze-conversation', {
        conversation_id: selectedConversation.id,
        messages: messages.slice(-10) // Analyze last 10 messages
      });
      
      if (response.data.success) {
        setAiSuggestions(response.data.suggestions || []);
        toast.success('Conversation analyzed successfully!');
      }
    } catch (error) {
      console.error('Error analyzing conversation:', error);
      // Fallback suggestions
      setAiSuggestions([
        { type: 'sentiment', text: 'Customer appears satisfied', confidence: 0.8 },
        { type: 'intent', text: 'Looking for product information', confidence: 0.7 },
        { type: 'suggestion', text: 'Offer product catalog', confidence: 0.9 }
      ]);
      toast.success('Analysis completed with fallback data!');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const generateSmartReply = async () => {
    if (!selectedConversation || messages.length === 0) {
      toast.warning('No conversation or messages available for smart reply generation');
      return;
    }
    
    setIsGeneratingReply(true);
    try {
      // Find the most recent inbound message (from customer)
      const inboundMessages = messages.filter(msg => msg.direction === 'inbound');
      const lastInboundMessage = inboundMessages[inboundMessages.length - 1];
      
      if (lastInboundMessage) {
        // Show loading state
        toast.info('Generating smart reply...');
        
        const response = await api.post('/ai/generate-reply', {
          message: lastInboundMessage.message_content,
          conversation_context: messages.slice(-5).map(m => m.message_content).join(' ')
        });
        
        if (response.data.success) {
          setNewMessage(response.data.reply);
          toast.success('Smart reply generated!');
        } else {
          // Fallback smart replies based on message content
          const fallbackReplies = generateFallbackReplies(lastInboundMessage.message_content);
          setNewMessage(fallbackReplies[0]);
          toast.success('Smart reply generated (fallback)!');
        }
      } else {
        // If no inbound messages, generate a general reply
        toast.info('Generating general smart reply...');
        
        const response = await api.post('/ai/generate-reply', {
          message: 'Customer inquiry',
          conversation_context: 'General conversation context'
        });
        
        if (response.data.success) {
          setNewMessage(response.data.reply);
          toast.success('Smart reply generated!');
        } else {
          // Fallback general reply
          setNewMessage('Hello! How can I help you today?');
          toast.success('Smart reply generated (fallback)!');
        }
      }
    } catch (error) {
      console.error('Error generating smart reply:', error);
      
      // Fallback smart replies
      if (messages.length > 0) {
        const inboundMessages = messages.filter(msg => msg.direction === 'inbound');
        const lastInboundMessage = inboundMessages[inboundMessages.length - 1];
        
        if (lastInboundMessage) {
          const fallbackReplies = generateFallbackReplies(lastInboundMessage.message_content);
          setNewMessage(fallbackReplies[0]);
          toast.success('Smart reply generated (fallback)!');
        } else {
          setNewMessage('Hello! How can I help you today?');
          toast.success('Smart reply generated (fallback)!');
        }
      } else {
        toast.error('Failed to generate smart reply');
      }
    } finally {
      setIsGeneratingReply(false);
    }
  };

  const generateFallbackReplies = (messageContent) => {
    const content = messageContent.toLowerCase();
    
    if (content.includes('hello') || content.includes('hi') || content.includes('hey')) {
      return [
        'Hello! How can I help you today?',
        'Hi there! Welcome to Niswa Info Tech. How may I assist you?',
        'Hello! Thanks for reaching out. What can I do for you?'
      ];
    } else if (content.includes('price') || content.includes('cost') || content.includes('quote')) {
      return [
        'I\'d be happy to provide you with a quote. Could you share more details about your requirements?',
        'For pricing information, please let me know the specific services you\'re interested in.',
        'I can help you with pricing. What type of project are you looking for?'
      ];
    } else if (content.includes('service') || content.includes('help') || content.includes('support')) {
      return [
        'We offer web development, mobile apps, and digital marketing services. What specific service are you looking for?',
        'I\'m here to help! We provide comprehensive digital solutions. What do you need assistance with?',
        'Our services include web development, mobile apps, and digital marketing. Which area interests you?'
      ];
    } else if (content.includes('thank') || content.includes('thanks')) {
      return [
        'You\'re welcome! Is there anything else I can help you with?',
        'My pleasure! Feel free to reach out if you need anything else.',
        'You\'re very welcome! Let me know if you have any other questions.'
      ];
    } else {
      return [
        'Thank you for your message. How can I assist you today?',
        'I understand your inquiry. Let me help you with that.',
        'Thanks for reaching out. I\'m here to help you with your needs.'
      ];
    }
  };

  const exportConversation = async () => {
    if (!selectedConversation) {
      toast.warning('No conversation selected for export');
      return;
    }
    
    try {
      toast.info('Preparing conversation export...');
      
      // First try the API endpoint
      try {
        const response = await api.get(`/whatsapp/conversations/export/${selectedConversation.phone_number}`, {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `conversation-${selectedConversation.phone_number}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        toast.success('Conversation exported successfully!');
        return;
      } catch (apiError) {

      }
      
      // Fallback: Generate text export
      const conversationText = generateConversationText();
      const blob = new Blob([conversationText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `conversation-${selectedConversation.phone_number}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Conversation exported as text file!');
    } catch (error) {
      console.error('Error exporting conversation:', error);
      toast.error('Failed to export conversation');
    }
  };

  const exportAllConversations = async () => {
    try {
      const response = await api.get('/whatsapp/conversations');
      if (response.data.success) {
        const conversations = response.data.conversations;
        const csvContent = [
          ['Phone Number', 'Message Count', 'Last Message', 'Last Message Time'],
          ...conversations.map(conv => [
            conv.phone_number,
            conv.message_count,
            conv.last_message?.substring(0, 100),
            new Date(conv.last_message_at).toLocaleString()
          ])
        ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `all-conversations-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        toast.success('All conversations exported successfully');
      }
    } catch (error) {
      console.error('Error exporting all conversations:', error);
      toast.error('Failed to export conversations');
    }
  };

  const archiveConversation = async (conversationId) => {
    try {
      const response = await api.post(`/whatsapp/conversations/${conversationId}/archive`);
      if (response.data.success) {
        toast.success('Conversation archived successfully');
        fetchConversations();
      } else {
        toast.error('Failed to archive conversation');
      }
    } catch (error) {
      console.error('Error archiving conversation:', error);
      toast.error('Failed to archive conversation');
    }
  };

  const generateConversationText = () => {
    if (!selectedConversation || messages.length === 0) {
      return 'No messages in this conversation.';
    }
    
    let text = `WhatsApp Conversation Export\n`;
    text += `Phone Number: ${selectedConversation.phone_number}\n`;
    text += `Status: ${selectedConversation.conversation_status}\n`;
    text += `Export Date: ${new Date().toLocaleString()}\n`;
    text += `\n${'='.repeat(50)}\n\n`;
    
    messages.forEach((message, index) => {
      const timestamp = new Date(message.created_at).toLocaleString();
      const direction = message.direction === 'outbound' ? 'You' : 'Customer';
      const content = message.message_content || message.message || 'No content';
      
      text += `[${timestamp}] ${direction}:\n`;
      text += `${content}\n\n`;
    });
    
    return text;
  };

  const startVoiceRecording = async () => {
    setIsRecording(true);
    setShowVoiceRecorder(true);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Simple approach - use default MediaRecorder without specifying MIME type
      // This will use the browser's default format which is usually compatible
      const mediaRecorder = new MediaRecorder(stream);
      const audioChunks = [];
      
      if (process.env.NODE_ENV !== 'production') {
        console.log('🎤 Using default MediaRecorder format');
      }
      
      mediaRecorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };
      
      mediaRecorder.onstop = () => {
        const actualMimeType = mediaRecorder.mimeType || 'audio/webm';
        if (process.env.NODE_ENV !== 'production') {
          console.log('🎤 Default MediaRecorder MIME type:', actualMimeType);
        }
        
        // Create blob with the actual MIME type
        const audioBlob = new Blob(audioChunks, { type: actualMimeType });
        if (process.env.NODE_ENV !== 'production') {
          console.log('🎤 Audio blob created:', {
            type: audioBlob.type,
            size: audioBlob.size
          });
        }
        
        // Always mark as compatible - we'll handle conversion in sendVoiceMessage
        setVoicePreview({
          blob: audioBlob,
          url: URL.createObjectURL(audioBlob),
          duration: recordingTime,
          mimeType: actualMimeType,
          compatible: true
        });
        setShowVoicePreview(true);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      const timer = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      window.currentMediaRecorder = mediaRecorder;
      window.currentTimer = timer;
      
      toast.success('Voice recording started');
    } catch (error) {
      console.error('Error starting voice recording:', error);
      toast.error('Failed to start voice recording: ' + error.message);
    }
  };

  const stopVoiceRecording = () => {
    setIsRecording(false);
    setShowVoiceRecorder(false);
    // Stop voice recording
    try {
      if (window.currentMediaRecorder && window.currentMediaRecorder.state === 'recording') {
        window.currentMediaRecorder.stop();
        
        // Stop all tracks
        window.currentMediaRecorder.stream.getTracks().forEach(track => track.stop());
      }
      
      if (window.currentTimer) {
        clearInterval(window.currentTimer);
      }
      
      setIsRecording(false);
      setRecordingTime(0);
      toast.success('Voice recording stopped');
    } catch (error) {
      console.error('Error stopping voice recording:', error);
      toast.error('Failed to stop voice recording: ' + error.message);
    }
  };

  const sendVoiceMessage = async () => {
    if (voicePreview) {
      try {
        setLoading(true);
        
        if (process.env.NODE_ENV !== 'production') {
          console.log('🎤 Sending voice message as text for testing...');
        }
        
        // For now, send as a text message to test the basic sending mechanism
        const testMessage = `🎤 Voice Message (${voicePreview.mimeType}, ${Math.round(voicePreview.blob.size / 1024)}KB)`;
        
        if (process.env.NODE_ENV !== 'production') {
          console.log('🎤 Sending test message:', testMessage);
        }
        
                 // Send as regular text message
                 const response = await api.post('/whatsapp/send/text', {
                   phoneNumber: selectedConversation.phone_number,
                   message: testMessage
                 });
        
        if (!response.data.success) {
          throw new Error(response.data.error || 'Failed to send message');
        }
        
        if (process.env.NODE_ENV !== 'production') {
          console.log('🎤 Test message sent successfully');
        }
        
        // Refresh messages
        await fetchMessages();
        
        setShowVoicePreview(false);
        setVoicePreview(null);
        toast.success('Test voice message sent successfully');
      } catch (error) {
        console.error('Error sending voice message:', error);
        toast.error('Failed to send voice message: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const cancelVoiceMessage = () => {
    if (voicePreview?.url) {
      URL.revokeObjectURL(voicePreview.url);
    }
    setShowVoicePreview(false);
    setVoicePreview(null);
  };

  // Debug function - call this in browser console to test MediaRecorder
  window.testMediaRecorderSupport = () => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('🧪 Testing MediaRecorder Support:');
    }
    const formats = [
      'audio/ogg; codecs=opus',
      'audio/mpeg', 
      'audio/ogg',
      'audio/mp4; codecs=opus',
      'audio/mp4',
      'audio/webm'
    ];
    
    formats.forEach(format => {
      const supported = MediaRecorder.isTypeSupported(format);
      if (process.env.NODE_ENV !== 'production') {
        console.log(`${supported ? '✅' : '❌'} ${format}: ${supported}`);
      }
    });
  };

  // Function to convert audio blob to WhatsApp-compatible format
  const convertAudioForWhatsApp = async (audioBlob, originalMimeType) => {
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('🔄 Converting audio for WhatsApp compatibility...');
      }
      
      // Create audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Convert blob to array buffer
      const arrayBuffer = await audioBlob.arrayBuffer();
      
      // Decode audio data
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Create a new audio buffer with the same data but in a compatible format
      const sampleRate = audioBuffer.sampleRate;
      const numberOfChannels = Math.min(audioBuffer.numberOfChannels, 2); // WhatsApp supports mono/stereo
      const length = audioBuffer.length;
      
      // Create new buffer
      const newBuffer = audioContext.createBuffer(numberOfChannels, length, sampleRate);
      
      // Copy audio data
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const channelData = audioBuffer.getChannelData(channel);
        newBuffer.copyToChannel(channelData, channel);
      }
      
      // Convert to OGG/Opus format (which WhatsApp supports)
      const oggBlob = await audioBufferToOgg(newBuffer);
      
      if (process.env.NODE_ENV !== 'production') {
        console.log('🔄 Audio conversion completed:', {
          originalType: originalMimeType,
          convertedType: oggBlob.type,
        originalSize: audioBlob.size,
        convertedSize: oggBlob.size
      });
      }
      return oggBlob;
    } catch (error) {
      console.error('❌ Audio conversion failed:', error);
      // Fallback: try to create a simple OGG from the original blob
      try {
        return await createSimpleOggFromBlob(audioBlob);
      } catch (fallbackError) {
        console.error('❌ Fallback conversion also failed:', fallbackError);
        return audioBlob; // Return original if all conversions fail
      }
    }
  };

  // Helper function to convert AudioBuffer to OGG/Opus blob
  const audioBufferToOgg = async (audioBuffer) => {
    // For now, we'll use a simpler approach since creating proper OGG/Opus
    // requires complex encoding. We'll use the MediaRecorder API to create
    // an OGG/Opus blob directly from the audio buffer.
    
    try {
      // Create a new audio context and source
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      
      // Create a MediaStreamDestination
      const destination = audioContext.createMediaStreamDestination();
      source.connect(destination);
      
      // Use MediaRecorder to encode as OGG/Opus
      const mediaRecorder = new MediaRecorder(destination.stream, {
        mimeType: 'audio/ogg; codecs=opus'
      });
      
      const chunks = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      return new Promise((resolve, reject) => {
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'audio/ogg; codecs=opus' });
          resolve(blob);
        };
        
        mediaRecorder.onerror = (error) => {
          reject(error);
        };
        
        // Start recording
        mediaRecorder.start();
        source.start();
        
        // Stop after the audio buffer duration
        setTimeout(() => {
          mediaRecorder.stop();
          source.stop();
        }, (audioBuffer.length / audioBuffer.sampleRate) * 1000 + 100);
      });
    } catch (error) {
      console.error('❌ OGG/Opus conversion failed:', error);
      // Fallback: return the original audio buffer as a simple blob
      const arrayBuffer = audioBuffer.getChannelData(0).buffer;
      return new Blob([arrayBuffer], { type: 'audio/ogg; codecs=opus' });
    }
  };

  // Fallback function to create a simple OGG from any blob
  const createSimpleOggFromBlob = async (audioBlob) => {
    // For the fallback, we'll just return the original blob with OGG MIME type
    // This is a simplified approach since creating proper OGG requires complex encoding
    const arrayBuffer = await audioBlob.arrayBuffer();
    
    // Create a simple OGG-like blob (this is not a proper OGG file, but WhatsApp
    // might accept it if the content is compatible)
    return new Blob([arrayBuffer], { type: 'audio/ogg; codecs=opus' });
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    toast.info(isMuted ? 'Conversation unmuted' : 'Conversation muted');
  };

  // Debounced search term to reduce filtering overhead
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(messageSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [messageSearchTerm]);



  // Memoized filtered messages to prevent unnecessary re-renders
  const filteredMessages = useMemo(() => {
    // If no search term, return all messages
    if (!debouncedSearchTerm || debouncedSearchTerm.trim() === '') {

      return messages;
    }
    
    const filtered = messages.filter(message => 
      (message?.message_content || '').toLowerCase().includes((debouncedSearchTerm || '').toLowerCase())
    );
    
    return filtered;
  }, [messages, debouncedSearchTerm]
  );

  // YouTube link detection and video ID extraction
  const extractYouTubeVideoId = useCallback((url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
      /youtube\.com\/watch\?.*&v=([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }, []);

  // Check if a message contains YouTube links
  const hasYouTubeLinks = useCallback((content) => {
    if (!content) return false;
    const youtubePatterns = [
      /youtube\.com\/watch\?v=/,
      /youtu\.be\//,
      /youtube\.com\/embed\//,
      /youtube\.com\/v\//
    ];
    return youtubePatterns.some(pattern => pattern.test(content));
  }, []);

  // Check if a message contains any links
  const hasLinks = useCallback((content) => {
    if (!content) return false;
    const urlPattern = /https?:\/\/[^\s]+/g;
    return urlPattern.test(content);
  }, []);

  // Extract all links from message content
  const extractLinks = useCallback((content) => {
    if (!content) return [];
    const urlPattern = /https?:\/\/[^\s]+/g;
    return content.match(urlPattern) || [];
  }, []);

  // Format WhatsApp message content to preserve formatting
  const formatWhatsAppMessage = useCallback((content) => {
    if (!content) return '';
    
    // Check if content is JSON (for structured messages like buttons, interactive messages)
    try {
      const parsedContent = JSON.parse(content);
      
      // Handle button messages
      if (parsedContent.type === 'button' && parsedContent.body && parsedContent.action) {
        let buttonHtml = '';
        
        // Format the body text
        const bodyText = parsedContent.body.text || '';
        const formattedBody = bodyText
          .replace(/\n/g, '<br>')
          .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
          .replace(/_(.*?)_/g, '<em>$1</em>')
          .replace(/~(.*?)~/g, '<del>$1</del>')
          .replace(/```(.*?)```/g, '<code>$1</code>')
          .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200 font-medium break-all">$1</a>');
        
        buttonHtml += `<div class="whatsapp-button-message">`;
        buttonHtml += `<div class="button-message-body mb-3">${formattedBody}</div>`;
        
        // Add buttons
        if (parsedContent.action.buttons && Array.isArray(parsedContent.action.buttons)) {
          buttonHtml += `<div class="button-container flex flex-wrap gap-2">`;
          parsedContent.action.buttons.forEach((button, index) => {
            if (button.type === 'reply' && button.reply) {
              buttonHtml += `<button class="whatsapp-reply-button px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors cursor-pointer" data-button-id="${button.reply.id}">${button.reply.title}</button>`;
            }
          });
          buttonHtml += `</div>`;
        }
        
        buttonHtml += `</div>`;
        return buttonHtml;
      }
      
      // Handle interactive messages
      if (parsedContent.type === 'interactive' && parsedContent.body) {
        let interactiveHtml = '';
        
        // Format the body text
        const bodyText = parsedContent.body.text || '';
        const formattedBody = bodyText
          .replace(/\n/g, '<br>')
          .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
          .replace(/_(.*?)_/g, '<em>$1</em>')
          .replace(/~(.*?)~/g, '<del>$1</del>')
          .replace(/```(.*?)```/g, '<code>$1</code>')
          .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200 font-medium break-all">$1</a>');
        
        interactiveHtml += `<div class="whatsapp-interactive-message">`;
        interactiveHtml += `<div class="interactive-message-body mb-3">${formattedBody}</div>`;
        
        // Add action buttons
        if (parsedContent.action && parsedContent.action.buttons) {
          interactiveHtml += `<div class="button-container flex flex-wrap gap-2">`;
          parsedContent.action.buttons.forEach((button, index) => {
            if (button.type === 'reply' && button.reply) {
              interactiveHtml += `<button class="whatsapp-reply-button px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors cursor-pointer" data-button-id="${button.reply.id}">${button.reply.title}</button>`;
            }
          });
          interactiveHtml += `</div>`;
        }
        
        interactiveHtml += `</div>`;
        return interactiveHtml;
      }
      
      // Handle list messages
      if (parsedContent.type === 'list' && parsedContent.body) {
        let listHtml = '';
        
        // Format the body text
        const bodyText = parsedContent.body.text || '';
        const formattedBody = bodyText
          .replace(/\n/g, '<br>')
          .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
          .replace(/_(.*?)_/g, '<em>$1</em>')
          .replace(/~(.*?)~/g, '<del>$1</del>')
          .replace(/```(.*?)```/g, '<code>$1</code>')
          .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200 font-medium break-all">$1</a>');
        
        listHtml += `<div class="whatsapp-list-message">`;
        listHtml += `<div class="list-message-body mb-3">${formattedBody}</div>`;
        
        // Add list items
        if (parsedContent.action && parsedContent.action.sections) {
          parsedContent.action.sections.forEach((section, sectionIndex) => {
            if (section.title) {
              listHtml += `<div class="list-section mb-2">`;
              listHtml += `<div class="list-section-title font-semibold text-gray-800 dark:text-gray-200 mb-1">${section.title}</div>`;
              
              if (section.rows && Array.isArray(section.rows)) {
                listHtml += `<div class="list-items space-y-1">`;
                section.rows.forEach((row, rowIndex) => {
                  listHtml += `<div class="list-item p-2 bg-gray-50 dark:bg-gray-700 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors" data-list-id="${row.id}">`;
                  listHtml += `<div class="list-item-title font-medium text-gray-900 dark:text-white">${row.title}</div>`;
                  if (row.description) {
                    listHtml += `<div class="list-item-description text-sm text-gray-600 dark:text-gray-400">${row.description}</div>`;
                  }
                  listHtml += `</div>`;
                });
                listHtml += `</div>`;
              }
              
              listHtml += `</div>`;
            }
          });
        }
        
        listHtml += `</div>`;
        return listHtml;
      }
      
      // If it's JSON but not a recognized type, return the original content
      return content;
      
    } catch (e) {
      // Not JSON, treat as regular text
    }
    
    // Convert WhatsApp formatting to HTML for regular text messages
    let formattedContent = content
      // Preserve line breaks
      .replace(/\n/g, '<br>')
      // Bold text: *text* -> <strong>text</strong>
      .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
      // Italic text: _text_ -> <em>text</em>
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // Strikethrough: ~text~ -> <del>text</del>
      .replace(/~(.*?)~/g, '<del>$1</del>')
      // Monospace: ```text``` -> <code>text</code>
      .replace(/```(.*?)```/g, '<code>$1</code>')
      // URLs: Convert to clickable links with enhanced styling for light/dark modes
      .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200 font-medium break-all">$1</a>');
    
    return formattedContent;
  }, []);

  // Scroll to and highlight original message when clicking reply
  const scrollToOriginalMessage = useCallback((replyToMessageId) => {
    if (!replyToMessageId) return;
    
    // Find the original message in the messages array
    const originalMessage = messages.find(msg => 
      msg.message_id === replyToMessageId || 
      msg.id === replyToMessageId ||
      msg.id === parseInt(replyToMessageId)
    );
    
    if (originalMessage) {
      // Set the highlighted message
      setHighlightedMessageId(originalMessage.id);
      
      // Scroll to the message
      const messageElement = document.getElementById(`message-${originalMessage.id}`);
      if (messageElement) {
        messageElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Show a brief toast notification
        toast.info('Original message highlighted', { 
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: true
        });
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          setHighlightedMessageId(null);
        }, 3000);
      }
    } else {
      // Show error if original message not found
      toast.error('Original message not found', { 
        position: "top-center",
        autoClose: 2000
      });
    }
  }, [messages]);

  // Memoized message renderer to prevent unnecessary re-renders
  const renderMessage = useCallback((message, index) => {
    const isOutbound = message.direction === 'outbound';
    const isSelected = selectedMessages.has(message.id);
    
    return (
      <div 
        id={`message-${message.id}`}
        key={`${message.id}-${index}`} 
        className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-4 relative group ${
          highlightedMessageId === message.id ? 'animate-pulse bg-yellow-100 dark:bg-yellow-900/20 rounded-lg p-2 border-2 border-yellow-400 dark:border-yellow-500' : ''
        }`}
        onClick={(e) => toggleMessageSelection(message.id, e)}
        onContextMenu={(e) => showMessageOptionsMenu(message, e)}
      >
        {/* Selection checkbox */}
        {selectedMessages.size > 0 && (
          <div className="absolute left-2 top-2 z-10">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => toggleMessageSelection(message.id, e)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>
        )}
        
        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg relative ${
          isOutbound 
            ? 'bg-blue-500 text-white' 
            : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
        } ${isSelected ? 'ring-2 ring-blue-400' : ''} ${
          message.status === 'read' && isOutbound ? 'ring-1 ring-blue-300 dark:ring-blue-400' : ''
        }`}>
            {/* Custom styles for links in messages */}
            <style jsx="true">{`
              .message-content a {
                word-break: break-all;
                text-decoration: underline;
                text-underline-offset: 2px;
              }
              .message-content a:hover {
                opacity: 0.8;
              }
              /* Ensure links are visible in both light and dark message bubbles */
              .bg-blue-500 .message-content a {
                color: #dbeafe !important;
                text-decoration-color: #dbeafe;
              }
              .bg-blue-500 .message-content a:hover {
                color: #ffffff !important;
                text-decoration-color: #ffffff;
              }
              .bg-gray-200 .message-content a,
              .dark .bg-gray-700 .message-content a {
                color: #2563eb !important;
                text-decoration-color: #2563eb;
              }
              .bg-gray-200 .message-content a:hover,
              .dark .bg-gray-700 .message-content a:hover {
                color: #1d4ed8 !important;
                text-decoration-color: #1d4ed8;
              }
            `}</style>
          
          {/* Reply indicator */}
          {message.reply_to && (
            <div className="absolute top-1 right-8 opacity-0 group-hover:opacity-100 transition-opacity">
              <Reply className="w-3 h-3 text-blue-300" title="Reply to message" />
            </div>
          )}
          
          {/* Message options button */}
          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => showMessageOptionsMenu(message, e)}
              className="p-1 rounded-full hover:bg-black hover:bg-opacity-20 text-white"
            >
              <MoreVertical className="w-3 h-3" />
            </button>
          </div>
          
          {(message.message_type === 'image' || (message.message_type === 'media' && message.media_type === 'image')) && (
            <div className="mb-2 p-3 md:p-4 bg-white dark:bg-gray-800 rounded-lg border shadow-sm w-full max-w-[600px]">
              {message.media_url ? (
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Image className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-medium text-gray-900 dark:text-white truncate">
                        Image Message
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Tap to view full size
                      </p>
                    </div>
                  </div>
              <img 
                src={message.media_url} 
                alt="Shared image" 
                    className="w-full rounded-lg cursor-pointer"
                onClick={() => window.open(message.media_url, '_blank')}
                    onError={(e) => {
                      console.error('❌ Image failed to load:', message.media_url, e);
                      e.target.style.display = 'none';
                    }}
                  />
                  {message.message_content && (
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                      <span className="message-content" dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(message.message_content) }} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-3 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded text-sm">
                  ⚠️ Image URL missing: {message.media_url}
                </div>
              )}
            </div>
          )}
          
          {(message.message_type === 'document' || (message.message_type === 'media' && message.media_type === 'document')) && (
            <div className="mb-2 p-2 bg-white dark:bg-gray-800 rounded border">
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4" />
                <div className="flex-1">
                  <div className="text-sm">Document</div>
                  {message.message_content && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      <span className="message-content" dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(message.message_content) }} />
                    </div>
                  )}
                </div>
                <Download className="w-4 h-4 cursor-pointer" onClick={() => window.open(message.media_url, '_blank')} />
              </div>
            </div>
          )}
          
          {(message.message_type === 'video' || (message.message_type === 'media' && message.media_type === 'video')) && (
            <div className="mb-2 p-3 md:p-4 bg-white dark:bg-gray-800 rounded-lg border shadow-sm w-full max-w-[600px]">
              {message.media_url ? (
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Play className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-medium text-gray-900 dark:text-white truncate">
                        Video Message
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Tap to play
                      </p>
                    </div>
                  </div>
              <video 
                src={message.media_url} 
                controls
                    className="w-full rounded-lg"
                preload="metadata"
                    onError={(e) => {
                      console.error('❌ Video failed to load:', message.media_url, e);
                      e.target.style.display = 'none';
                    }}
                  />
                  {message.message_content && (
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                      <span className="message-content" dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(message.message_content) }} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-3 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded text-sm">
                  ⚠️ Video URL missing: {message.media_url}
                </div>
              )}
            </div>
          )}
          
          {(message.message_type === 'audio' || (message.message_type === 'media' && message.media_type === 'audio')) && (
            <div className="mb-2 p-3 md:p-4 bg-white dark:bg-gray-800 rounded-lg border shadow-sm w-full max-w-[600px]">
              {message.media_url ? (
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Play className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-medium text-gray-900 dark:text-white truncate">
                        Audio Message
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {playingAudio === message.id ? 'Playing...' : 'Tap to play'}
                      </p>
                </div>
              </div>
                  <audio 
                    src={message.media_url} 
                    controls 
                    className="w-full h-12"
                    preload="metadata"
                    onPlay={() => handleAudioPlay(message.id)}
                    onPause={handleAudioPause}
                    onEnded={handleAudioEnded}
                  />
                </div>
              ) : (
                <div className="p-3 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded text-sm">
                  ⚠️ Audio URL missing: {message.media_url}
                </div>
              )}
            </div>
          )}
          
          {message.message_type === 'location' && (
            <div className="mb-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                 onClick={() => {
                   // Ensure location data is properly structured for map display
                   let locationData = {
                     latitude: message.location?.latitude || message.location?.lat || message.latitude || message.lat,
                     longitude: message.location?.longitude || message.location?.lng || message.longitude || message.lng,
                     address: message.location?.address || message.address || ''
                   };
                   
                   // If no coordinates found in location object, try to extract from message content
                   if (!locationData.latitude || !locationData.longitude) {
                     // Try to extract coordinates from message content (e.g., "28.6139, 77.209")
                     if (message.message_content) {
                       const coordMatch = message.message_content.match(/(\d+\.\d+),\s*(\d+\.\d+)/);
                       if (coordMatch) {
                         locationData.latitude = parseFloat(coordMatch[1]);
                         locationData.longitude = parseFloat(coordMatch[2]);
                       }
                     }
                   }
                   
                   // Check if we have valid coordinates
                   if (locationData.latitude && locationData.longitude) {
                     showLocationOnMap(locationData);
                   } else {
                     toast.error('Location coordinates not found in message');
                   }
                 }}>
              {/* Custom styles for location message content */}
              <style jsx="true">{`
                .location-message-content {
                  color: inherit;
                  line-height: 1.5;
                }
                .location-message-content strong {
                  color: inherit;
                  font-weight: 600;
                }
                .location-message-content em {
                  color: inherit;
                  font-style: italic;
                }
                .location-message-content del {
                  color: inherit;
                  text-decoration: line-through;
                  opacity: 0.7;
                }
                .location-message-content code {
                  color: inherit;
                  background-color: rgba(0, 0, 0, 0.1);
                  padding: 1px 3px;
                  border-radius: 3px;
                  font-family: monospace;
                }
                .dark .location-message-content code {
                  background-color: rgba(255, 255, 255, 0.1);
                }
                .location-message-content a {
                  color: #2563eb !important;
                  text-decoration: underline;
                  text-underline-offset: 2px;
                }
                .dark .location-message-content a {
                  color: #60a5fa !important;
                }
                .location-message-content a:hover {
                  opacity: 0.8;
                }
                /* Ensure emojis and special characters are visible */
                .location-message-content span {
                  color: inherit;
                }
                /* Specific styling for location emojis */
                .location-message-content span:first-child {
                  font-size: 1.1em;
                  margin-right: 4px;
                }
              `}</style>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </div>
                <div className="flex-1 min-w-0">
                  {/* Show the formatted message content if available */}
                  {message.message_content ? (
                    <div className="text-sm whitespace-pre-line text-gray-900 dark:text-white leading-relaxed">
                      <span 
                        className="location-message-content"
                        dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(message.message_content) }} 
                      />
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-1">
                        <MapPin className="w-3 h-3" />
                        <span>Tap to view on map</span>
                </div>
              </div>
                  ) : (
                    <>
                      <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">Location</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        {message.location?.address || message.address || `${message.location?.latitude || message.location?.lat || message.latitude || message.lat}, ${message.location?.longitude || message.location?.lng || message.longitude || message.lng}`}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-1">
                        <MapPin className="w-3 h-3" />
                        <span>Tap to view on map</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {message.message_type === 'contact' && (
            <div className="mb-2 p-2 bg-white dark:bg-gray-800 rounded border">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-green-500" />
                <div>
                  <div className="text-sm font-medium">{message.contact?.name || 'Contact'}</div>
                  <div className="text-xs text-gray-500">{message.contact?.phone || 'No phone number'}</div>
                </div>
              </div>
            </div>
          )}
          
          {/* Reply/Quote Display - WhatsApp Mobile Style */}
          {message.reply_to && (
            <div 
              className="mb-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-l-lg border-l-4 border-blue-500 w-full max-w-[85%] relative cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                scrollToOriginalMessage(message.reply_to.message_id);
              }}
              title="Click to view original message"
            >
              <div className="flex items-center space-x-2 mb-1">
                <Reply className="w-3 h-3 text-blue-500" />
                <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                  {message.reply_to.direction === 'outbound' ? 'You' : 'Contact'}
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-full">
                <span dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(message.reply_to.message_content) }} />
              </div>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight className="w-3 h-3 text-blue-500" />
              </div>
            </div>
          )}
          
          <div className="text-sm">
            {editingMessage?.id === message.id && !message.isNewlySent ? (
              <div className="space-y-2">
                <textarea
                  value={editMessageText}
                  onChange={(e) => setEditMessageText(e.target.value)}
                  className="w-full p-2 text-sm border border-gray-300 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={2}
                />
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => saveEditedMessage(message.id)}
                    disabled={loading}
                    className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 touch-manipulation"
                  >
                    {loading ? (
                      <>
                        <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <span>Save</span>
                    )}
                  </button>
                  <button
                    onClick={cancelEditingMessage}
                    disabled={loading}
                    className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  >
                    Cancel
                  </button>
                </div>
              </div>
                         ) : (
               <>
                 {/* Show message content for non-media messages, or for media messages that have content but no media URL */}
                 {(!['image', 'document', 'video', 'audio', 'media'].includes(message.message_type) || !message.media_url) && message.message_content && (
               <>
                 {messageSearchResults.length > 0 && messageSearchResults[currentSearchIndex]?.id === message.id ? (
                   <span className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
                     <span className="message-content" dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(message.message_content) }} />
                   </span>
                 ) : (
                   <span className="message-content" dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(message.message_content) }} />
                 )}
                   </>
                 )}
                 
                 {/* For media messages with captions, show caption below the media (already handled in media blocks above) */}
                 {message.edited && (
                   <span className="text-xs text-gray-500 ml-2">(edited)</span>
                 )}
                 {message.starred && (
                   <Star className="w-3 h-3 text-yellow-500 inline ml-1" />
                 )}
               </>
             )}
             
             {/* YouTube Video Preview */}
             {hasYouTubeLinks(message.message_content) && (
               <div className="mt-3">
                 {(() => {
                   const urls = extractLinks(message.message_content);
                   const youtubeUrls = urls.filter(url => extractYouTubeVideoId(url));
                   
                   return youtubeUrls.map((url, index) => {
                     const videoId = extractYouTubeVideoId(url);
                     if (!videoId) return null;
                     
                     return (
                       <div key={index} className="mb-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                         <div className="flex items-center space-x-3 mb-2">
                           <div className="w-8 h-8 md:w-10 md:h-10 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                             <Play className="w-4 h-4 md:w-5 md:h-5 text-white" />
                           </div>
                           <div className="flex-1 min-w-0">
                             <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                               YouTube Video
                             </p>
                             <p className="text-xs text-gray-500 dark:text-gray-400">
                               Tap to watch
                             </p>
                           </div>
                         </div>
                         
                         {/* YouTube Thumbnail and Info */}
                         <div className="space-y-2">
                           <div className="relative group">
                             <img 
                               src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                               alt="YouTube video thumbnail"
                               className="w-full h-32 object-cover rounded-lg transition-all duration-200 group-hover:scale-105"
                               onError={(e) => {
                                 // Fallback to medium quality thumbnail if maxres fails
                                 e.target.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                               }}
                             />
                             {/* Clickable Play Button Overlay */}
                             <div 
                               className="absolute inset-0 flex items-center justify-center cursor-pointer"
                               onClick={() => window.open(url, '_blank')}
                               onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                               onMouseLeave={(e) => e.currentTarget.style.opacity = '0.9'}
                             >
                               <div className="w-16 h-16 bg-red-600 bg-opacity-90 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110">
                                 <Play className="w-8 h-8 text-white ml-1" />
                               </div>
                             </div>
                             
                             {/* Hover Effect */}
                             <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg" />
                           </div>
                           
                           {/* Video Link and Additional Click Area */}
                           <div className="text-xs">
                             <a 
                               href={url} 
                               target="_blank" 
                               rel="noopener noreferrer"
                               className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline break-all"
                             >
                               {url}
                             </a>
                             
                             {/* Fallback Click Area */}
                             <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                  onClick={() => window.open(url, '_blank')}>
                               <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                 <Play className="w-4 h-4" />
                                 <span>Click here to watch on YouTube</span>
                               </div>
                             </div>
                           </div>
                         </div>
                       </div>
                     );
                   });
                 })()}
               </div>
             )}

             {/* General Link Preview for non-YouTube links */}
             {hasLinks(message.message_content) && !hasYouTubeLinks(message.message_content) && (
               <div className="mt-3">
                 {(() => {
                   const urls = extractLinks(message.message_content);
                   
                   return urls.map((url, index) => (
                     <div key={index} className="mb-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                       <div className="flex items-center space-x-3 mb-2">
                         <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                           <Link className="w-4 h-4 md:w-5 md:h-5 text-white" />
                         </div>
                         <div className="flex-1 min-w-0">
                           <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                             Link
                           </p>
                           <p className="text-xs text-gray-500 dark:text-gray-400">
                             Tap to open
                           </p>
                         </div>
                       </div>
                       
                       {/* Link Display */}
                       <div className="space-y-2">
                         <div className="p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                           <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                             {new URL(url).hostname}
                           </div>
                           <a 
                             href={url} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline break-all text-sm"
                           >
                             {url}
                           </a>
                         </div>
                       </div>
                     </div>
                   ));
                 })()}
               </div>
             )}
          </div>
          
          {/* Message Reactions */}
          {messageReactions[message.id] && messageReactions[message.id].length > 0 && (
            <div className="flex items-center space-x-1 mt-1">
              {messageReactions[message.id].map((reaction, idx) => (
                <span key={idx} className="text-xs bg-gray-200 dark:bg-gray-600 px-1 rounded">
                  {reaction}
                </span>
              ))}
            </div>
          )}
          
          {/* Translation */}
          {showTranslation[message.id] && messageTranslations[message.id] && (
            <div className="mt-1 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs text-blue-700 dark:text-blue-300">
              {messageTranslations[message.id]}
            </div>
          )}
          
          <div className={`flex items-center justify-end space-x-1 mt-1 ${
            isOutbound ? 'text-blue-100' : 'text-gray-500'
          }`}>
            <MessageStatus status={message.status} timestamp={message.created_at} />
          </div>
        </div>
      </div>
    );
  }, [selectedMessages, selectedMessages.size]);

  // Debounced conversation search term
  const [debouncedConversationSearch, setDebouncedConversationSearch] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedConversationSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle clicking outside message options menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMessageOptions && !event.target.closest('.message-options-menu')) {
        hideMessageOptionsMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMessageOptions]);

  // Debug reply state changes
  useEffect(() => {
    
  }, [replyingToMessage]);

  // Chat Window Timer Effect
  useEffect(() => {
    if (!selectedConversation?.last_message_at) return;
    
    // Update timer immediately
    updateChatWindowTimer();
    
    // Set up interval to update timer every minute
    const interval = setInterval(updateChatWindowTimer, 60000);
    
    return () => clearInterval(interval);
  }, [selectedConversation?.last_message_at, updateChatWindowTimer]);

  // Memoized filtered conversations to prevent unnecessary re-renders
  const filteredConversations = useMemo(() => 
    Array.isArray(conversations) ? conversations.filter(conv => {
      const matchesSearch = (conv?.phone_number || '').includes(debouncedConversationSearch || '') || 
                           (conv?.user_name && conv.user_name.toLowerCase().includes((debouncedConversationSearch || '').toLowerCase()));
    const matchesFilter = filterStatus === 'all' || conv.conversation_status === filterStatus;
    return matchesSearch && matchesFilter;
    }) : [], [conversations, debouncedConversationSearch, filterStatus]
  );

  return (
    <>
      {/* WhatsApp Message Styles */}
              <style jsx="true">{`
        .whatsapp-button-message {
          background: #f0f0f0;
          border-radius: 8px;
          padding: 12px;
          margin: 4px 0;
        }
        
        .whatsapp-interactive-message {
          background: #f0f0f0;
          border-radius: 8px;
          padding: 12px;
          margin: 4px 0;
        }
        
        .whatsapp-list-message {
          background: #f0f0f0;
          border-radius: 8px;
          padding: 12px;
          margin: 4px 0;
        }
        
        .whatsapp-reply-button {
          background: #25D366 !important;
          color: white !important;
          border: none;
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
          margin: 2px;
          display: inline-block;
        }
        
        .whatsapp-reply-button:hover {
          background: #128C7E !important;
        }
        
        .button-message-body,
        .interactive-message-body,
        .list-message-body {
          margin-bottom: 12px;
          line-height: 1.4;
        }
        
        .button-container {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .list-section {
          margin-bottom: 8px;
        }
        
        .list-section-title {
          font-weight: 600;
          color: #333;
          margin-bottom: 4px;
        }
        
        .list-item {
          padding: 8px;
          background: #f8f8f8;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s;
          margin-bottom: 4px;
        }
        
        .list-item:hover {
          background: #e8e8e8;
        }
        
        .list-item-title {
          font-weight: 500;
          color: #333;
        }
        
        .list-item-description {
          font-size: 12px;
          color: #666;
          margin-top: 2px;
        }
        
        /* Dark mode styles */
        .dark .whatsapp-button-message,
        .dark .whatsapp-interactive-message,
        .dark .whatsapp-list-message {
          background: #374151;
        }
        
        .dark .list-section-title {
          color: #e5e7eb;
        }
        
        .dark .list-item {
          background: #4b5563;
        }
        
        .dark .list-item:hover {
          background: #6b7280;
        }
        
        .dark .list-item-title {
          color: #f3f4f6;
        }
        
        .dark .list-item-description {
          color: #d1d5db;
        }
      `}</style>
      
    <div className="h-screen flex bg-gray-100 dark:bg-gray-900">
      {/* Conversations Sidebar */}
      <div className={`${selectedConversation ? 'hidden md:flex' : 'flex'} w-full md:w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-col min-h-0`}>
        {/* Header */}
        <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">WhatsApp Chats</h2>
            <div className="flex items-center space-x-1">
              {/* WhatsApp Status Indicator */}
              {whatsappStatus && (
                <div className="flex items-center space-x-2 mr-2">
                  <div className={`w-2 h-2 rounded-full ${
                    whatsappStatus.enabled && whatsappStatus.configured 
                      ? 'bg-green-500' 
                      : 'bg-red-500'
                  }`} title={
                    whatsappStatus.enabled && whatsappStatus.configured 
                      ? 'WhatsApp Connected' 
                      : 'WhatsApp Disconnected'
                  } />
                </div>
              )}
              <button 
                onClick={() => setShowAnalytics(!showAnalytics)}
                className="p-2 md:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 touch-manipulation"
                title="Analytics"
              >
                <BarChart3 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
              <button 
                onClick={() => fetchConversations(true)}
                disabled={isManualRefresh}
                className={`p-2 md:p-2 rounded-lg transition-colors touch-manipulation ${
                  isManualRefresh 
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${isManualRefresh ? 'animate-spin' : ''}`} />
              </button>
              <div className="relative">
                <button 
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const dropdownWidth = 192;
                    const dropdownHeight = 220;
                    let x = rect.left;
                    let y = rect.bottom + 6;
                    if (x + dropdownWidth > window.innerWidth - 8) x = rect.right - dropdownWidth;
                    if (y + dropdownHeight > window.innerHeight - 8) y = rect.top - dropdownHeight - 6;
                    setHeaderMenuCoords({ x, y });
                    setShowHeaderMenu(!showHeaderMenu);
                  }}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>
                
                {showHeaderMenu && createPortal(
                  <>
                    <div className="fixed inset-0 z-[9998]" onClick={() => setShowHeaderMenu(false)} />
                    <div className="dropdown-menu fixed w-48 md:w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[9999]" style={{ left: headerMenuCoords.x, top: headerMenuCoords.y }} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                      <div className="py-1">
                      <button
                        onClick={withMenuEvent(() => {
                          setShowHeaderMenu(false);
                          setShowAnalytics(!showAnalytics);
                        })}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <BarChart3 className="w-4 h-4" />
                        <span>Analytics</span>
                      </button>
                      <button
                        onClick={withMenuEvent(() => {
                          setShowHeaderMenu(false);
                          fetchConversations(true);
                        })}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        <span>Refresh</span>
                      </button>
                      <button
                        onClick={withMenuEvent(() => {
                          setShowHeaderMenu(false);
                          // Add export all conversations functionality
                          exportAllConversations();
                        })}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <FileDown className="w-4 h-4" />
                        <span>Export All</span>
                      </button>
                      <button
                        onClick={withMenuEvent(() => {
                          setShowHeaderMenu(false);
                          testMessageToNumber();
                        })}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span>Test Message to +918639981771</span>
                      </button>
                      <button
                        onClick={withMenuEvent(() => {
                          setShowHeaderMenu(false);
                          testFileUpload();
                        })}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <FileText className="w-4 h-4" />
                        <span>Test File to +918639981771</span>
                      </button>
                      <button
                        onClick={withMenuEvent(() => {
                          setShowHeaderMenu(false);
                          testAddMessage();
                        })}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span>Test: Add Message to Current Chat</span>
                      </button>
                      <button
                        onClick={withMenuEvent(() => {
                          setShowHeaderMenu(false);
                          testAddAttachmentMessage();
                        })}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <FileText className="w-4 h-4" />
                        <span>Test: Add Attachment Message</span>
                      </button>
                      <button
                        onClick={withMenuEvent(() => {
                          setShowHeaderMenu(false);
                          if (selectedConversation) {
                            toggleMessageDisplay();
                          } else {
                            toast.warning('Please select a conversation first');
                          }
                        })}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <List className="w-4 h-4" />
                        <span>{showAllMessages ? 'Switch to Performance Mode' : 'Load All Messages'}</span>
                      </button>
                      <button
                        onClick={withMenuEvent(() => {
                          setShowHeaderMenu(false);
                          toggleBulkMode();
                        })}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <Users className="w-4 h-4" />
                        <span>{isBulkMode ? 'Exit Bulk Mode' : 'Bulk Mode'}</span>
                      </button>
                      <button
                        onClick={withMenuEvent(() => {
                          setShowHeaderMenu(false);
                          // Add settings functionality
                          setShowConversationInfo(true);
                        })}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </button>
                    </div>
                    </div>
                  </>,
                  document.body
                )}
              </div>
            </div>
          </div>
          
          {/* Bulk Mode Controls */}
          {isBulkMode && (
            <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">Bulk Mode Active</h4>
                <span className="text-xs text-blue-700 dark:text-blue-300">
                  {selectedConversations.size} conversation(s) selected
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={selectAllConversations}
                  className="text-xs px-3 py-1.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 font-medium"
                >
                  Select All
                </button>
                <button
                  onClick={clearSelection}
                  className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-800 font-medium"
                >
                  Clear Selection
                </button>
                <button
                  onClick={() => showDeleteDialog('bulk')}
                  disabled={selectedConversations.size === 0}
                  className={`text-xs px-3 py-1.5 rounded font-medium ${
                    selectedConversations.size === 0
                      ? 'bg-gray-100 text-gray-400 dark:bg-gray-900 dark:text-gray-600 cursor-not-allowed'
                      : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800'
                  }`}
                >
                  Delete Selected ({selectedConversations.size})
                </button>
              </div>
            </div>
          )}
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 md:py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Filter */}
          <div className="mt-2 md:mt-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Conversations</option>
              <option value="active">Active</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
              <option value="pending">Pending</option>
            </select>
            {selectedConversation && filterStatus !== 'all' && filterStatus !== selectedConversation.conversation_status && (
              <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    Current conversation is filtered out.
                  </p>
                  <button
                    onClick={() => setFilterStatus('all')}
                    className="text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700"
                  >
                    Show All
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          {showAnalytics && (
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">Quick Stats</h4>
              <div className="space-y-1 text-xs text-blue-700 dark:text-blue-300">
                <div>Total: {conversations.length}</div>
                <div>Active: {conversations.filter(c => c.conversation_status === 'active').length}</div>
                <div>Resolved: {conversations.filter(c => c.conversation_status === 'resolved').length}</div>
              </div>
            </div>
          )}
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {filteredConversations.slice(0, 50).map(conversation => ( // Limit to first 50 for performance
            <div
              key={conversation.id}
              onClick={() => !isBulkMode && handleConversationSelect(conversation)}
              className={`group p-3 md:p-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 touch-manipulation ${
                selectedConversation?.id === conversation.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
              } ${isBulkMode && selectedConversations.has(conversation.id) ? 'bg-blue-100 dark:bg-blue-800/30' : ''}`}
            >
              <div className="flex items-center space-x-3">
                {/* Bulk Selection Checkbox */}
                {isBulkMode && (
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleConversationSelection(conversation.id);
                    }}
                    className="flex-shrink-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedConversations.has(conversation.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleConversationSelection(conversation.id);
                      }}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                )}
                
                <div className="relative">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                  {onlineStatus[conversation.phone_number] && (
                    <div className="absolute -bottom-1 -right-1 w-3 h-3 md:w-4 md:h-4 bg-green-400 border-2 border-white rounded-full"></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs md:text-sm font-medium text-gray-900 dark:text-white truncate">
                      {conversation.user_name || conversation.phone_number}
                    </h3>
                    <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTime(conversation.last_message_at)}
                    </span>
                      {/* Individual Delete Button */}
                      {!isBulkMode && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            showDeleteDialog('single', conversation);
                          }}
                          className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                          title="Delete conversation"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                  </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate flex-1">
                    {conversation.last_message || 'No messages yet'}
                  </p>
                    {conversation.last_message_status === 'read' && (
                      <span className="text-blue-500 dark:text-blue-400 text-xs">✓✓</span>
                    )}
                    {conversation.last_message_status === 'delivered' && (
                      <span className="text-gray-400 dark:text-gray-500 text-xs">✓✓</span>
                    )}
                    {conversation.last_message_status === 'sent' && (
                      <span className="text-gray-400 dark:text-gray-500 text-xs">✓</span>
                    )}
                  </div>
                  
                  {/* Chat Window Status Indicator */}
                  {(() => {
                    const timer = getConversationChatWindowStatus(conversation);
                    if (timer) {
                      if (timer.isExpired) {
                        return (
                          <div className="flex items-center space-x-1 mt-1">
                            <Clock className="w-3 h-3 text-red-500" />
                            <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                              Chat Window Expired
                            </span>
                          </div>
                        );
                      } else if (timer.remainingTime < 3600000) { // Less than 1 hour
                        return (
                          <div className="flex items-center space-x-1 mt-1">
                            <Clock className="w-3 h-3 text-orange-500" />
                            <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                              ⚠️ {formatRemainingTime(timer.remainingTime)}
                            </span>
                          </div>
                        );
                      } else if (timer.remainingTime < 7200000) { // Less than 2 hours
                        return (
                          <div className="flex items-center space-x-1 mt-1">
                            <Clock className="w-3 h-3 text-yellow-500" />
                            <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                              {formatRemainingTime(timer.remainingTime)}
                            </span>
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}
                  <div className="flex items-center justify-between mt-1">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      conversation.conversation_status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      conversation.conversation_status === 'resolved' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                      conversation.conversation_status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                    }`}>
                      {conversation.conversation_status === 'active' ? '🟢 ' :
                       conversation.conversation_status === 'resolved' ? '🔵 ' :
                       conversation.conversation_status === 'pending' ? '🟡 ' : '⚫ '}
                      {conversation.conversation_status.charAt(0).toUpperCase() + conversation.conversation_status.slice(1)}
                    </span>
                    {conversation.message_count > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {conversation.message_count} messages
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`${selectedConversation ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-h-0`}>
        {selectedConversation ? (
          <>
            {/* Attachment menu portal */}
            {showAttachMenu && createPortal(
              <>
                <div className="fixed inset-0 z-[9998]" onClick={() => setShowAttachMenu(false)} />
                <div className="fixed z-[9999] w-60 md:w-60 max-h-[70vh] overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl"
                  style={{ left: attachMenuCoords.x, top: attachMenuCoords.y }}
                  onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                  <div className="py-2">
                    <button onClick={() => { setShowAttachMenu(false); setShowCameraOptions(true); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>📷 Camera</span>
                    </button>
                    <button onClick={() => { setShowAttachMenu(false); imageInputRef.current?.click(); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>🖼️ Image</span>
                    </button>
                    <button onClick={() => { setShowAttachMenu(false); videoInputRef.current?.click(); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>🎬 Video</span>
                    </button>
                    <button onClick={() => { setShowAttachMenu(false); galleryInputRef.current?.click(); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>🖼️ Gallery</span>
                    </button>
                    <button onClick={() => { setShowAttachMenu(false); audioInputRef.current?.click(); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>🎵 Audio</span>
                    </button>
                    
                    {/* Divider */}
                    <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                    
                    <button onClick={() => { setShowAttachMenu(false); docInputRef.current?.click(); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>📄 Document</span>
                    </button>
                    <button onClick={() => { setShowAttachMenu(false); setProductsSearch(''); setProducts([]); setSelectedProducts(new Set()); setShowProductsModal(true); loadProducts('', { limit: 6, append: false, page: 1 }); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>🛍️ Products</span>
                    </button>
                    <button onClick={handleSendLocation} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>📍 Location</span>
                    </button>
                    <button onClick={handleSendCompanyAddress} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>🏢 Company Address</span>
                    </button>
                    <button onClick={() => { setShowAttachMenu(false); setShowContactSelector(true); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>👤 Contact</span>
                    </button>
                    <button onClick={() => { setShowAttachMenu(false); setShowPollSelector(true); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>📊 Poll</span>
                    </button>
                    <button onClick={() => { setShowAttachMenu(false); setShowEventSelector(true); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>📅 Event</span>
                    </button>
                    <button onClick={() => { setShowAttachMenu(false); setShowPaymentSelector(true); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                      <span>💳 Payment</span>
                    </button>
                  </div>
                </div>
              </>,
              document.body
            )}
            {/* Chat Header - Fixed Height */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 md:p-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {/* Back button for mobile */}
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 touch-manipulation"
                    title="Back to conversations"
                  >
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <div className="relative">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    {onlineStatus[selectedConversation.phone_number] && (
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {selectedConversation.user_name || selectedConversation.phone_number}
                    </h3>
                    {isTestMode() && (
                      <div className="flex items-center space-x-2 mt-1 mb-1">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                        <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                          Test Mode - Status updates simulated
                        </span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {onlineStatus[selectedConversation.phone_number] ? 'Online' : 'Last seen recently'}
                    </p>
                      {selectedConversation.last_message_status === 'read' && (
                        <span className="text-blue-500 dark:text-blue-400 text-xs font-medium">✓✓ Read</span>
                      )}
                      {selectedConversation.last_message_status === 'delivered' && (
                        <span className="text-gray-400 dark:text-gray-500 text-xs">✓✓ Delivered</span>
                      )}
                      {selectedConversation.last_message_status === 'sent' && (
                        <span className="text-gray-400 dark:text-gray-500 text-xs">✓ Sent</span>
                      )}
                    </div>

                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => setShowSaveContactModal(true)}
                    className="p-2 rounded-lg transition-colors bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900 dark:text-green-400 dark:hover:bg-green-800"
                    title="Save Contact"
                  >
                    <User className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={analyzeConversation}
                    disabled={isAnalyzing}
                    className={`p-2 rounded-lg transition-colors ${
                      isAnalyzing 
                        ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400' 
                        : aiSuggestions.length > 0
                        ? 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                    title={aiSuggestions.length > 0 ? "Close Analysis" : "Analyze Conversation"}
                  >
                    {isAnalyzing ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <TrendingUp className="w-5 h-5" />
                    )}
                  </button>
                  <button 
                    onClick={generateSmartReply}
                    disabled={isGeneratingReply}
                    className={`p-2 rounded-lg transition-colors ${
                      isGeneratingReply 
                        ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400' 
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                    title="Generate Smart Reply"
                  >
                    {isGeneratingReply ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <Zap className="w-5 h-5" />
                    )}
                  </button>
                  <button 
                    onClick={() => setShowAIFeatures(!showAIFeatures)}
                    className={`p-2 rounded-lg transition-colors ${
                      showAIFeatures 
                        ? 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400' 
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                    title="AI Features"
                  >
                    <Brain className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={toggleMute}
                    className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${isMuted ? 'text-red-500' : 'text-gray-600 dark:text-gray-400'}`}
                    title={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <button 
                    onClick={() => setShowConversationInfo(!showConversationInfo)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Conversation Info"
                  >
                    <Eye className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  <button 
                    onClick={() => setShowSearch(!showSearch)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Search Messages"
                  >
                    <Search className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  <button 
                    onClick={exportMessages}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Export Messages (JSON)"
                  >
                    <Download className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  <button 
                    onClick={exportConversation}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Export Conversation (PDF/TXT)"
                  >
                    <FileDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  {/* WhatsApp Chat Window Timer - Right Side */}
                  {chatWindowTimer && (
                    <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className={`text-xs font-medium ${
                        chatWindowTimer.isExpired 
                          ? 'text-red-600 dark:text-red-400' 
                          : chatWindowTimer.remainingTime < 3600000 // Less than 1 hour
                          ? 'text-orange-600 dark:text-orange-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {chatWindowTimer.isExpired 
                          ? '⏰ Expired' 
                          : formatRemainingTime(chatWindowTimer.remainingTime)
                        }
                      </span>
                    </div>
                  )}
                  
                  <div className="relative">
                    <button 
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const dropdownWidth = 192;
                        const dropdownHeight = 260;
                        let x = rect.left;
                        let y = rect.bottom + 6;
                        if (x + dropdownWidth > window.innerWidth - 8) x = rect.right - dropdownWidth;
                        if (y + dropdownHeight > window.innerHeight - 8) y = rect.top - dropdownHeight - 6;
                        setConversationMenuCoords({ x, y });
                        setShowConversationMenu(!showConversationMenu);
                      }}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                    
                    {showConversationMenu && createPortal(
                      <>
                        <div className="fixed inset-0 z-[9998]" onClick={() => setShowConversationMenu(false)} />
                        <div className="dropdown-menu fixed w-48 md:w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[9999]" style={{ left: conversationMenuCoords.x, top: conversationMenuCoords.y }} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                          <div className="py-1">
                          <button
                            onClick={withMenuEvent(() => {
                              setShowConversationMenu(false);
                              setShowConversationInfo(!showConversationInfo);
                            })}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                          >
                            <Eye className="w-4 h-4" />
                            <span>Conversation Info</span>
                          </button>
                          <button
                            onClick={withMenuEvent(() => {
                              setShowConversationMenu(false);
                              setShowSearch(!showSearch);
                            })}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                          >
                            <Search className="w-4 h-4" />
                            <span>Search Messages</span>
                          </button>
                          <button
                            onClick={withMenuEvent(() => {
                              setShowConversationMenu(false);
                              exportConversation();
                            })}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                          >
                            <FileDown className="w-4 h-4" />
                            <span>Export Conversation</span>
                          </button>
                          <button
                            onClick={withMenuEvent(() => {
                              setShowConversationMenu(false);
                              toggleMute();
                            })}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                          >
                            {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                            <span>{isMuted ? 'Unmute' : 'Mute'}</span>
                          </button>
                          <button
                            onClick={withMenuEvent(() => {
                              setShowConversationMenu(false);
                              // Add archive functionality
                              archiveConversation(selectedConversation?.id);
                            })}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                          >
                            <Archive className="w-4 h-4" />
                            <span>Archive</span>
                          </button>
                          <button
                            onClick={withMenuEvent(() => {
                              setShowConversationMenu(false);
                              clearChat();
                            })}
                            className="w-full px-4 py-2 text-left text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 flex items-center space-x-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Clear Chat</span>
                          </button>
                          <button
                            onClick={withMenuEvent(() => {
                              setShowConversationMenu(false);
                              // Add delete functionality
                              if (window.confirm('Are you sure you want to delete this conversation?')) {
                                handleDeleteConversation(selectedConversation?.id);
                              }
                            })}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete</span>
                          </button>
                        </div>
                        </div>
                      </>,
                      document.body
                    )}
                  </div>
                </div>
              </div>

              {/* Conversation Actions - Fixed Height */}
              <div className="mt-3 flex items-center space-x-2">
                <button
                  onClick={() => setShowQuickReplies(!showQuickReplies)}
                  className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200"
                >
                  Quick Replies
                </button>
                <button
                  onClick={() => setShowTemplates(!showTemplates)}
                  className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200"
                >
                  Templates
                </button>
                <button
                  onClick={() => setShowNotes(!showNotes)}
                  className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200"
                >
                  Notes
                </button>
                <button
                  onClick={() => setShowAgents(!showAgents)}
                  className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200"
                >
                  Assign Agent
                </button>
                <div className="relative">
                  <select
                    onChange={(e) => updateConversationStatus(e.target.value)}
                    value={selectedConversation.conversation_status}
                    className={`px-3 py-1 text-xs border rounded-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8 cursor-pointer transition-colors ${
                      selectedConversation.conversation_status === 'active' ? 'border-green-300 dark:border-green-600 text-green-700 dark:text-green-300' :
                      selectedConversation.conversation_status === 'resolved' ? 'border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300' :
                      selectedConversation.conversation_status === 'closed' ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300' :
                      'border-yellow-300 dark:border-yellow-600 text-yellow-700 dark:text-yellow-300'
                    }`}
                  >
                    <option value="active" className="text-green-700 dark:text-green-300">🟢 Active</option>
                    <option value="resolved" className="text-blue-700 dark:text-blue-300">🔵 Resolved</option>
                    <option value="closed" className="text-gray-700 dark:text-gray-300">⚫ Closed</option>
                    <option value="pending" className="text-yellow-700 dark:text-yellow-300">🟡 Pending</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Panels Area - Scrollable */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-y-auto max-h-64">
              <div className="p-4 space-y-3">
                {/* Quick Replies Panel */}
                {showQuickReplies && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">Quick Replies</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {quickReplies.map(reply => (
                        <button
                          key={reply.id}
                          onClick={() => handleQuickReply(reply)}
                          className="text-left p-2 text-xs bg-white dark:bg-gray-700 rounded border hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          {reply.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Templates Panel */}
                {showTemplates && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-2">Message Templates</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {messageTemplates.map(template => (
                        <button
                          key={template.id}
                          onClick={() => handleTemplateUse(template)}
                          className="text-left p-2 text-xs bg-white dark:bg-gray-700 rounded border hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          <div className="font-medium">{template.name}</div>
                          <div className="text-gray-600 dark:text-gray-400 truncate">{template.text}</div>
                        </button>
                      ))}
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={productsSearch}
                          onChange={(e) => setProductsSearch(e.target.value)}
                          placeholder="Search products..."
                          className="flex-1 px-3 py-2 text-xs border rounded bg-white dark:bg-gray-700"
                        />
                        <button
                          onClick={async () => { await loadProducts(productsSearch); setShowProducts(true); setShowTemplates(false); }}
                          className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Browse Products
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes Panel */}
                {showNotes && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <h4 className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mb-2">Conversation Notes</h4>
                    <textarea
                      value={conversationNotes}
                      onChange={(e) => setConversationNotes(e.target.value)}
                      placeholder="Add notes about this conversation..."
                      rows={3}
                      className="w-full p-2 text-xs border border-gray-300 rounded bg-white dark:bg-gray-700"
                    />
                    <button
                      onClick={saveConversationNotes}
                      className="mt-2 px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
                    >
                      Save Notes
                    </button>
                  </div>
                )}

                {/* Agents Panel */}
                {showAgents && (
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <h4 className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-2">Assign Agent</h4>
                    <select
                      value={selectedAgent}
                      onChange={(e) => setSelectedAgent(e.target.value)}
                      className="w-full p-2 text-xs border border-gray-300 rounded bg-white dark:bg-gray-700"
                    >
                      <option value="">Select an agent...</option>
                      {agents.map(agent => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name} ({agent.specialty}) - {agent.status}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={assignAgent}
                      disabled={!selectedAgent}
                      className="mt-2 px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                    >
                      Assign
                    </button>
                  </div>
                )}

                {/* AI Features Panel */}
                {showAIFeatures && (
                  <div>
                    <WhatsAppAIFeatures 
                      selectedConversation={selectedConversation}
                      messages={messages}
                      onSendMessage={sendMessage}
                    />
                  </div>
                )}

                {/* Map Panel moved to modal preview below */}

                {/* Products Panel */}
                {showProducts && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">Products</h4>
                      <button onClick={() => setShowProducts(false)} className="text-xs touch-manipulation">Close</button>
                    </div>
                    <div className="mb-2 flex space-x-2">
                      <input
                        type="text"
                        placeholder="Search products..."
                        value={productsSearch}
                        onChange={(e) => setProductsSearch(e.target.value)}
                        className="flex-1 px-3 py-2 text-xs border rounded bg-white dark:bg-gray-700"
                      />
                      <button onClick={() => loadProducts(productsSearch)} className="px-3 py-2 text-xs bg-blue-600 text-white rounded touch-manipulation">Search</button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-auto">
                      {productsLoading && <div className="text-xs text-blue-700">Loading...</div>}
                      {!productsLoading && (products || []).map((p) => (
                        <div key={p.id} className="p-2 bg-white dark:bg-gray-700 rounded border flex items-center justify-between">
                          <div className="text-xs text-gray-800 dark:text-gray-200">
                            <div className="font-medium">{p.name}</div>
                            <div className="opacity-80">{p.formattedPrice || p.price}</div>
                          </div>
                          <div className="space-x-2">
                            <button onClick={() => sendProduct(p)} className="px-2 py-1 text-xs bg-green-600 text-white rounded touch-manipulation">Send</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Conversation Info Panel */}
                {showConversationInfo && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Conversation Info</h4>
                    <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
                      <div>Phone: {selectedConversation.phone_number}</div>
                      <div className="flex items-center space-x-2">
                        <span>Status:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          selectedConversation.conversation_status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          selectedConversation.conversation_status === 'resolved' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                          selectedConversation.conversation_status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                        }`}>
                          {selectedConversation.conversation_status === 'active' ? '🟢 ' :
                           selectedConversation.conversation_status === 'resolved' ? '🔵 ' :
                           selectedConversation.conversation_status === 'pending' ? '🟡 ' : '⚫ '}
                          {selectedConversation.conversation_status.charAt(0).toUpperCase() + selectedConversation.conversation_status.slice(1)}
                        </span>
                      </div>
                      <div>Messages: {messages.length}</div>
                      <div>Last Activity: {formatTime(selectedConversation.last_message_at)}</div>
                      <div>Created: {formatTime(selectedConversation.created_at)}</div>
                      
                      {/* Chat Window Status */}
                      <div className="flex items-center space-x-2">
                        <span>Chat Window:</span>
                        {chatWindowTimer ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            chatWindowTimer.isExpired 
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
                              : chatWindowTimer.remainingTime < 3600000 // Less than 1 hour
                              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          }`}>
                          {chatWindowTimer.isExpired 
                            ? '⏰ Expired' 
                            : formatRemainingTime(chatWindowTimer.remainingTime)
                          }
                        </span>
                        ) : (
                          <span className="text-xs text-gray-500">Calculating...</span>
                        )}
                      </div>
                      
                      {/* Chat Window Warning */}
                      {chatWindowTimer && (
                        <div className={`px-2 py-1 rounded text-xs ${
                          chatWindowTimer.isExpired 
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border border-red-200 dark:border-red-800' 
                            : chatWindowTimer.remainingTime < 3600000 // Less than 1 hour
                            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 border border-orange-200 dark:border-orange-800'
                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border border-green-200 dark:border-green-800'
                        }`}>
                          {chatWindowTimer.isExpired 
                            ? '⚠️ You can only send template messages now' 
                            : chatWindowTimer.remainingTime < 3600000 
                            ? '⚠️ Chat window expires soon - send messages quickly!' 
                            : '✅ Free chat window active - you can send messages'
                          }
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* AI Suggestions Panel */}
                {aiSuggestions.length > 0 && (
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-purple-900 dark:text-purple-100 flex items-center">
                        <Brain className="w-4 h-4 mr-1" />
                        AI Insights
                      </h4>
                      <button
                        onClick={() => setAiSuggestions([])}
                        className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200"
                        title="Close Analysis"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      {aiSuggestions.map((suggestion, index) => (
                        <div key={index} className="text-xs p-2 bg-white dark:bg-gray-700 rounded border">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-purple-700 dark:text-purple-300 capitalize">
                              {suggestion.type}
                            </span>
                            <span className="text-gray-500">
                              {Math.round(suggestion.confidence * 100)}%
                            </span>
                          </div>
                          <div className="text-gray-600 dark:text-gray-400">
                            {suggestion.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Message Search */}
                {showSearch && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder="Search messages..."
                      value={messageSearchTerm}
                      onChange={(e) => {
                        setMessageSearchTerm(e.target.value);
                        searchMessagesInConversation(e.target.value);
                      }}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-700"
                    />
                    {messageSearchResults.length > 0 && (
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-500">
                          {currentSearchIndex + 1}/{messageSearchResults.length}
                        </span>
                        <button
                          onClick={() => navigateSearchResults('prev')}
                          className="p-1 text-gray-500 hover:text-gray-700"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => navigateSearchResults('next')}
                          className="p-1 text-gray-500 hover:text-gray-700"
                        >
                          ↓
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900 min-h-0 messages-container">
              <div className="space-y-3">
        
                {Array.isArray(filteredMessages) ? (
                  <>
                                                            {/* Message Display Controls */}
                    {filteredMessages.length > 0 && (
                      <div className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        {/* Single Row: All Controls */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {showAllMessages ? 
                                `Showing all ${filteredMessages.length} messages` : 
                                `Showing ${currentMessageLimit} of ${totalMessageCount} messages`
                              }
                            </span>
                            {!showAllMessages && (
                              <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded font-medium">
                                Performance Mode
                              </span>
                            )}
                            {showAllMessages && (
                              <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-1 rounded font-medium">
                                Full History
                              </span>
                            )}
                            {!showAllMessages && hasMoreMessages && (
                          <button
                                onClick={loadMoreMessages}
                            disabled={loadingMoreMessages}
                                className="px-3 py-1.5 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 font-medium"
                          >
                            {loadingMoreMessages ? (
                              <>
                                <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Loading...</span>
                              </>
                            ) : (
                              <>
                                    <span>📄</span>
                                    <span>Load 50 More Messages</span>
                              </>
                            )}
                          </button>
                            )}
                        </div>
                            <button
                            onClick={toggleMessageDisplay}
                              disabled={loadingMoreMessages}
                            className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 font-medium"
                            >
                              {loadingMoreMessages ? (
                                <>
                                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                                  <span>Loading...</span>
                                </>
                            ) : showAllMessages ? (
                              <>
                                <span>📱</span>
                                <span>Performance Mode</span>
                                </>
                              ) : (
                                <>
                                <span>📚</span>
                                <span>Load All Messages</span>
                                </>
                              )}
                            </button>
                          </div>
                      </div>
                    )}

                    {/* Message Selection Controls */}
                    {selectedMessages.size > 0 && (
                      <div className="sticky top-0 z-20 bg-blue-500 text-white p-3 rounded-lg border border-blue-600 mb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium">
                              {selectedMessages.size} message(s) selected
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={selectAllMessages}
                              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Select All
                            </button>
                            <button
                              onClick={clearMessageSelection}
                              className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                            >
                              Clear
                            </button>
                            <button
                              onClick={() => deleteSelectedMessages(false)}
                              className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              Delete for Me
                            </button>
                            <button
                              onClick={() => deleteSelectedMessages(true)}
                              className="px-3 py-1 text-xs bg-red-800 text-white rounded hover:bg-red-900"
                            >
                              Delete for Everyone
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Messages */}
                    {showAllMessages ? (
                      // Show all messages when requested (oldest at top, newest at bottom)
                      <div className="space-y-3">
                        {filteredMessages.map((message, index) => renderMessage(message, index))}
                      </div>
                    ) : (
                      // Always show the most recent messages (up to 50 for performance)
                      <div className="space-y-3">
                        {(() => {
                        const messagesToShow = filteredMessages.slice(-50);
                        return messagesToShow.map((message, index) => renderMessage(message, index));
                        })()}
                      </div>
                    )}
                  </>
                ) : null}
                <div ref={messagesEndRef} />
                {/* new message notification audio */}
                <audio ref={messageAudioRef} src="/sounds/message-notification.mp3" preload="auto" />
              </div>
            </div>

            {/* Message Input - Fixed Position */}
            <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-2 md:p-3 flex-shrink-0">
              {/* Chat Window Expired Warning */}
              {chatWindowTimer?.isExpired && (
                <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                        ⚠️ Chat Window Expired
                      </p>
                      <p className="text-xs text-red-700 dark:text-red-300">
                        The 24-hour free chat window has expired. You can only send template messages now.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Reply Interface - WhatsApp Mobile Style */}
              {replyingToMessage && (
                <div className="mb-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-t-lg border-l-4 border-blue-500">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">
                        {replyingToMessage.direction === 'outbound' ? 'You' : 'Contact'}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 truncate">
                        <span dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(replyingToMessage.message_content) }} />
                      </div>
                    </div>
                    <button
                      onClick={cancelReply}
                      className="ml-2 p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      title="Cancel reply"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              
              <div className="flex items-end space-x-2">
                <div className="flex-1 relative">
                  <textarea
                    value={newMessage}
                    onChange={handleMessageChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    rows={1}
                    className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    style={{ minHeight: '40px', maxHeight: '100px' }}
                  />
                  
                  {/* Emoji Picker */}
                  {showEmojiPicker && (
                    <div className="absolute bottom-full left-0 mb-2 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10">
                      <div className="grid grid-cols-6 md:grid-cols-8 gap-1">
                        {['😀', '😂', '😍', '😎', '🤔', '👍', '❤️', '🎉', '🔥', '💯', '😊', '😭', '😱', '🤯', '👏', '🙏'].map(emoji => (
                          <button
                            key={emoji}
                            onClick={() => {
                              setNewMessage(prev => prev + emoji);
                              setShowEmojiPicker(false);
                            }}
                            className="w-6 h-6 md:w-8 md:h-8 text-sm md:text-lg hover:bg-gray-100 dark:hover:bg-gray-700 rounded touch-manipulation"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-2 md:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 touch-manipulation"
                  >
                    <Smile className="w-4 h-4 md:w-5 md:h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  <button
                    onClick={openAttachMenu}
                    className="p-2 md:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 touch-manipulation"
                    title="Attach"
                  >
                    <Paperclip className="w-4 h-4 md:w-5 md:h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  <button
                    onClick={startVoiceRecording}
                    className={`p-2 md:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 touch-manipulation ${isRecording ? 'text-red-500' : 'text-gray-600 dark:text-gray-400'}`}
                  >
                    <Mic className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                  

                  <button
                    onClick={() => sendMessage()}
                    disabled={loading || !newMessage.trim() || chatWindowTimer?.isExpired}
                    className={`p-2 md:p-2 rounded-lg touch-manipulation ${
                      chatWindowTimer?.isExpired
                        ? 'bg-red-500 text-white cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={chatWindowTimer?.isExpired ? 'Chat window expired - only templates allowed' : 'Send message'}
                  >
                    {chatWindowTimer?.isExpired ? (
                      <AlertCircle className="w-4 h-4 md:w-5 md:h-5" />
                    ) : (
                    <Send className="w-4 h-4 md:w-5 md:h-5" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Voice Recording Indicator */}
              {isRecording && (
                <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-red-700 dark:text-red-300">
                      Recording... {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                  <button
                    onClick={stopVoiceRecording}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Stop
                  </button>
                </div>
              )}

              {/* Voice Message Preview */}
              {showVoicePreview && voicePreview && (
                <div className={`mt-2 p-4 rounded-lg border ${
                  voicePreview.compatible === false 
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        voicePreview.compatible === false 
                          ? 'bg-red-500' 
                          : 'bg-blue-500'
                      }`}>
                        <Mic className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className={`text-sm font-medium ${
                          voicePreview.compatible === false 
                            ? 'text-red-900 dark:text-red-100' 
                            : 'text-blue-900 dark:text-blue-100'
                        }`}>
                          Voice Message
                          {voicePreview.compatible === false && (
                            <span className="ml-2 text-xs bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 px-2 py-1 rounded">
                              ⚠️ Incompatible Format
                            </span>
                          )}
                        </div>
                        <div className={`text-xs ${
                          voicePreview.compatible === false 
                            ? 'text-red-700 dark:text-red-300' 
                            : 'text-blue-700 dark:text-blue-300'
                        }`}>
                          Duration: {Math.floor(voicePreview.duration / 60)}:{(voicePreview.duration % 60).toString().padStart(2, '0')}
                          <br />
                          Format: {voicePreview.mimeType}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={cancelVoiceMessage}
                      className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                      title="Cancel"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Audio Player */}
                  <div className="mb-3">
                    <audio 
                      controls 
                      className="w-full h-8"
                      src={voicePreview.url}
                    >
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={cancelVoiceMessage}
                      className="flex-1 px-3 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={sendVoiceMessage}
                      disabled={loading || voicePreview.compatible === false}
                      className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
                        voicePreview.compatible === false
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed'
                      }`}
                    >
                      {loading ? 'Sending...' : 
                       voicePreview.compatible === false ? 'Incompatible Format' : 
                       'Send Voice Message'}
                    </button>
                  </div>
                </div>
              )}

              {/* Enhanced Features Bar */}
              <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center space-x-4">
                  <span>Characters: {newMessage.length}</span>
                  <span>Messages: {messages.length}</span>
                  {selectedConversation && (
                    <span>Status: {selectedConversation.conversation_status}</span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowQuickReplies(!showQuickReplies)}
                    className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/30"
                  >
                    Quick Replies
                  </button>
                  <button
                    onClick={() => setShowTemplates(!showTemplates)}
                    className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/30"
                  >
                    Templates
                  </button>
                </div>
              </div>
              
              {/* File Upload Input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleFileSelect(file);
                  }
                  e.target.value = '';
                }}
                className="hidden"
                accept=".pdf,.doc,.docx,.txt"
              />

              {/* Hidden inputs for different types */}
              <input ref={docInputRef} type="file" multiple accept=".pdf,.doc,.docx,.txt,.ppt,.pptx,.xls,.xlsx,.zip" className="hidden"
                onChange={(e) => { const fs=e.target.files; if (fs?.length) handleFilesSelect(fs); e.target.value=''; }} />
              <input ref={imageInputRef} type="file" multiple accept="image/*" className="hidden"
                onChange={(e) => { const fs=e.target.files; if (fs?.length) handleFilesSelect(fs); e.target.value=''; }} />
              <input ref={videoInputRef} type="file" multiple accept="video/*" className="hidden"
                onChange={(e) => { const fs=e.target.files; if (fs?.length) handleFilesSelect(fs); e.target.value=''; }} />
              <input ref={galleryInputRef} type="file" multiple accept="image/*,video/*" className="hidden"
                onChange={(e) => { const fs=e.target.files; if (fs?.length) handleFilesSelect(fs); e.target.value=''; }} />
              <input ref={audioInputRef} type="file" multiple accept="audio/*" className="hidden"
                onChange={(e) => { const fs=e.target.files; if (fs?.length) handleFilesSelect(fs); e.target.value=''; }} />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => { const f=e.target.files?.[0]; if (f) handleFileSelect(f); e.target.value=''; }} />
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Select a conversation
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Choose a conversation from the list to start chatting
              </p>
            </div>
          </div>
        )}

        {/* File Preview Modal */}
        {showFilePreview && filePreview && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Preview & Send File
                </h3>
                <button
                  onClick={() => {
                    setShowFilePreview(false);
                    setFilePreview(null);
                    setPreviewFile(null);
                    if (filePreview?.previewUrl) {
                      URL.revokeObjectURL(filePreview.previewUrl);
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* File Preview */}
              <div className="mb-4">
                {filePreview.type === 'image' && (
                  <img 
                    src={filePreview.previewUrl} 
                    alt="Preview" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                )}
                {filePreview.type === 'video' && (
                  <div className="space-y-3">
                    <video 
                      src={filePreview.previewUrl} 
                      controls 
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <div className="text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                        {filePreview.file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {(filePreview.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                )}
                {filePreview.type === 'audio' && (
                  <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <div className="mb-3">
                      <div className="flex items-center justify-center mb-2">
                        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                          <Play className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 text-center font-medium">
                        {filePreview.file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                        {(filePreview.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <audio 
                      src={filePreview.previewUrl} 
                      controls 
                      className="w-full"
                      preload="metadata"
                    />
                  </div>
                )}
                {filePreview.type === 'document' && (
                  <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg text-center">
                    <FileText className="w-12 h-12 mx-auto text-gray-400" />
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      {filePreview.file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {(filePreview.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </div>

              {/* Caption Input (except for audio) */}
              {filePreview.type !== 'audio' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Caption (optional)
                  </label>
                  <input
                    type="text"
                    value={previewCaption}
                    onChange={(e) => setPreviewCaption(e.target.value)}
                    placeholder="Add a caption..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowFilePreview(false);
                    setFilePreview(null);
                    setPreviewFile(null);
                    if (filePreview?.previewUrl) {
                      URL.revokeObjectURL(filePreview.previewUrl);
                    }
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (previewFile) {
                      await sendFile(previewFile, previewCaption);
                      setShowFilePreview(false);
                      setFilePreview(null);
                      setPreviewFile(null);
                      if (filePreview?.previewUrl) {
                        URL.revokeObjectURL(filePreview.previewUrl);
                      }
                    }
                  }}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Location Preview Modal */}
        {showMapPanel && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]" onClick={() => setShowMapPanel(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 md:p-4 sm:p-6 w-full max-w-lg mx-2 md:mx-4" onClick={(e)=>e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Share Location</h3>
                <button onClick={() => setShowMapPanel(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><X className="w-5 h-5"/></button>
              </div>

              {companyAddress && (
                <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                  <button onClick={async ()=>{
                    setMapAddress(companyAddress);
                    try {
                      const google = await loadGoogle();
                      const { Geocoder } = await google.maps.importLibrary('geocoding');
                      const geocoder = new Geocoder();
                      const res = await geocoder.geocode({ address: companyAddress });
                      if (res.results.length) {
                        const loc = res.results[0].geometry.location; setMapMarker({ lat: loc.lat(), lng: loc.lng() });
                      }
                    } catch {}
                  }} className="w-full text-left">
                    <div className="text-xs text-blue-800 dark:text-blue-200">
                      <div className="font-medium mb-1">🏢 Company Location:</div>
                      <div>{companyAddress}</div>
                    </div>
                  </button>
                </div>
              )}

              <div className="mb-2">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                  💡 Click on the map or drag the pin to change location. The search bar will update automatically.
                </div>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Search address or click/drag map pin"
                    value={mapAddress}
                    onChange={async (e) => {
                      const val = e.target.value; setMapAddress(val);
                      try {
                        const google = await loadGoogle();
                        const svc = new google.maps.places.AutocompleteService();
                        svc.getPlacePredictions({ input: val }, (preds) => {
                          setMapSuggestions((preds||[]).slice(0,5));
                        });
                      } catch {}
                    }}
                    className="flex-1 px-3 py-2 text-sm border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                  />
                  <button
                  onClick={async () => {
                    if (!navigator.geolocation) return;
                    navigator.geolocation.getCurrentPosition((pos)=>{
                      setMapMarker({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                    });
                  }}
                  className="px-3 py-2 text-sm border rounded border-gray-300 dark:border-gray-600"
                >Current</button>
                <button
                  onClick={async () => {
                    if (!selectedConversation) return;
                    try {
                      // Geocode the address to get lat/lng
                      const google = await loadGoogle();
                      const { Geocoder } = await google.maps.importLibrary('geocoding');
                      const geocoder = new Geocoder();
                      const result = await geocoder.geocode({ address: mapAddress || 'India' });
                      let latitude, longitude;
                      if (!result.results.length && mapMarker) {
                        latitude = mapMarker.lat; longitude = mapMarker.lng;
                      } else if (!result.results.length) {
                        // fallback to Mumbai
                        latitude = 19.0760; longitude = 72.8777;
                      } else {
                        const location = result.results[0].geometry.location;
                        latitude = location.lat();
                        longitude = location.lng();
                      }

                      if (process.env.NODE_ENV !== 'production') {
                        console.log('Sending pinned location:', { latitude, longitude, name: 'Pinned Location', address: mapAddress || '' });
                      }
                      const response = await whatsappAPI.sendLocation({
                        phoneNumber: selectedConversation.phone_number,
                        location: { latitude, longitude, name: 'Pinned Location', address: mapAddress || '' },
                        context: {}
                      });

                      if (response.data?.success) {
                        // The backend will now format the message content properly
                        const messageData = {
                          id: response.data?.messageId || Date.now(),
                          message_id: response.data?.messageId,
                          phone_number: selectedConversation.phone_number,
                          message_type: 'location',
                          message_content: `📍 ${mapAddress || 'Pinned Location'}\n🌍 ${latitude}, ${longitude}`,
                          direction: 'outbound',
                          status: 'sent',
                          created_at: new Date().toISOString(),
                          location: { latitude, longitude, address: mapAddress || '' }
                        };
                        setMessages(prev => [...prev, messageData]);
                        updateConversationLastMessage(selectedConversation.phone_number, `📍 ${mapAddress || 'Location'}`);
                        toast.success('Location sent successfully');
                        setShowMapPanel(false);
                      } else {
                        throw new Error(response.data?.error || 'Failed to send location');
                      }
                    } catch (e) {
                      console.error('Send location error:', e);
                      toast.error('Failed to send location');
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded"
                >
                  Send
                </button>
                </div>
              </div>

              {mapSuggestions.length > 0 && (
                <div className="mb-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  {mapSuggestions.map((sug) => (
                    <button key={sug.place_id} onClick={async ()=>{
                      setMapAddress(sug.description);
                      setMapSuggestions([]);
                      try {
                        const google = await loadGoogle();
                        const { Geocoder } = await google.maps.importLibrary('geocoding');
                        const geocoder = new Geocoder();
                        const res = await geocoder.geocode({ address: sug.description });
                        if (res.results.length) {
                          const loc = res.results[0].geometry.location; setMapMarker({ lat: loc.lat(), lng: loc.lng() });
                        }
                      } catch {}
                    }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
                      {sug.description}
                    </button>
                  ))}
                </div>
              )}
              <GoogleMap 
                address={mapAddress || 'India'} 
                height="260px" 
                showInfoWindow={true} 
                onMapClick={async (p) => {
                  setMapMarker(p);
                  // Reverse geocode to get address when pin is moved
                  try {
                    const google = await loadGoogle();
                    const { Geocoder } = await google.maps.importLibrary('geocoding');
                    const geocoder = new Geocoder();
                    const result = await geocoder.geocode({ 
                      location: { lat: p.lat, lng: p.lng } 
                    });
                    if (result.results.length > 0) {
                      const address = result.results[0].formatted_address;
                      setMapAddress(address);
                      if (process.env.NODE_ENV !== 'production') {
                        console.log('Pin moved to:', address);
                      }
                    }
                  } catch (error) {
                    console.error('Reverse geocoding failed:', error);
                    // If reverse geocoding fails, still update the marker but keep current address
                  }
                }} 
                markerPosition={mapMarker} 
              />
            </div>
          </div>,
          document.body
        )}

        {/* Multi-file Preview Modal */}
        {showMultiPreview && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 md:p-4 sm:p-6 max-w-2xl w-full mx-2 md:mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Preview & Send Files</h3>
                <button
                  onClick={() => {
                    // cleanup preview URLs
                    multiPreviewFiles.forEach(i => { try { URL.revokeObjectURL(i.previewUrl); } catch(_){} });
                    setShowMultiPreview(false);
                    setMultiPreviewFiles([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {multiPreviewFiles.length > 0 && (
                <div>
                  {/* Media Preview */}
                  <div className="mb-4">
                    {multiPreviewFiles[multiPreviewIndex].type === 'image' && (
                      <img src={multiPreviewFiles[multiPreviewIndex].previewUrl} alt="Preview" className="w-full max-h-80 object-contain rounded" />
                    )}
                    {multiPreviewFiles[multiPreviewIndex].type === 'video' && (
                      <video src={multiPreviewFiles[multiPreviewIndex].previewUrl} controls className="w-full max-h-80 object-contain rounded" />
                    )}
                    {multiPreviewFiles[multiPreviewIndex].type === 'audio' && (
                      <audio src={multiPreviewFiles[multiPreviewIndex].previewUrl} controls className="w-full" />
                    )}
                    {multiPreviewFiles[multiPreviewIndex].type === 'document' && (
                      <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded text-center">
                        <FileText className="w-12 h-12 mx-auto text-gray-400" />
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{multiPreviewFiles[multiPreviewIndex].file.name}</p>
                      </div>
                    )}
                  </div>

                  {/* Caption and controls */}
                  <div className="mb-4 flex items-center space-x-2">
                    <input
                      type="text"
                      value={multiPreviewFiles[multiPreviewIndex].caption}
                      onChange={(e) => {
                        const next = [...multiPreviewFiles];
                        next[multiPreviewIndex] = { ...next[multiPreviewIndex], caption: e.target.value };
                        setMultiPreviewFiles(next);
                      }}
                      placeholder="Add a caption..."
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      onClick={() => {
                        setEditorTarget({ mode: 'multi', index: multiPreviewIndex });
                        setShowEditorModal(true);
                      }}
                      className="px-3 py-2 border border-blue-300 text-blue-600 rounded hover:bg-blue-50 dark:border-blue-600 dark:hover:bg-blue-900/20"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        const next = multiPreviewFiles.filter((_, idx) => idx !== multiPreviewIndex);
                        if (next.length === 0) {
                          setShowMultiPreview(false);
                          setMultiPreviewFiles([]);
                          return;
                        }
                        setMultiPreviewFiles(next);
                        setMultiPreviewIndex(Math.max(0, multiPreviewIndex - 1));
                      }}
                      className="px-3 py-2 border border-red-300 text-red-600 rounded hover:bg-red-50 dark:border-red-600 dark:hover:bg-red-900/20"
                    >
                      Remove
                    </button>
                    <button
                      onClick={() => addMoreInputRef.current?.click()}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Add more
                    </button>
                    <input ref={addMoreInputRef} type="file" multiple accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.ppt,.pptx,.xls,.xlsx,.zip" className="hidden"
                      onChange={(e) => {
                        const fs = Array.from(e.target.files || []);
                        if (fs.length) {
                          const extras = fs.map((file) => ({
                            file,
                            previewUrl: URL.createObjectURL(file),
                            type: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'document',
                            caption: ''
                          }));
                          setMultiPreviewFiles(prev => [...prev, ...extras]);
                        }
                        e.target.value = '';
                      }} />
                  </div>

                  {/* Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      disabled={multiPreviewIndex === 0}
                      onClick={() => setMultiPreviewIndex(i => Math.max(0, i - 1))}
                      className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                    >
                      ← Previous
                    </button>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{multiPreviewIndex + 1} / {multiPreviewFiles.length}</div>
                    <button
                      disabled={multiPreviewIndex >= multiPreviewFiles.length - 1}
                      onClick={() => setMultiPreviewIndex(i => Math.min(multiPreviewFiles.length - 1, i + 1))}
                      className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                    >
                      Next →
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        multiPreviewFiles.forEach(i => { try { URL.revokeObjectURL(i.previewUrl); } catch(_){} });
                        setShowMultiPreview(false);
                        setMultiPreviewFiles([]);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={loading}
                      onClick={async () => {
                        try {
                          setLoading(true);
                          for (const item of multiPreviewFiles) {
                            // eslint-disable-next-line no-await-in-loop
                            await sendFile(item.file, item.caption || '');
                          }
                          toast.success(`${multiPreviewFiles.length} files sent successfully`);
                          multiPreviewFiles.forEach(i => { try { URL.revokeObjectURL(i.previewUrl); } catch(_){} });
                          setShowMultiPreview(false);
                          setMultiPreviewFiles([]);
                        } catch (e) {
                          console.error('Send all error:', e);
                          toast.error('Failed to send some files');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                    >
                      {loading ? 'Sending...' : 'Send All'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
        
        {/* Editor Modal (Image crop / Video trim + mute) */}
        {showEditorModal && (() => {
          const isMulti = editorTarget?.mode === 'multi';
          const current = isMulti ? multiPreviewFiles[editorTarget.index] : filePreview;
          const type = isMulti ? current?.type : filePreview?.type;
          return createPortal(
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[10001]" onClick={() => setShowEditorModal(false)}>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 w-full max-w-3xl mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit {type === 'image' ? 'Image' : 'Video'}</h3>
                  <button onClick={() => setShowEditorModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><X className="w-5 h-5"/></button>
                </div>

                {type === 'image' && (
                  <div className="space-y-4">
                    {/* Toolbar */}
                    <div className="flex flex-wrap items-center gap-2 mb-2 p-2 rounded bg-gray-50 dark:bg-gray-700/50">
                      <select value={editorMode} onChange={(e)=>setEditorMode(e.target.value)} className="px-2 py-1 border rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-white">
                        <option value="move">Move</option>
                        <option value="draw">Brush</option>
                        <option value="erase">Erase</option>
                        <option value="text">Text</option>
                        <option value="emoji">Emoji</option>
                      </select>
                      {editorMode === 'draw' && (
                        <>
                          <input type="color" value={brushColor} onChange={(e)=>setBrushColor(e.target.value)} className="h-8 w-10 rounded bg-transparent" />
                          <input type="range" min="1" max="40" value={brushSize} onChange={(e)=>setBrushSize(parseInt(e.target.value))} className="accent-blue-500" />
                        </>
                      )}
                      {editorMode === 'erase' && (
                        <input type="range" min="5" max="60" value={eraseSize} onChange={(e)=>setEraseSize(parseInt(e.target.value))} className="accent-blue-500" />
                      )}
                      {editorMode === 'text' && (
                        <button onClick={()=>{
                          setOverlays(prev=>[...prev,{type:'text', text:'Your text', x:50, y:50, color:'#ffffff', fontSize:24, fontFamily:'Arial'}]);
                        }} className="px-2 py-1 border rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-white">Add Text</button>
                      )}
                      {editorMode === 'emoji' && (
                        <button onClick={()=>{
                          setOverlays(prev=>[...prev,{type:'emoji', text:'😊', x:80, y:80, fontSize:32}]);
                        }} className="px-2 py-1 border rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-white">Add Emoji</button>
                      )}
                    </div>
                    {/* Editable canvas container */}
                    <div className="relative bg-gray-100 dark:bg-gray-700 rounded overflow-hidden max-h-[60vh] flex items-center justify-center">
                      <img ref={editorImgRef} src={isMulti ? current.previewUrl : filePreview.previewUrl} alt="to-edit" className="max-h-[60vh] object-contain" />
                      <canvas ref={editorCanvasRef} className="absolute inset-0" style={{ cursor: editorMode==='draw'?'crosshair': editorMode==='erase'?'not-allowed':'move' }}
                        onMouseDown={(e)=>{
                          if (editorMode==='draw' || editorMode==='erase') {
                            isDrawingRef.current = true;
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = e.clientX - rect.left; const y = e.clientY - rect.top;
                            lastPointRef.current = { x, y };
                            setOverlays(prev=>[...prev,{type:'stroke', points:[{x,y}], color:brushColor, size: editorMode==='draw'? brushSize : eraseSize, erase: editorMode==='erase'}]);
                          }
                        }}
                        onMouseMove={(e)=>{
                          if (!isDrawingRef.current) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX - rect.left; const y = e.clientY - rect.top;
                          setOverlays(prev => {
                            const next = [...prev];
                            const last = next[next.length-1];
                            if (last && last.type==='stroke') {
                              last.points.push({x,y});
                            }
                            return next;
                          });
                        }}
                        onMouseUp={()=>{ isDrawingRef.current = false; lastPointRef.current=null; }}
                        onMouseLeave={()=>{ isDrawingRef.current = false; lastPointRef.current=null; }}
                      />
                      {/* Draggable text/emoji overlays */}
                      {overlays.map((ov, idx)=> (ov.type==='text' || ov.type==='emoji') && (
                        <div key={idx}
                          draggable
                          onDragStart={(e)=>{
                            e.dataTransfer.setData('text/plain', String(idx));
                          }}
                          onDragEnd={(e)=>{
                            const container = e.currentTarget.parentElement.getBoundingClientRect();
                            const x = e.clientX - container.left; const y = e.clientY - container.top;
                            setOverlays(prev=>{
                              const next = [...prev];
                              next[idx] = { ...next[idx], x, y };
                              return next;
                            });
                          }}
                          style={{ position:'absolute', left: ov.x, top: ov.y, cursor:'move', userSelect:'none' }}
                          className="select-none">
                          {ov.type==='text' ? (
                            <span contentEditable suppressContentEditableWarning
                              onBlur={(e)=>{
                                const val = e.currentTarget.textContent || '';
                                setOverlays(prev=>{ const next=[...prev]; next[idx] = { ...next[idx], text: val }; return next; });
                              }}
                              style={{ color: ov.color, fontSize: ov.fontSize, fontFamily: ov.fontFamily }}>
                              {ov.text}
                            </span>
                          ) : (
                            <span style={{ fontSize: ov.fontSize }}>{ov.text}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    {/* Basic crop sliders */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <label className="text-sm text-gray-600 dark:text-gray-300">Width %
                        <input type="range" min="10" max="100" value={imageCrop.widthPct} onChange={(e)=>setImageCrop(c=>({...c,widthPct:parseInt(e.target.value)}))} className="accent-blue-500" />
                      </label>
                      <label className="text-sm text-gray-600 dark:text-gray-300">Height %
                        <input type="range" min="10" max="100" value={imageCrop.heightPct} onChange={(e)=>setImageCrop(c=>({...c,heightPct:parseInt(e.target.value)}))} className="accent-blue-500" />
                      </label>
                      <label className="text-sm text-gray-600 dark:text-gray-300">X %
                        <input type="range" min="0" max="100" value={imageCrop.x} onChange={(e)=>setImageCrop(c=>({...c,x:parseInt(e.target.value)}))} className="accent-blue-500" />
                      </label>
                      <label className="text-sm text-gray-600 dark:text-gray-300">Y %
                        <input type="range" min="0" max="100" value={imageCrop.y} onChange={(e)=>setImageCrop(c=>({...c,y:parseInt(e.target.value)}))} className="accent-blue-500" />
                      </label>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button onClick={()=>setShowEditorModal(false)} className="px-4 py-2 border rounded">Cancel</button>
                      <button
                        onClick={async ()=>{
                          const src = isMulti ? current.previewUrl : filePreview.previewUrl;
                          const baseImg = new window.Image();
                          baseImg.crossOrigin = 'anonymous';
                          baseImg.onload = () => {
                            const cw = baseImg.width * (imageCrop.widthPct/100);
                            const ch = baseImg.height * (imageCrop.heightPct/100);
                            const cx = (baseImg.width - cw) * (imageCrop.x/100);
                            const cy = (baseImg.height - ch) * (imageCrop.y/100);
                            const canvas = document.createElement('canvas');
                            canvas.width = cw; canvas.height = ch;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(baseImg, cx, cy, cw, ch, 0, 0, cw, ch);
                            // render strokes
                            overlays.forEach(ov => {
                              if (ov.type==='stroke') {
                                ctx.globalCompositeOperation = ov.erase ? 'destination-out' : 'source-over';
                                ctx.lineCap = 'round';
                                ctx.lineJoin = 'round';
                                ctx.lineWidth = ov.size;
                                ctx.strokeStyle = ov.color || brushColor;
                                ctx.beginPath();
                                ov.points.forEach((p, i)=>{
                                  const sx = p.x - cx; const sy = p.y - cy;
                                  if (i===0) ctx.moveTo(sx, sy); else ctx.lineTo(sx, sy);
                                });
                                ctx.stroke();
                                ctx.globalCompositeOperation = 'source-over';
                              } else if (ov.type==='text') {
                                ctx.fillStyle = ov.color || '#ffffff';
                                ctx.font = `${ov.fontSize||24}px ${ov.fontFamily||'Arial'}`;
                                ctx.fillText(ov.text||'', ov.x - cx, ov.y - cy);
                              } else if (ov.type==='emoji') {
                                ctx.font = `${ov.fontSize||32}px serif`;
                                ctx.fillText(ov.text||'', ov.x - cx, ov.y - cy);
                              }
                            });
                            canvas.toBlob((blob)=>{
                              if(!blob) return;
                              const file = new File([blob], `edited_${Date.now()}.jpg`, { type: 'image/jpeg' });
                              const item = { file, previewUrl: URL.createObjectURL(file), type: 'image', caption: (isMulti? current.caption : previewCaption)||'' };
                              if (isMulti) {
                                const next = [...multiPreviewFiles];
                                next[editorTarget.index] = item;
                                setMultiPreviewFiles(next);
                              } else {
                                setPreviewFile(file);
                                setFilePreview(item);
                              }
                              setShowEditorModal(false);
                              setOverlays([]);
                            }, 'image/jpeg', 0.95);
                          };
                          baseImg.src = src;
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >Apply</button>
                    </div>
                  </div>
                )}

                {type === 'video' && (
                  <div className="space-y-4">
                    <video src={isMulti ? current.previewUrl : filePreview.previewUrl} controls className="w-full rounded" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <label className="text-sm text-gray-600 dark:text-gray-300">Start (sec)
                        <input type="number" min="0" value={videoTrim.start} onChange={(e)=>setVideoTrim(v=>({...v,start:Math.max(0, Number(e.target.value)||0)}))} className="w-full px-2 py-1 border rounded" />
                      </label>
                      <label className="text-sm text-gray-600 dark:text-gray-300">End (sec)
                        <input type="number" min="0" value={videoTrim.end} onChange={(e)=>setVideoTrim(v=>({...v,end:Math.max(0, Number(e.target.value)||0)}))} className="w-full px-2 py-1 border rounded" />
                      </label>
                      <label className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                        <input type="checkbox" checked={videoTrim.mute} onChange={(e)=>setVideoTrim(v=>({...v,mute:e.target.checked}))} />
                        <span>Mute audio</span>
                      </label>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button onClick={()=>setShowEditorModal(false)} className="px-4 py-2 border rounded">Cancel</button>
                      <button
                        disabled={isProcessingVideo}
                        onClick={async ()=>{
                          try {
                            setIsProcessingVideo(true);
                            if (!ffmpegLoaded) {
                              const { createFFmpeg, fetchFile } = await import('@ffmpeg/ffmpeg');
                              const ffmpeg = createFFmpeg({ log: false });
                              ffmpegRef.current = { ffmpeg, fetchFile };
                              await ffmpeg.load();
                              setFfmpegLoaded(true);
                            }
                            const { ffmpeg, fetchFile } = ffmpegRef.current;
                            const srcUrl = isMulti ? current.previewUrl : filePreview.previewUrl;
                            const inputName = 'input.mp4';
                            const outputName = 'output.mp4';
                            ffmpeg.FS('writeFile', inputName, await fetchFile(srcUrl));
                            const args = ['-i', inputName];
                            if (videoTrim.start > 0) { args.push('-ss', String(videoTrim.start)); }
                            if (videoTrim.end > 0 && videoTrim.end > videoTrim.start) { args.push('-to', String(videoTrim.end)); }
                            if (videoTrim.mute) { args.push('-an'); }
                            args.push('-c', 'copy', outputName);
                            await ffmpeg.run(...args);
                            const data = ffmpeg.FS('readFile', outputName);
                            const blob = new Blob([data.buffer], { type: 'video/mp4' });
                            const file = new File([blob], `edited_${Date.now()}.mp4`, { type: 'video/mp4' });
                            const item = { file, previewUrl: URL.createObjectURL(file), type: 'video', caption: (isMulti? current.caption : previewCaption)||'' };
                            if (isMulti) {
                              const next = [...multiPreviewFiles];
                              next[editorTarget.index] = item;
                              setMultiPreviewFiles(next);
                            } else {
                              setPreviewFile(file);
                              setFilePreview(item);
                            }
                            setShowEditorModal(false);
                          } catch (e) {
                            console.error('Video process error:', e);
                            toast.error('Failed to process video');
                          } finally {
                            setIsProcessingVideo(false);
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >{isProcessingVideo ? 'Processing…' : 'Apply'}</button>
                    </div>
                  </div>
                )}
              </div>
            </div>,
            document.body
          );
        })()}

        {/* Camera Options Modal */}
        {showCameraOptions && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCameraOptions(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Camera Options</h3>
              <div className="space-y-3">
                <button
                  onClick={() => { setShowCameraOptions(false); setShowCameraModal(true); }}
                  className="w-full px-4 py-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                >
                  📸 Open Camera
                </button>
                <button
                  onClick={() => { setShowCameraOptions(false); galleryInputRef.current?.click(); }}
                  className="w-full px-4 py-3 rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800"
                >
                  🖼️ Gallery
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Camera Modal */}
        {showCameraModal && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[10000]" onClick={() => closeCamera()}>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Camera</h3>
                <button onClick={() => closeCamera()} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><X className="w-5 h-5"/></button>
              </div>
              {cameraError && (
                <div className="mb-3 p-2 text-sm rounded bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300">{cameraError}</div>
              )}
              <div className="rounded-lg overflow-hidden bg-black aspect-video mb-3">
                <video ref={cameraVideoRef} autoPlay playsInline muted className="w-full h-full object-contain" />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-x-2">
                  <button onClick={capturePhoto} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">📷 Capture Photo</button>
                  <button onClick={toggleRecording} className={`px-4 py-2 rounded text-white ${isCameraRecording ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>{isCameraRecording ? '⏹ Stop Recording' : '🎥 Start Recording'}</button>
                </div>
                <button onClick={() => closeCamera()} className="px-3 py-2 border rounded border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Close</button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Message Options Menu */}
        {showMessageOptions && selectedMessageForOptions && createPortal(
          <div 
            className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-48 message-options-menu"
            style={{
              left: messageOptionsCoords.x,
              top: messageOptionsCoords.y,
              transform: 'translateX(-50%)',
              zIndex: 9999
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="message-options-menu"
          >
            <button
              onClick={() => copyMessageText(selectedMessageForOptions.message_content)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <Copy className="w-4 h-4" />
              <span>Copy</span>
            </button>
            
            <button
              onClick={() => replyToMessage(selectedMessageForOptions)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <Reply className="w-4 h-4" />
              <span>Reply</span>
            </button>
            
            <button
              onClick={() => forwardMessage(selectedMessageForOptions)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <Forward className="w-4 h-4" />
              <span>Forward</span>
            </button>
            
            <button
              onClick={() => pinMessage(selectedMessageForOptions)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <Pin className="w-4 h-4" />
              <span>Pin</span>
            </button>
            
            <button
              onClick={() => showMessageInfo(selectedMessageForOptions)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <Info className="w-4 h-4" />
              <span>Message Info</span>
            </button>
            
            <button
              onClick={() => startEditingMessage(selectedMessageForOptions)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <Edit3 className="w-4 h-4" />
              <span>Edit</span>
            </button>
            
            <button
              onClick={() => toggleMessageStar(selectedMessageForOptions.id)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <Star className="w-4 h-4" />
              <span>{selectedMessageForOptions.starred ? 'Unstar' : 'Star'}</span>
            </button>
            
            <button
              onClick={() => translateMessage(selectedMessageForOptions.id, selectedMessageForOptions.message_content)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <Languages className="w-4 h-4" />
              <span>Translate</span>
            </button>
            
            <button
              onClick={() => showReactionsForMessage(selectedMessageForOptions.id)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <Heart className="w-4 h-4" />
              <span>React</span>
            </button>
            
            <hr className="my-1 border-gray-200 dark:border-gray-700" />
            
            <button
              onClick={() => deleteMessage(selectedMessageForOptions, false)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 text-red-600"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete for Me</span>
            </button>
            
            <button
              onClick={() => deleteMessage(selectedMessageForOptions, true)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 text-red-600"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete for Everyone</span>
            </button>
          </div>,
          document.body
        )}

        {/* Message Reactions Popup */}
        {Object.entries(showMessageReactions).map(([messageId, isVisible]) => {
          if (!isVisible) return null;
          const message = messages.find(msg => msg.id === messageId);
          if (!message) return null;
          
          return createPortal(
            <div
              key={messageId}
              className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2"
              style={{
                left: messageOptionsCoords.x,
                top: messageOptionsCoords.y - 60,
                transform: 'translateX(-50%)'
              }}
            >
              <div className="flex items-center space-x-1">
                {['👍', '❤️', '😊', '😢', '😡', '👏', '🙏', '🎉'].map((reaction) => (
                  <button
                    key={reaction}
                    onClick={() => {
                      toggleMessageReaction(messageId, reaction);
                      setShowMessageReactions(prev => ({ ...prev, [messageId]: false }));
                    }}
                    className="w-8 h-8 text-lg hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-center"
                  >
                    {reaction}
                  </button>
                ))}
              </div>
            </div>,
            document.body
          );
        })}

        {/* Location Modal */}
        {showLocationModal && selectedLocation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Location</h3>
                <button
                  onClick={() => {
                    setShowLocationModal(false);
                    setSelectedLocation(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              

              
              <div className="mb-4">
                <GoogleMap
                  address={`${selectedLocation.latitude}, ${selectedLocation.longitude}`}
                  height="300px"
                  showInfoWindow={true}
                  markerPosition={{ lat: selectedLocation.latitude, lng: selectedLocation.longitude }}
                />
              </div>
              
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {selectedLocation.address || `${selectedLocation.latitude}, ${selectedLocation.longitude}`}
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowLocationModal(false);
                    setSelectedLocation(null);
                  }}
                  className="px-4 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Products Modal */}
        {showProductsModal && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]" onClick={() => setShowProductsModal(false)}>
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl mx-2 md:mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Products</h3>
                <button onClick={() => setShowProductsModal(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100">✕</button>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={productsSearch}
                    onChange={(e) => setProductsSearch(e.target.value)}
                    placeholder="Search products..."
                    className="flex-1 px-3 py-2 text-sm border rounded bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-800 dark:text-gray-100"
                  />
                  <button
                    onClick={() => { loadProducts(productsSearch, { limit: 6, append: false, page: 1 }); }}
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >Search</button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 max-h-[420px] overflow-auto">
                  {productsLoading && (
                    <div className="col-span-full text-sm text-blue-700">Loading...</div>
                  )}
                  {!productsLoading && products.length === 0 && (
                    <div className="col-span-full text-sm text-gray-600 dark:text-gray-300">No products found.</div>
                  )}
                  {!productsLoading && products.map((p) => {
                    const image = (p.image_url || p.image || (Array.isArray(p.images) && (p.images[0]?.url || p.images[0]))) || '';
                    const price = p.formattedPrice || p.price || '';
                    const checked = selectedProducts.has(p.id);
                    return (
                      <div key={p.id} className={`p-2 rounded border ${checked ? 'border-blue-500' : 'border-gray-200 dark:border-gray-700'} bg-white dark:bg-gray-800`}> 
                        <div className="flex items-start space-x-2">
                          <input type="checkbox" checked={checked} onChange={() => toggleSelectProduct(p.id)} className="mt-1" />
                          <div className="flex-1">
                            <div className="w-full h-24 bg-gray-100 dark:bg-gray-700 rounded mb-2 overflow-hidden flex items-center justify-center">
                              {image ? (
                                <a href={p.url || (p.slug ? `/product/${p.slug}` : `#/product/${p.id}`)} target="_blank" rel="noreferrer" className="block w-full h-full">
                                  <img src={image} alt={p.name} className="w-full h-full object-cover" />
                                </a>
                              ) : (
                                <div className="text-xs text-gray-500">No image</div>
                              )}
                            </div>
                            <a href={p.url || (p.slug ? `/product/${p.slug}` : `#/product/${p.id}`)} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-600 dark:text-blue-400 truncate" title={p.name}>{p.name}</a>
                            {(() => {
                              let priceDisplay = '';
                              if (p.regularLicensePrice || p.regular_license_price) {
                                priceDisplay = `Starting from ${formatPrice(p.regularLicensePrice || p.regular_license_price)}`;
                              } else if (p.extendedLicensePrice || p.extended_license_price) {
                                priceDisplay = `Starting from ${formatPrice(p.extendedLicensePrice || p.extended_license_price)}`;
                              } else if (p.formattedPrice || p.price) {
                                priceDisplay = formatPrice(p.formattedPrice || p.price);
                              }
                              return priceDisplay ? <div className="text-xs text-gray-600 dark:text-gray-300">{priceDisplay}</div> : null;
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-end">
                  <div className="flex items-center space-x-2">
                    <button onClick={() => setShowProductsModal(false)} className="px-3 py-2 text-sm rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200">Cancel</button>
                    <button onClick={sendSelectedProducts} disabled={selectedProducts.size === 0} className={`px-3 py-2 text-sm rounded text-white ${selectedProducts.size === 0 ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>Send</button>
                  </div>
                </div>
              </div>
            </div>
          </div>, document.body
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {deleteType === 'bulk' ? 'Delete Conversations' : 'Delete Conversation'}
                </h3>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-6">
                {deleteType === 'bulk' ? (
                  <p className="text-gray-600 dark:text-gray-400">
                    Are you sure you want to delete <strong>{selectedConversations.size}</strong> conversation(s)? 
                    This action cannot be undone and will also delete all associated messages.
                  </p>
                ) : (
                  <p className="text-gray-600 dark:text-gray-400">
                    Are you sure you want to delete the conversation with{' '}
                    <strong>{conversationToDelete?.user_name || conversationToDelete?.phone_number}</strong>? 
                    This action cannot be undone and will also delete all associated messages.
                  </p>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (deleteType === 'bulk') {
                      handleBulkDeleteConversations();
                    } else {
                      handleDeleteConversation(conversationToDelete.id);
                    }
                  }}
                  disabled={loading}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Click outside to close message options */}
        {showMessageOptions && createPortal(
          <div 
            className="fixed inset-0 z-40"
            onClick={hideMessageOptionsMenu}
          />, 
          document.body
        )}

        {/* Contact Selector Modal */}
        {showContactSelector && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 md:p-4 sm:p-6 max-w-md w-full mx-2 md:mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Select Contact</h3>
                <button onClick={() => setShowContactSelector(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-3">
                {/* Saved Contacts */}
                {managementData.contacts && managementData.contacts.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Saved Contacts</h4>
                    {managementData.contacts.map((contact, index) => (
                      <button
                        key={index}
                        onClick={async () => {
                          try {
                            const response = await whatsappAPI.sendMessage({
                              phoneNumber: selectedConversation.phone_number,
                              message: `👤 Contact:\nName: ${contact.name}\nPhone: ${contact.phone}`
                            });

                            if (response.data?.success) {
                              const messageData = {
                                id: response.data?.data?.dbId || response.data?.data?.messageId || Date.now(),
                                message_id: response.data?.data?.messageId || response.data?.messageId,
                                phone_number: selectedConversation.phone_number,
                                message_type: 'text',
                                message_content: `👤 Contact:\nName: ${contact.name}\nPhone: ${contact.phone}`,
                                direction: 'outbound',
                                status: 'sent',
                                created_at: new Date().toISOString()
                              };
                              setMessages(prev => [...prev, messageData]);
                              updateConversationLastMessage(selectedConversation.phone_number, `👤 Contact: ${contact.name}`);
                              toast.success('Contact sent successfully');
                            } else {
                              throw new Error(response.data?.message || 'Failed to send contact');
                            }
                          } catch (error) {
                            console.error('Error sending contact:', error);
                            toast.error('Failed to send contact: ' + error.message);
                          }
                          setShowContactSelector(false);
                        }}
                        className="w-full p-3 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <div className="font-medium">{contact.name}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{contact.phone}</div>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Create New Contact */}
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Create New Contact</h4>
                  <button
                    onClick={() => {
                      setShowContactSelector(false);
                      handleSendContact();
                    }}
                    className="w-full p-3 border border-blue-200 dark:border-blue-700 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                  >
                    + Create New Contact
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Poll Selector Modal */}
        {showPollSelector && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 md:p-4 sm:p-6 max-w-md w-full mx-2 md:mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Select Poll</h3>
                <button onClick={() => setShowPollSelector(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-3">
                {/* Saved Polls */}
                {managementData.polls && managementData.polls.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Saved Polls</h4>
                    {managementData.polls.map((poll, index) => (
                      <button
                        key={index}
                        onClick={async () => {
                          try {
                            const response = await whatsappAPI.sendMessage({
                              phoneNumber: selectedConversation.phone_number,
                              message: `📊 Poll: ${poll.question}\nReply with: 1) Yes  2) No`
                            });

                            if (response.data?.success) {
                              const messageData = {
                                id: response.data?.data?.dbId || response.data?.data?.messageId || Date.now(),
                                message_id: response.data?.data?.messageId || response.data?.messageId,
                                phone_number: selectedConversation.phone_number,
                                message_type: 'text',
                                message_content: `📊 Poll: ${poll.question}\nReply with: 1) Yes  2) No`,
                                direction: 'outbound',
                                status: 'sent',
                                created_at: new Date().toISOString()
                              };
                              setMessages(prev => [...prev, messageData]);
                              updateConversationLastMessage(selectedConversation.phone_number, `📊 Poll: ${poll.question.substring(0, 30)}...`);
                              toast.success('Poll sent successfully');
                            } else {
                              throw new Error(response.data?.message || 'Failed to send poll');
                            }
                          } catch (error) {
                            console.error('Error sending poll:', error);
                            toast.error('Failed to send poll: ' + error.message);
                          }
                          setShowPollSelector(false);
                        }}
                        className="w-full p-3 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <div className="font-medium">{poll.question}</div>
                        {poll.options && (
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            Options: {poll.options.join(', ')}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Create New Poll */}
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Create New Poll</h4>
                  <button
                    onClick={() => {
                      setShowPollSelector(false);
                      handleSendPoll();
                    }}
                    className="w-full p-3 border border-blue-200 dark:border-blue-700 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                  >
                    + Create New Poll
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Event Selector Modal */}
        {showEventSelector && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 md:p-4 sm:p-6 max-w-md w-full mx-2 md:mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Select Event</h3>
                <button onClick={() => setShowEventSelector(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-3">
                {/* Saved Events */}
                {managementData.events && managementData.events.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Saved Events</h4>
                    {managementData.events.map((event, index) => (
                      <button
                        key={index}
                        onClick={async () => {
                          try {
                            const response = await whatsappAPI.sendMessage({
                              phoneNumber: selectedConversation.phone_number,
                              message: `📅 Event: ${event.title}\n${event.description || 'Details will follow.'}`
                            });

                            if (response.data?.success) {
                              const messageData = {
                                id: response.data?.data?.dbId || response.data?.data?.messageId || Date.now(),
                                message_id: response.data?.data?.messageId || response.data?.messageId,
                                phone_number: selectedConversation.phone_number,
                                message_type: 'text',
                                message_content: `📅 Event: ${event.title}\n${event.description || 'Details will follow.'}`,
                                direction: 'outbound',
                                status: 'sent',
                                created_at: new Date().toISOString()
                              };
                              setMessages(prev => [...prev, messageData]);
                              updateConversationLastMessage(selectedConversation.phone_number, `📅 Event: ${event.title}`);
                              toast.success('Event sent successfully');
                            } else {
                              throw new Error(response.data?.message || 'Failed to send event');
                            }
                          } catch (error) {
                            console.error('Error sending event:', error);
                            toast.error('Failed to send event: ' + error.message);
                          }
                          setShowEventSelector(false);
                        }}
                        className="w-full p-3 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <div className="font-medium">{event.title}</div>
                        {event.description && (
                          <div className="text-sm text-gray-600 dark:text-gray-400">{event.description}</div>
                        )}
                        {event.date && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(event.date).toLocaleDateString()}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Create New Event */}
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Create New Event</h4>
                  <button
                    onClick={() => {
                      setShowEventSelector(false);
                      handleSendEvent();
                    }}
                    className="w-full p-3 border border-blue-200 dark:border-blue-700 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                  >
                    + Create New Event
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Payment Selector Modal */}
        {showPaymentSelector && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 md:p-4 sm:p-6 max-w-md w-full mx-2 md:mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Select Payment Option</h3>
                <button onClick={() => setShowPaymentSelector(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-3">
                {/* Payment Link */}
                {managementData.payment?.link && (
                  <button
                    onClick={async () => {
                      try {
                        const response = await whatsappAPI.sendMessage({
                          phoneNumber: selectedConversation.phone_number,
                          message: `💳 Payment Link: ${managementData.payment.link}`
                        });

                        if (response.data?.success) {
                          const messageData = {
                            id: response.data?.data?.dbId || response.data?.data?.messageId || Date.now(),
                            message_id: response.data?.data?.messageId || response.data?.messageId,
                            phone_number: selectedConversation.phone_number,
                            message_type: 'text',
                            message_content: `💳 Payment Link: ${managementData.payment.link}`,
                            direction: 'outbound',
                            status: 'sent',
                            created_at: new Date().toISOString()
                          };
                          setMessages(prev => [...prev, messageData]);
                          updateConversationLastMessage(selectedConversation.phone_number, '💳 Payment Link');
                          toast.success('Payment link sent successfully');
                        } else {
                          throw new Error(response.data?.message || 'Failed to send payment link');
                        }
                      } catch (error) {
                        console.error('Error sending payment link:', error);
                        toast.error('Failed to send payment link: ' + error.message);
                      }
                      setShowPaymentSelector(false);
                    }}
                    className="w-full p-3 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <div className="font-medium">💳 Payment Link</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 truncate">{managementData.payment.link}</div>
                  </button>
                )}
                
                {/* Payment QR */}
                {managementData.payment?.qr && (
                  <button
                    onClick={async () => {
                      try {
                        const qr = managementData.payment.qr;
                        

                        
                        if (qr) {
                          let qrUrl = qr;
                          
                          // Handle different QR formats
                          if (typeof qr === 'string') {
                            if (qr.startsWith('data:image/')) {
                              // Already a data URL
                              qrUrl = qr;

                            } else if (qr.startsWith('http')) {
                              // HTTP URL
                              qrUrl = qr;

                            } else if (qr.length > 100 && !qr.includes(' ')) {
                              // Likely a base64 string without prefix - add data:image/png prefix
                              qrUrl = `data:image/png;base64,${qr}`;
                              
                                                          }
                          }
                          
                          // Try to send as image if we have a valid image URL
                          if (qrUrl && (qrUrl.startsWith('data:image/') || qrUrl.startsWith('http'))) {

                            
                            const response = await whatsappAPI.sendMedia({
                              phoneNumber: selectedConversation.phone_number,
                              mediaUrl: qrUrl,
                              caption: '💳 Payment QR Code - Please scan to make payment',
                              mediaType: 'image'
                            });

                            if (response.data?.success) {
                              const messageData = {
                                id: response.data?.data?.dbId || response.data?.data?.messageId || Date.now(),
                                message_id: response.data?.data?.messageId || response.data?.messageId,
                                phone_number: selectedConversation.phone_number,
                                message_type: 'image',
                                message_content: '💳 Payment QR Code - Please scan to make payment',
                                media_url: qrUrl,
                                media_type: 'image',
                                direction: 'outbound',
                                status: 'sent',
                                created_at: new Date().toISOString()
                              };
                              setMessages(prev => [...prev, messageData]);
                              updateConversationLastMessage(selectedConversation.phone_number, '💳 Payment QR Code');
                              toast.success('Payment QR sent successfully');
                            } else {
                              throw new Error(response.data?.message || 'Failed to send QR image');
                            }
                          } else {
                            // Fallback to text
                            const response = await whatsappAPI.sendMessage({
                              phoneNumber: selectedConversation.phone_number,
                              message: '💳 Payment QR: Please scan the QR shared by our team.'
                            });

                            if (response.data?.success) {
                              const messageData = {
                                id: response.data?.data?.dbId || response.data?.data?.messageId || Date.now(),
                                message_id: response.data?.data?.messageId || response.data?.messageId,
                                phone_number: selectedConversation.phone_number,
                                message_type: 'text',
                                message_content: '💳 Payment QR: Please scan the QR shared by our team.',
                                direction: 'outbound',
                                status: 'sent',
                                created_at: new Date().toISOString()
                              };
                              setMessages(prev => [...prev, messageData]);
                              updateConversationLastMessage(selectedConversation.phone_number, '💳 Payment QR');
                              toast.success('Payment QR message sent successfully');
                            } else {
                              throw new Error(response.data?.message || 'Failed to send payment QR message');
                            }
                          }
                        } else {
                          // No QR data available
                          const response = await whatsappAPI.sendMessage({
                            phoneNumber: selectedConversation.phone_number,
                            message: '💳 Payment QR: Please scan the QR shared by our team.'
                          });

                          if (response.data?.success) {
                            const messageData = {
                              id: response.data?.data?.dbId || response.data?.data?.messageId || Date.now(),
                              message_id: response.data?.data?.messageId || response.data?.messageId,
                              phone_number: selectedConversation.phone_number,
                              message_type: 'text',
                              message_content: '💳 Payment QR: Please scan the QR shared by our team.',
                              direction: 'outbound',
                              status: 'sent',
                              created_at: new Date().toISOString()
                            };
                            setMessages(prev => [...prev, messageData]);
                            updateConversationLastMessage(selectedConversation.phone_number, '💳 Payment QR');
                            toast.success('Payment QR message sent successfully');
                          } else {
                            throw new Error(response.data?.message || 'Failed to send payment QR message');
                          }
                        }
                      } catch (error) {
                        console.error('Error sending payment QR:', error);
                        toast.error('Failed to send payment QR: ' + error.message);
                      }
                      setShowPaymentSelector(false);
                    }}
                    className="w-full p-3 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <div className="font-medium">💳 Payment QR</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">QR Code Available</div>
                  </button>
                )}
                
                {/* Payment Details */}
                {managementData.payment?.details && (
                  <button
                    onClick={async () => {
                      try {
                        const response = await whatsappAPI.sendMessage({
                          phoneNumber: selectedConversation.phone_number,
                          message: `🏦 Payment Details:\n${managementData.payment.details}`
                        });

                        if (response.data?.success) {
                          const messageData = {
                            id: response.data?.data?.dbId || response.data?.data?.messageId || Date.now(),
                            message_id: response.data?.data?.messageId || response.data?.messageId,
                            phone_number: selectedConversation.phone_number,
                            message_type: 'text',
                            message_content: `🏦 Payment Details:\n${managementData.payment.details}`,
                            direction: 'outbound',
                            status: 'sent',
                            created_at: new Date().toISOString()
                          };
                          setMessages(prev => [...prev, messageData]);
                          updateConversationLastMessage(selectedConversation.phone_number, '🏦 Payment Details');
                          toast.success('Payment details sent successfully');
                        } else {
                          throw new Error(response.data?.message || 'Failed to send payment details');
                        }
                      } catch (error) {
                        console.error('Error sending payment details:', error);
                        toast.error('Failed to send payment details: ' + error.message);
                      }
                      setShowPaymentSelector(false);
                    }}
                    className="w-full p-3 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <div className="font-medium">🏦 Payment Details</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Account & UPI Details</div>
                  </button>
                )}
                
                {/* Create New Payment */}
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Create New Payment</h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setShowPaymentSelector(false);
                        handleSendPaymentLink();
                      }}
                      className="w-full p-2 border border-blue-200 dark:border-blue-700 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-sm"
                    >
                      + Payment Link
                    </button>
                    <button
                      onClick={() => {
                        setShowPaymentSelector(false);
                        handleSendPaymentQR();
                      }}
                      className="w-full p-2 border border-blue-200 dark:border-blue-700 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-sm"
                    >
                      + Payment QR
                    </button>
                    <button
                      onClick={() => {
                        setShowPaymentSelector(false);
                        handleSendPaymentDetails();
                      }}
                      className="w-full p-2 border border-blue-200 dark:border-blue-700 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-sm"
                    >
                      + Payment Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Save Contact Modal */}
        {showSaveContactModal && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Save Contact</h3>
                <button
                  onClick={() => setShowSaveContactModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => handleContactFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter contact name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="text"
                    value={contactForm.phone_number}
                    onChange={(e) => handleContactFormChange('phone_number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter phone number"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => handleContactFormChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter email address"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    value={contactForm.company}
                    onChange={(e) => handleContactFormChange('company', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter company name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={contactForm.notes}
                    onChange={(e) => handleContactFormChange('notes', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter any notes about this contact"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowSaveContactModal(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveContact}
                  disabled={savingContact}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {savingContact ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Save Contact</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
    </>
  );
};

export default WhatsAppChat;

